/**
 * Minimal fetch wrappers for the admin app. Parallel to the customer-app's
 * lib/api.ts but targets the staff auth endpoint and uses its own storage
 * namespace so the two apps can share a browser without stomping each
 * other's session.
 *
 * #711 — this file is the error-surfacing choke point. EVERY non-ok
 * response (the `request<T>` family AND the legacy throwing helpers)
 * funnels through `parseErrorBody`, which:
 *   - fires the global onUnauthorized auto-logout on ANY 401 (pre-fix,
 *     only `request<T>` did — the ~40 legacy helpers let an expired 8h
 *     staff JWT die on-page with the raw string `invalid_token`),
 *   - sanitizes machine codes via the shared `errorCopy` mapper (prose
 *     passes through; unknown codes become generic copy — NEVER raw),
 *   - preserves the raw machine token on `.code` for typed branching.
 */

import type { VaccinationSource } from '@ferro/core/modules/dog-profile/vaccination-source.js';
import { errorCopy, isMachineCode } from '@ferro/core/i18n/error-copy';
import { z } from 'zod';

export type StaffLoginRequest = {
  username: string;
  password: string;
};

export type StaffUser = {
  id: string;
  username: string;
  role: string;
  /**
   * #428 — effective roles (primary first, then additive grants),
   * returned by /staff/login and /staff/me. Optional: sessions persisted
   * before multi-role shipped won't carry it — consumers fall back to
   * `[role]` via `effectiveRolesOf` (lib/rbac-matrix.ts).
   */
  roles?: string[];
  first_name?: string | null;
  last_name?: string | null;
};

export type StaffLoginResponse = {
  token: string;
  staff: StaffUser;
};

export class ApiError extends Error {
  public readonly status: number;
  /**
   * #711 — the server's machine token (`body.error` when code-shaped, or
   * `body.code`), preserved for typed branching. `message` is ALWAYS
   * render-safe human copy; branch on `code`, never on `message`.
   */
  public readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'ApiError';
  }
}

// ─── Core request infrastructure (W0.5; cold-start retry expanded 2026-05-11) ──
// One fetch wrapper for the admin-app. Existing pages call `request<T>` via the
// `api.*` verb shorthand or via the namespaced `adminXxxApi` helpers further
// down. Direct `fetch()` callers exist (loginStaff, fetchAdminSchedule, etc.)
// — those bypass this retry path intentionally for back-compat.
//
// Cold-start (Railway Fluid Compute idle → 50-90s wake) used to surface as a
// broken UI on the first dashboard fetch after idle. This wrapper now:
//   - Retries on 502/503/504 (cold-start signatures), network errors, and the
//     30s timeout abort. Exponential backoff with jitter caps at 4s per delay.
//   - Does NOT retry on 4xx other than 408/429 (auth/client errors aren't
//     cold-start signatures — see `isRetryableStatus`).
//   - Notifies subscribers via `onRetryStatus` so the ServerWakingBanner can
//     render only after 2s of retrying, dismissing on resolution.

const REQUEST_TIMEOUT_MS = 30_000;
/** Max retries after the initial attempt — so total attempts = 1 + MAX_RETRIES. */
const MAX_RETRIES = 3;

/** Backoff parameters per AC: base 500ms, cap 4s, jitter ±100ms. */
export interface BackoffOpts {
  /** Base delay in ms; doubled each attempt. */
  readonly baseMs: number;
  /** Max delay in ms after exponential growth. */
  readonly capMs: number;
  /** Jitter amplitude in ms; actual jitter is in `[-jitterMs, +jitterMs]`. */
  readonly jitterMs: number;
  /** Optional random source for deterministic tests. Default `Math.random`. */
  readonly rng?: () => number;
}

const DEFAULT_BACKOFF: BackoffOpts = {
  baseMs: 500,
  capMs: 4_000,
  jitterMs: 100,
};

/**
 * Pure exponential-backoff function. `attempt` is 1-indexed: attempt=1 returns
 * roughly `baseMs ± jitter`, attempt=2 returns `2 * baseMs ± jitter`, etc.,
 * capped at `capMs`. Always returns a non-negative number.
 *
 * Exposed for unit-testability and so any future helper that needs the same
 * cold-start backoff (e.g. the loginStaff direct fetch) can reuse it.
 */
export function computeBackoffMs(
  attempt: number,
  opts: Partial<BackoffOpts> = {},
): number {
  const { baseMs, capMs, jitterMs, rng } = { ...DEFAULT_BACKOFF, ...opts };
  const safeAttempt = Math.max(1, attempt);
  const exponential = baseMs * Math.pow(2, safeAttempt - 1);
  const capped = Math.min(exponential, capMs);
  const random = (rng ?? Math.random)();
  const jitter = (random * 2 - 1) * jitterMs; // [-jitterMs, +jitterMs]
  return Math.max(0, Math.round(capped + jitter));
}

/**
 * Status codes that signal a cold-start / transient infra issue worth retrying.
 * 502 (bad gateway), 503 (unavailable), 504 (gateway timeout) all show up when
 * Railway's container is waking from idle. 408 (request timeout) and 429
 * (rate-limited with `Retry-After`) are also transient by convention.
 */
export function isRetryableStatus(status: number): boolean {
  return (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    status === 408 ||
    status === 429
  );
}

/**
 * Whether an exception (not an HTTP status) should trigger a retry. Network
 * failures throw `TypeError` ("Failed to fetch"); the 30s timeout aborts the
 * controller and surfaces as `DOMException("AbortError")`.
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof TypeError) return true;
  return false;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  details?: unknown;
  /**
   * Machine token from the server's error body (`body.code`) — the desk
   * money endpoints' body convention puts the HUMAN sentence in `error`
   * and the branchable token here (e.g. 'charge_declined',
   * 'charge_unresolved'). Absent on transport-level failures, so a
   * caller that must distinguish "the server answered" from "the wire
   * failed" can branch on presence (#522 keyed entry).
   */
  code?: string;
  /**
   * #522 review — Authorize.net's own response code for a decline
   * (`body.gatewayCode`) plus, for a HELD-for-review auth, the LIVE
   * transaction id holding the customer's funds. A held auth is NOT a
   * "nothing happened" decline: the port contract says the id must never
   * be discarded, and re-keying the card would collect twice once the
   * merchant approves the held transaction.
   */
  gatewayCode?: string;
  heldTransactionId?: string;
  /** True when the failure was a cold-start / network issue (UI can offer retry). */
  isRetryable?: boolean;
  /**
   * SPEC 2026-10-26 (AC 8.2) — the deny-by-default admin guard's `body.reason`.
   * `'route_not_mapped'` means this server predates the route (frontends
   * deploy before the server), which a caller says as "available after the
   * next server update", never as a permissions error. Absent otherwise.
   */
  reason?: string;
}

export type RetryStatusListener = (status: RetryStatus) => void;

/**
 * Phase semantics:
 *   - `retrying`: a retry is in flight (banner should start its 2s reveal timer).
 *   - `resolved`: the call finished (success OR terminal failure); any visible
 *     banner should dismiss. `attempt` is the final attempt count.
 */
export type RetryPhase = 'retrying' | 'resolved';

export interface RetryStatus {
  readonly phase: RetryPhase;
  readonly attempt: number;
  readonly maxRetries: number;
  readonly message: string;
}

const retryStatusListeners = new Set<RetryStatusListener>();

type UnauthorizedListener = () => void;
let onUnauthorizedCallback: UnauthorizedListener | null = null;

/**
 * Called by AuthContext / app shell to register the auto-logout handler
 * triggered when any request returns 401.
 */
export function setOnUnauthorized(callback: UnauthorizedListener | null): void {
  onUnauthorizedCallback = callback;
}

/** Subscribe to retry-status updates. Returns an unsubscribe function. */
export function onRetryStatus(listener: RetryStatusListener): () => void {
  retryStatusListeners.add(listener);
  return () => {
    retryStatusListeners.delete(listener);
  };
}

function notifyRetryListeners(status: RetryStatus): void {
  retryStatusListeners.forEach((fn) => fn(status));
}

function buildRetryingStatus(attempt: number): RetryStatus {
  return {
    phase: 'retrying',
    attempt,
    maxRetries: MAX_RETRIES,
    message:
      attempt === 1
        ? 'Connecting to server...'
        : 'Server is waking up, please wait...',
  };
}

function buildResolvedStatus(attempt: number): RetryStatus {
  return {
    phase: 'resolved',
    attempt,
    maxRetries: MAX_RETRIES,
    message: '',
  };
}

/**
 * Test-only escape hatch for publishing synthetic retry-status events,
 * so banner/component tests don't need to drive a full fetch round-trip
 * (which interacts poorly with `vi.useFakeTimers`). Not part of the
 * public API — do not call from production code.
 *
 * @internal
 */
export const __testOnly__ = {
  notifyRetryListeners,
  buildRetryingStatus,
  buildResolvedStatus,
};

function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
  });
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: unknown;
  code?: string;
}

/**
 * #711 — the byte-exact copy consumers pattern-match on (customer-app's
 * AuthContext does an exact string compare on its twin). Do NOT reword.
 */
const SESSION_EXPIRED_COPY = 'Session expired. Please log in again.';

/** What `parseErrorBody` distills out of a non-ok response body. */
export interface ParsedApiErrorBody {
  /** Render-safe human copy. NEVER a machine code. */
  message: string;
  /** The raw machine token (body.code, or a code-shaped body.error). */
  code?: string;
  /** body.details passthrough. */
  details?: unknown;
}

/**
 * #711 — THE error-body choke point. Used by `request<T>`'s !ok branch
 * AND by every legacy throwing helper (via `apiErrorFromResponse`), so
 * both fetch families surface identical, sanitized errors.
 *
 * Behavior:
 *  - 401 (except credential endpoints): fires the global onUnauthorized
 *    auto-logout and yields the exact session-expired sentence.
 *  - Otherwise: yields a SANITIZED message — the server's own prose when
 *    it sent prose (in `error`, or in `message` when `error` is a bare
 *    machine code — the rate-limit convention), else the shared
 *    `errorCopy` mapping, else the caller's fallback / generic copy.
 *  - Always preserves the machine token on `code`.
 */
export function parseErrorBody(
  status: number,
  body: unknown,
  opts: {
    /** Prose fallback when the body carries no usable error text. */
    fallback?: string;
    /**
     * Credential endpoints (staff login): a 401 means "wrong credentials",
     * not "session expired" — keep the server's copy and do NOT fire the
     * global auto-logout (there is no session to expire yet).
     */
    credentialEndpoint?: boolean;
    /**
     * #429 AIM convention — prefer the human `message` field over `error`
     * when both are present, regardless of shape.
     */
    preferBodyMessage?: boolean;
  } = {},
): ParsedApiErrorBody {
  const b = (typeof body === 'object' && body !== null ? body : {}) as ApiErrorBody;
  const rawError = typeof b.error === 'string' ? b.error : undefined;
  const rawMessage = typeof b.message === 'string' ? b.message : undefined;

  const code =
    typeof b.code === 'string' && b.code.trim().length > 0
      ? b.code
      : rawError && isMachineCode(rawError)
        ? rawError.trim()
        : undefined;

  if (status === 401 && !opts.credentialEndpoint) {
    onUnauthorizedCallback?.();
    return { message: SESSION_EXPIRED_COPY, code, details: b.details };
  }

  // Pick the raw display candidate…
  let candidate = opts.preferBodyMessage
    ? (rawMessage ?? rawError)
    : (rawError ?? rawMessage);
  // …but when it is a machine token and the OTHER field carries a human
  // sentence (the `{ error: code, message: prose }` rate-limit dialect),
  // the server's own sentence beats a dictionary lookup.
  if (candidate !== undefined && isMachineCode(candidate)) {
    const other = candidate === rawError ? rawMessage : rawError;
    if (other !== undefined && other.trim().length > 0 && !isMachineCode(other)) {
      candidate = other;
    }
  }

  const message =
    candidate !== undefined && candidate.trim().length > 0
      ? errorCopy(candidate)
      : (opts.fallback ?? errorCopy(undefined));

  return { message, code, details: b.details };
}

/**
 * #711 — shared thrower for the legacy `throw new ApiError` helpers.
 * Reads the JSON body (tolerating non-JSON), routes it through
 * `parseErrorBody` (401 auto-logout + sanitation), and returns the
 * ApiError for the caller to throw.
 */
async function apiErrorFromResponse(
  res: Response,
  fallback: string,
  opts: { credentialEndpoint?: boolean; preferBodyMessage?: boolean } = {},
): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined; // non-JSON body — fallback copy carries the status
  }
  const parsed = parseErrorBody(res.status, body, { fallback, ...opts });
  return new ApiError(res.status, parsed.message, parsed.code);
}

/**
 * Core fetch wrapper used by every namespaced helper below. Pulls the
 * staff token from `readSession()` so callers don't have to thread it
 * through. Returns `{ data }` on success, `{ error, isRetryable? }` on
 * failure — same discriminated-union shape as HTHD's admin-app.
 *
 * Existing platform helpers above (`loginStaff`, `fetchAdminSchedule`,
 * `fetchCustomers`, etc.) still throw `ApiError` for back-compat with
 * pages already in tree; new ports of HTHD pages should consume the
 * `adminXxxApi` namespaces below and check `result.error`.
 */
export async function request<T>(
  apiUrl: string,
  endpoint: string,
  options: RequestInit = {},
  retryCfg: {
    /**
     * Override the cold-start retry budget (default MAX_RETRIES).
     * Endpoints whose 503/429 answers are DETERMINISTIC service states
     * rather than cold-start signatures (e.g. the AIM chat's
     * ai_unavailable / rate_limited) pass 0 so the user sees the real
     * answer immediately instead of after 3 futile backoff retries.
     */
    maxRetries?: number;
    /**
     * On a non-OK JSON body, prefer the human-readable `message` field
     * over the machine `error` code when both are present (#429). The
     * AIM chat's handled-error bodies carry
     * `{ error: 'ai_error', message: '<friendly sentence>' }` — without
     * this flag the raw code wins and gets rendered in the chat bubble.
     * OPT-IN and deliberately scoped: every other caller keeps the
     * existing `error`-first semantics (many rely on the code for typed
     * branching).
     */
    preferBodyMessage?: boolean;
  } = {},
): Promise<ApiResponse<T>> {
  const maxRetries = retryCfg.maxRetries ?? MAX_RETRIES;
  const { token } = readSession();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  let lastError: unknown;
  let lastWasRetryableStatus = false;
  let didRetry = false;
  let finalAttempt = 0;

  /**
   * Emit `resolved` only when we previously emitted `retrying`. A successful
   * first attempt is silent — no listener overhead, no banner flash.
   */
  const resolveIfRetried = (attempt: number): void => {
    if (didRetry) notifyRetryListeners(buildResolvedStatus(attempt));
  };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    finalAttempt = attempt;
    try {
      if (attempt > 0) {
        didRetry = true;
        notifyRetryListeners(buildRetryingStatus(attempt));
      }

      const response = await fetchWithTimeout(
        `${apiUrl}${endpoint}`,
        { ...options, headers },
        REQUEST_TIMEOUT_MS,
      );

      const statusIsRetryable = isRetryableStatus(response.status);
      if (statusIsRetryable && attempt < maxRetries) {
        lastWasRetryableStatus = true;
        await new Promise((r) =>
          setTimeout(r, computeBackoffMs(attempt + 1)),
        );
        continue;
      }
      // Either response was non-retryable OR we exhausted retries on a
      // retryable status. The latter still gets `isRetryable: true` so the
      // caller can show a "try again" CTA.
      lastWasRetryableStatus = statusIsRetryable;

      if (response.status === 401) {
        // #711 — parseErrorBody fires the global onUnauthorized and yields
        // the byte-exact session-expired copy; the body read (tolerant of
        // non-JSON) lets us preserve the machine token on `code`.
        let body401: unknown;
        try {
          body401 = await response.json();
        } catch {
          body401 = undefined;
        }
        const parsed401 = parseErrorBody(response.status, body401);
        resolveIfRetried(attempt);
        return {
          error: parsed401.message,
          ...(parsed401.code ? { code: parsed401.code } : {}),
        };
      }

      let json: unknown;
      try {
        json = await response.json();
      } catch {
        resolveIfRetried(attempt);
        if (!response.ok) {
          return {
            error: `Server error (${response.status}). Please try again.`,
            isRetryable: true,
          };
        }
        return { error: 'Unexpected server response. Please try again.' };
      }

      if (!response.ok) {
        const body = json as ApiErrorBody;
        resolveIfRetried(attempt);
        // #711 — sanitize via the shared choke point: machine codes map to
        // human copy (or the server's own prose `message`), and the raw
        // token is preserved on `code` for typed branching.
        const parsed = parseErrorBody(response.status, body, {
          fallback: 'An error occurred',
          preferBodyMessage: retryCfg.preferBodyMessage,
        });
        return {
          error: parsed.message,
          details: body.details,
          ...(parsed.code ? { code: parsed.code } : {}),
          ...(typeof (body as { gatewayCode?: unknown }).gatewayCode === 'string'
            ? { gatewayCode: (body as { gatewayCode: string }).gatewayCode }
            : {}),
          ...(typeof (body as { held_transaction_id?: unknown })
            .held_transaction_id === 'string'
            ? {
                heldTransactionId: (body as { held_transaction_id: string })
                  .held_transaction_id,
              }
            : {}),
          ...(statusIsRetryable ? { isRetryable: true } : {}),
          ...(typeof (body as { reason?: unknown }).reason === 'string'
            ? { reason: (body as { reason: string }).reason }
            : {}),
        };
      }

      resolveIfRetried(attempt);
      return { data: json as T };
    } catch (err) {
      lastError = err;
      if (!isRetryableError(err) || attempt === maxRetries) break;
      await new Promise((r) => setTimeout(r, computeBackoffMs(attempt + 1)));
    }
  }

  // All retries exhausted. Notify resolved so banner dismisses, then return
  // an error shape with the appropriate user-facing copy.
  resolveIfRetried(finalAttempt);

  if (lastWasRetryableStatus) {
    return {
      error: 'The server is still starting up. Please wait a moment and try again.',
      isRetryable: true,
    };
  }
  if (lastError instanceof DOMException && lastError.name === 'AbortError') {
    return {
      error: 'Request timed out. The server may be starting up — please try again.',
      isRetryable: true,
    };
  }
  return {
    error: 'Unable to reach the server. Please check your connection and try again.',
    isRetryable: true,
  };
}

/** Thin verb-shaped wrapper around `request<T>`, mirrors HTHD's `api.*`. */
export const api = {
  get: <T>(apiUrl: string, endpoint: string): Promise<ApiResponse<T>> =>
    request<T>(apiUrl, endpoint, { method: 'GET' }),
  post: <T>(apiUrl: string, endpoint: string, body?: unknown): Promise<ApiResponse<T>> =>
    request<T>(apiUrl, endpoint, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(apiUrl: string, endpoint: string, body?: unknown): Promise<ApiResponse<T>> =>
    request<T>(apiUrl, endpoint, {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(apiUrl: string, endpoint: string, body?: unknown): Promise<ApiResponse<T>> =>
    request<T>(apiUrl, endpoint, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(apiUrl: string, endpoint: string): Promise<ApiResponse<T>> =>
    request<T>(apiUrl, endpoint, { method: 'DELETE' }),
};

export async function loginStaff(
  apiUrl: string,
  body: StaffLoginRequest,
): Promise<StaffLoginResponse> {
  const res = await fetch(`${apiUrl}/api/auth/staff/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // Credential endpoint: a 401 here means "wrong credentials" (server
    // prose: "Invalid credentials" / "Account deactivated"), NOT an
    // expired session — keep the server copy, never fire the global
    // auto-logout (there is no session to expire yet).
    throw await apiErrorFromResponse(res, `Login failed (${res.status})`, {
      credentialEndpoint: true,
    });
  }

  return (await res.json()) as StaffLoginResponse;
}

export const storageKeys = {
  token: 'ferro.admin.token',
  staff: 'ferro.admin.staff',
} as const;

export function saveSession(token: string, staff: StaffUser): void {
  window.localStorage.setItem(storageKeys.token, token);
  window.localStorage.setItem(storageKeys.staff, JSON.stringify(staff));
}

export function clearSession(): void {
  window.localStorage.removeItem(storageKeys.token);
  window.localStorage.removeItem(storageKeys.staff);
}

export function readSession(): { token: string | null; staff: StaffUser | null } {
  const token = window.localStorage.getItem(storageKeys.token);
  const raw = window.localStorage.getItem(storageKeys.staff);
  let staff: StaffUser | null = null;
  if (raw) {
    try {
      staff = JSON.parse(raw) as StaffUser;
    } catch {
      staff = null;
    }
  }
  return { token, staff };
}

export type StaffSessionValidation = 'valid' | 'invalid' | 'unknown';

/**
 * BUG-6 — validate the persisted staff token against the server BEFORE the
 * admin shell renders. A stale/expired token returns 'invalid' (server 401)
 * so AuthContext can clear the session in ONE request instead of letting every
 * dashboard widget fire a doomed request (the 401 burst → first-401
 * self-logout → bounce to /login). A transient/cold-start failure (network
 * error or 5xx) returns 'unknown' so a Neon wake-up doesn't masquerade as an
 * expired session — mirrors the customer-side 503 posture. Uses a raw fetch
 * (not `request<T>`) so it stays decoupled from the global onUnauthorized
 * auto-logout and fully owns the keep-vs-clear decision.
 */
export async function validateStaffSession(
  apiUrl: string,
): Promise<StaffSessionValidation> {
  const { token } = readSession();
  if (!token) return 'invalid';
  try {
    const res = await fetch(`${apiUrl}/api/auth/staff/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) return 'invalid';
    if (res.ok) return 'valid';
    return 'unknown'; // 5xx / cold-start / unexpected → keep the session
  } catch {
    return 'unknown'; // network error → keep the session (don't log out)
  }
}

/**
 * #546 — immunization shield status, derived server-side by the shared
 * `buildDogCard()` so the dashboard and the Bookings list never disagree.
 *  - `expired`  → RED shield
 *  - `expiring` → ORANGE shield (earliest expiry within 30 days)
 *  - `none`     → ORANGE attention shield (NO records — never a silent pass)
 *  - `ok`       → hidden/neutral (has records, none expiring)
 */
export type DogVaxLevel = 'expired' | 'expiring' | 'none' | 'ok';

/**
 * Ted #28 (#1050) — transport on a STAY. Mirrors the server `TransportMode`
 * (`dog-flags.ts`): `arrival` = the van brings the dog in (check-in must be
 * verified), `departure` = the van takes the dog home, `both` = both.
 */
export type TransportMode = 'none' | 'arrival' | 'departure' | 'both';

export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  none: 'No transport',
  arrival: 'Transport in (we pick the dog up)',
  departure: 'Transport home (we drop the dog off)',
  both: 'Transport both ways',
};

/**
 * Ted #28 (#1050) — one visual flag that follows a dog, derived by the
 * server's ONE registry (`dog-flags.ts`) and rendered by the one
 * `DogFlagChips` component. `severity` maps to the status tokens — never a
 * brand colour, never colour alone: `label` is always shown as text.
 */
export type DogFlagKey =
  | 'transport'
  | 'vax'
  | 'intact'
  | 'altered_unknown'
  | 'meds'
  | 'vax_unverified'
  | 'visit_unpaid'
  | 'balance'
  | 'credit'
  | 'agreement'
  // Ted 09-24 / 09-29 (lane LC) — check-in is the loud moment.
  | 'grooming'
  | 'banned'
  | 'isolated'
  // Sawyer 10 customer-booking-verify-policy (C6) — a medication on the tenant's lockbox list.
  | 'lockbox';
export type DogFlagScope = 'dog' | 'stay' | 'account';
/** Gingr's per-condition alert moments (Phase 2). */
export type DogFlagAlertMoment = 'creation' | 'checkin' | 'checkout';
export type DogFlag = {
  /**
   * Stable machine key. A NEWER server may send a key this bundle has not
   * learned; the renderer still draws the pill from `label`.
   */
  key: DogFlagKey | (string & {});
  scope: DogFlagScope;
  severity: 'danger' | 'warn' | 'info';
  label: string;
  detail: string;
  /** True ⇒ the server refuses check-in until the desk confirms the dog is here. */
  requiresArrivalCheck: boolean;
  /**
   * When a surface should raise this flag as a (non-blocking) prompt.
   * Optional for deploy skew: a Phase 1 server omits it.
   */
  alertAt?: DogFlagAlertMoment[];
};

/** One dog on a dog-first card/row (#545/#546). Mirrors the server DogCardDto. */
export type DogCard = {
  dogId: string;
  name: string;
  breed: string | null;
  photoUrl: string | null;
  /** null → no sex glyph (unknown; never guessed). */
  sex: 'male' | 'female' | null;
  /** null → no altered glyph (unknown; never guessed). */
  altered: 'intact' | 'spayed' | 'neutered' | 'altered' | null;
  vax: { level: DogVaxLevel; detail: string };
  /**
   * Ted #28 (#1050) — the stay's flags for this dog on this surface.
   * Optional for deploy skew: a server older than #1050 omits it and the
   * strip renders no flag (never a false one).
   */
  flags?: DogFlag[];
  /**
   * Sawyer #1295 — the meals this dog ate none of today (`/today` rows only).
   * Absent on an older server and on surfaces that do not send it.
   */
  ateNone?: AteNoneWarning | null;
};

/**
 * Per-shot immunization level on the Dog ID Card — mirrors the server
 * `VaxItemLevel` (derived from the SAME 30-day threshold as the status strip's
 * summary, so a lapsed shot reads red on the card exactly as the strip shows).
 * `unknown` = a record with no expiry on file (present but never alerts).
 */
export type DogVaxItemLevel = 'expired' | 'expiring' | 'ok' | 'unknown';

/** One vaccination row on the Dog ID Card. */
export type DogIdCardVaccination = {
  id: string;
  name: string;
  /** ISO date (`@db.Date`) or null when no expiry is on file. */
  expiresAt: string | null;
  verified: boolean;
  status: DogVaxItemLevel;
  /**
   * Ted #32a (#1156) — a later record of the same vaccine replaced this one,
   * so it does not drive the dog's status. Optional: an older server omits it
   * (read as not replaced, exactly what it rendered before).
   */
  superseded?: boolean;
  /** Ted #32a (E1.7) — who supplied the values; optional for an older server. */
  source?: VaccinationSource | null;
};

/** One active medication row on the Dog ID Card. */
export type DogIdCardMedication = {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  startDate: string | null;
  endDate: string | null;
};

/**
 * The full Dog ID Card payload (#581-followup) — everything about one pet on a
 * single card, opened by clicking a dog's name on any dog-first surface.
 * Mirrors the server `idCardView`. Nulls stay null — the client renders
 * nothing rather than guessing an age/sex/status (honesty, #546).
 */
export type DogIdCard = {
  id: string;
  name: string;
  /** Ted #28 Phase 2 (#1050) — the dog's + owner's flags; absent on an older server. */
  flags?: DogFlag[];
  breed: string | null;
  /** ISO datetime or null; age is derived client-side, never when null. */
  birthDate: string | null;
  sizeCategory: string | null;
  weight: number | null;
  /** null → sex unknown; render no sex line (never guessed). */
  sex: 'male' | 'female' | null;
  /** null → altered status unknown (never guessed). */
  altered: 'intact' | 'spayed' | 'neutered' | 'altered' | null;
  isNeutered: boolean;
  alteredStatus: string | null;
  photoUrl: string | null;
  temperament: string | null;
  careInstructions: string | null;
  allergies: string | null;
  specialNeeds: string | null;
  feedingMethod: string | null;
  foodType: string | null;
  feedingNotes: string | null;
  vetName: string | null;
  vetPhone: string | null;
  vetEmail: string | null;
  vetAddress: string | null;
  /** Summary shield — the SAME derivation the dog-first strip renders. */
  vax: { level: DogVaxLevel; detail: string };
  vaccinations: DogIdCardVaccination[];
  medications: DogIdCardMedication[];
  owner: {
    customerId: string;
    firstName: string;
    lastName: string;
    phone: string;
  } | null;
};

/**
 * Fetch a single dog's ID Card. GET /api/admin/dogs/:dogId — tenant-scoped
 * server-side; a dog outside the caller's tenant answers 404 (surfaced here as
 * a friendly "not found" error, never a leak).
 */
export function fetchDogIdCard(
  apiUrl: string,
  dogId: string,
): Promise<ApiResponse<{ dog: DogIdCard }>> {
  return api.get<{ dog: DogIdCard }>(
    apiUrl,
    `/api/admin/dogs/${encodeURIComponent(dogId)}`,
  );
}

// ─── Pet Profile page (#553) ────────────────────────────────────────────
// The dedicated `/dogs/:dogId` admin page reads the FULL record from a
// separate endpoint (GET /api/admin/dogs/:dogId/profile) so the hot ID-card
// modal fetch stays lean. Mirrors the server `dogProfileView`. Nulls stay
// null — the page renders an honest empty, never a guessed value (#546).

/** One vaccination row on the Pet Profile — richer than the ID card's. */
export type DogProfileVaccination = {
  id: string;
  name: string;
  /** ISO date or null. */
  dateGiven: string | null;
  /** ISO date or null when no expiry on file. */
  expiresAt: string | null;
  verified: boolean;
  /** Staff display name who verified, or null. */
  verifiedByName: string | null;
  notes: string | null;
  /** ISO datetime — the compliance "last updated" provenance. */
  updatedAt: string;
  status: DogVaxItemLevel;
  /** Ted #32a (#1156) — replaced by a later record of the same vaccine (see DogIdCardVaccination). */
  superseded?: boolean;
  /**
   * Ted #32a (E1.7) — who supplied the values: 'gingr' | 'intake' | 'staff' |
   * 'customer', or null when unknown. Optional: an older server omits it.
   */
  source?: VaccinationSource | null;
  /**
   * Ted #32a (E1.6c) — the certificate staff attached, or null. Optional: an
   * older server omits it (no certificate shown, no error).
   */
  document?: DogVaccinationDocument | null;
};

/** The certificate staff attached to one vaccination row (metadata only). */
export type DogVaccinationDocument = {
  id: string;
  filename: string | null;
  contentType: string;
  size: number;
  uploadedAt: string;
};

/** One medication row on the Pet Profile (active AND past). */
export type DogProfileMedication = {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  /** Sawyer 08 meds-feeding (A1) — the times staff set. Absent on an older server. */
  slots?: string[];
  /** Sawyer A1 — the times the floor uses (set, else from frequency). */
  effectiveSlots?: string[];
  slotSource?: 'set' | 'frequency' | 'frequency_default' | 'unscheduled';
};

/**
 * #714 — which channel a note lives in. 'staff_only' notes NEVER appear in
 * the owner's app (the customer serializer strips them server-side);
 * 'owner_visible' is the explicit share.
 */
export type DogNoteAudience = 'staff_only' | 'owner_visible';

/** One behavior note on the Pet Profile — full severity/category fidelity. */
export type DogProfileBehaviorNote = {
  id: string;
  /** Raw category ('social'|'anxiety'|'aggression'|'feeding'|'play'|'general'). */
  category: string;
  /** 1=info … 5=critical. Humanized + colored client-side. */
  severity: number;
  note: string;
  /**
   * #714 — optional in the TYPE only for the merge→server-deploy skew
   * window (frontends auto-deploy on merge; the server deploy is manual).
   * The updated server always sends it; the chip renders only when present.
   */
  audience?: DogNoteAudience;
  reportedByName: string | null;
  createdAt: string;
};

/** One incident summary on the Pet Profile (recent liability history). */
export type DogProfileIncident = {
  id: string;
  kind: string;
  severity: string;
  status: string;
  occurredAt: string;
  narrative: string;
};

/** One report card summary on the Pet Profile. */
export type DogProfileReportCard = {
  id: string;
  createdAt: string;
  mood: string | null;
  rating: number | null;
  sentAt: string | null;
  notes: string;
};

/** One grooming note on the Pet Profile. */
export type DogProfileGroomingNote = {
  id: string;
  originalLanguage: string;
  originalText: string;
  translatedText: string | null;
  createdAt: string;
};

/**
 * The full Pet Profile payload (#553) — everything about one pet, for the
 * dedicated `/dogs/:dogId` page. A superset of the ID card. Mirrors the
 * server `dogProfileView`.
 */
export type DogProfile = {
  id: string;
  name: string;
  /**
   * Ted #28 Phase 2 (#1050) — the dog's flags (vax / INTACT / meds) and the
   * owner's (balance / credit / agreement), from the ONE registry. Absent
   * on a server older than Phase 2 (the header falls back to what the
   * profile already carries — never a false flag).
   */
  flags?: DogFlag[];
  breed: string | null;
  birthDate: string | null;
  sizeCategory: string | null;
  weight: number | null;
  color: string | null;
  microchipNumber: string | null;
  sex: 'male' | 'female' | null;
  altered: 'intact' | 'spayed' | 'neutered' | 'altered' | null;
  isNeutered: boolean;
  alteredStatus: string | null;
  alteredDate: string | null;
  photoUrl: string | null;
  temperament: string | null;
  goodWith: string | null;
  socialNotes: string | null;
  careInstructions: string | null;
  feedingMethod: string | null;
  foodType: string | null;
  feedingNotes: string | null;
  /** Sawyer 08 meds-feeding (A1) — meal times; empty = house default. Absent on an older server. */
  feedingSlots?: string[];
  medicationNotes: string | null;
  allergies: string | null;
  specialNeeds: string | null;
  isBanned: boolean;
  bannedReason: string | null;
  vetName: string | null;
  vetPhone: string | null;
  vetEmail: string | null;
  vetAddress: string | null;
  /**
   * #552 (SPEC 2026-10-26 Story 6) — the second regular practice, for when
   * the first is closed. ABSENT (undefined) on a server that predates it;
   * null when nothing is on file. The page offers "Edit vet" only when the
   * server sends these keys, because that server also takes the write.
   */
  secondaryVetName?: string | null;
  secondaryVetPhone?: string | null;
  secondaryVetEmail?: string | null;
  secondaryVetAddress?: string | null;
  emergencyVetName: string | null;
  emergencyVetPhone: string | null;
  emergencyAgent: string | null;
  emergencyAgentRelationship: string | null;
  emergencyAgentPhone: string | null;
  /** Whole US dollars, or null. */
  emergencyVetCostLimit: number | null;
  notes: string | null;
  lastGroomDate: string | null;
  /** Summary shield — the SAME derivation the strip/ID card render. */
  vax: { level: DogVaxLevel; detail: string };
  vaccinations: DogProfileVaccination[];
  medications: DogProfileMedication[];
  behaviorNotes: DogProfileBehaviorNote[];
  incidents: DogProfileIncident[];
  reportCards: DogProfileReportCard[];
  groomingNotes: DogProfileGroomingNote[];
  owner: {
    customerId: string;
    firstName: string;
    lastName: string;
    phone: string;
  } | null;
  /**
   * Ted #28 (#1050) — the next (or current) stay that carries transport, so
   * the profile shows "Transport in · Tue 9/23". Transport is a property of
   * the stay, never of the dog. Optional for deploy skew; null when none.
   */
  nextTransportStay?: {
    bookingId: string;
    /** YYYY-MM-DD, the stay's first day. */
    date: string;
    transport: TransportMode;
    flags: DogFlag[];
  } | null;
  /**
   * #1116 — the desk's view of Ted's spay/neuter reminder, derived by the
   * server from the dog on every read. Optional for deploy skew: an older
   * server omits it and the profile shows nothing (never a guessed line).
   */
  spayNeuterReminder?: SpayNeuterReminderStatus | null;
  /** #1116 AC5 — the Do Not Admit decision with its provenance. Optional for deploy skew. */
  doNotAdmit?: DoNotAdmitStatus | null;
  /**
   * Sawyer 09 pet-inactivity (D1) — derived on every read; null when the read
   * failed. Optional for deploy skew: an older server omits it and the
   * profile shows nothing.
   */
  inactivity?: PetInactivityView | null;
};

// Sawyer 09 pet-inactivity (D1) — mirrors `InactivityView` in core (dog-profile/inactivity.ts).
export type PetInactivityView =
  | { rule: 'off' }
  | {
      rule: 'on';
      months: number;
      state: 'active';
      lastVisitOn: string | null;
      addedOn: string;
      becomesInactiveOn: string;
      needsMeetAndGreet: false;
    }
  | {
      rule: 'on';
      months: number;
      state: 'inactive';
      lastVisitOn: string | null;
      addedOn: string;
      inactiveSince: string;
      needsMeetAndGreet: boolean;
    };

/** Mirrors `SpayNeuterReminderStatus` in core (spay-neuter-reminder.ts). */
export type SpayNeuterReminderStatus = {
  state: 'queued' | 'due' | 'scheduled' | 'not_applicable';
  reason: 'not_intact' | 'birth_date_missing' | 'sex_unknown' | 'window_passed' | null;
  dueOn: string | null;
  months: number | null;
  queuedAt: string | null;
  delivery: 'pending' | 'sent' | 'failed' | 'cancelled' | null;
  sentAt: string | null;
  /** The sentence the desk reads. */
  summary: string;
};

/** Mirrors `DoNotAdmitView` in admin-dogs.ts. */
export type DoNotAdmitStatus = {
  active: boolean;
  reason: string | null;
  setByName: string | null;
  setAt: string | null;
};

/**
 * Fetch a single dog's full Pet Profile. GET /api/admin/dogs/:dogId/profile —
 * tenant-scoped server-side; a dog outside the caller's tenant answers 404
 * (surfaced here as a friendly "not found" error, never a leak).
 */
export function fetchDogProfile(
  apiUrl: string,
  dogId: string,
): Promise<ApiResponse<{ dog: DogProfile }>> {
  return api.get<{ dog: DogProfile }>(
    apiUrl,
    `/api/admin/dogs/${encodeURIComponent(dogId)}/profile`,
  );
}

/**
 * #550 — staff photo capture. Uploads a dog photo (multipart) to the staff
 * endpoint and returns the durable URL the server persisted.
 *
 * Multipart, NOT JSON: the shared `request()` wrapper always sends
 * `application/json`, which would corrupt the binary body. Here we set the
 * Authorization header ourselves and let the browser choose the
 * `multipart/form-data` Content-Type + boundary. No new dependency — native
 * `FormData` + `fetch`.
 */
export async function uploadDogPhoto(
  apiUrl: string,
  dogId: string,
  file: File,
): Promise<ApiResponse<{ photoUrl: string }>> {
  const { token } = readSession();
  const form = new FormData();
  form.append('photo', file);
  try {
    const res = await fetch(
      `${apiUrl}/api/admin/dogs/${encodeURIComponent(dogId)}/photo`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      },
    );
    let body: { photoUrl?: string; error?: string; retryable?: boolean } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      // Non-JSON body (proxy/edge error) — fall through to a generic message.
    }
    if (!res.ok) {
      // #711 review fix — this multipart path bypassed the choke point:
      // raw body.error reached render state and a 401 (expired staff JWT
      // mid-upload) never fired the global auto-logout. parseErrorBody
      // gives it the same 401 + sanitation semantics as every other path.
      const parsed = parseErrorBody(res.status, body, {
        fallback: `Upload failed (${res.status}).`,
      });
      return {
        error: parsed.message,
        ...(parsed.code ? { code: parsed.code } : {}),
        isRetryable: body.retryable ?? res.status >= 500,
      };
    }
    if (!body.photoUrl) {
      return { error: 'The server did not return a photo URL. Please try again.' };
    }
    return { data: { photoUrl: body.photoUrl } };
  } catch {
    return {
      error: 'Could not reach the server. Check your connection and try again.',
      isRetryable: true,
    };
  }
}

export type AdminBooking = {
  id: string;
  customer_id: string;
  service_type_id: string;
  date: string;
  start_date?: string | null;
  end_date?: string | null;
  start_time: string | null;
  // Becky-T2.7 — optional customer-requested drop-off / pick-up windows.
  // Null when the customer didn't specify a time (no constraint).
  drop_off_time: string | null;
  pick_up_time: string | null;
  status: string;
  total_cents: number;
  notes?: string | null;
  cancel_reason?: string | null;
  grooming_sub_service_id?: string | null;
  // Combo bookings (boarding+grooming) cross-link via this id. Present when
  // the booking is part of a combo — cancelling one cancels its partner
  // server-side, so the admin Cancel confirm warns about the cascade (#314).
  linked_booking_id?: string | null;
  // #328 A#4 — dog-first display: the schedule list endpoint enriches each row
  // with the owner name + booked dog names so a row reads "🐕 Cozette — Jodie
  // Royer" instead of a hex id. Optional (other booking responses omit them).
  customer_name?: string | null;
  dog_names?: string[];
  // #545/#546 — dog-first cards: per-dog photo/breed/sex/altered + vax shield,
  // and the row's balance-due state. Populated by the schedule endpoint only;
  // optional so other booking responses (and older servers) safely omit them.
  dogs?: DogCard[];
  balance_due_cents?: number;
  unpaid?: boolean;
  /**
   * Ted 10-09 (#915) — false only on a STAFF booking whose client was never
   * sent a confirmation; null when not applicable (customer-made, Gingr, a
   * leg) or unknown; absent from an older server. Render on `=== false` only.
   */
  customer_notified?: boolean | null;
  /**
   * Sawyer #1296 — true once this booking's note was kept in the owner's
   * notes, false when it was not, null when the server could not tell, and
   * absent from a server older than #1296. The owner's-notes prompt asks
   * only on `=== false`.
   */
  booking_note_in_owner_notes?: boolean | null;
  // Ted #27 (#988) — the Gingr reservation this row mirrors. Populated by the
  // outstanding feed only; the desk checks the visit out in GINGR first
  // (the 06:00 MT sync rewrites status for 30 days), so the id is what makes
  // that possible from this screen.
  gingr_reservation_id?: string | null;
  // 'customer' | 'staff' | 'gingr_import' — where the reservation came from.
  booking_source?: string | null;
  // Ted #23 (#984) / Ted #20 Part B (#940) — SERVICE LEGS. `isLeg` true means
  // this row IS an added service hanging off `parent_booking_id`, not a visit
  // of its own; `add_on_id` names the catalog row it was priced from.
  // Optional for deploy skew — frontends auto-deploy on merge and the server
  // does not, so these are legitimately absent until the Railway deploy lands,
  // and absent reads as "an ordinary booking" (the pre-#984 list).
  parent_booking_id?: string | null;
  add_on_id?: string | null;
  isLeg?: boolean;
  // Ted #29 (#1108) — per-date leg lines: the add-on's own name ("Walk"),
  // who performed it (id + display name). Populated by the schedule
  // endpoint; optional for deploy skew and absent on other responses.
  add_on_name?: string | null;
  assigned_staff_id?: string | null;
  assigned_staff_name?: string | null;
  // Ted #28 (#1050) — transport on the stay and the desk's arrival check.
  // Optional for deploy skew (absent ⇒ 'none' / never verified).
  transport?: TransportMode;
  arrival_verified_at?: string | null;
  // LG — Ted 09-29 gates (board Contracts §2 / §3). Optional for deploy skew:
  // absent ⇒ no hold, nobody asked. `review_held` is the server's one
  // definition of "waiting on a manager" (reason present, not yet cleared);
  // the reason stays on the row after clearing so it still says why.
  review_hold_reason?: string | null;
  review_cleared_at?: string | null;
  review_held?: boolean;
  // Ted 09-29 (#1111 AC6, lane LC) — isolated mid-stay (in heat). Optional
  // for deploy skew (absent ⇒ not isolated).
  isolated_at?: string | null;
  isolated_reason?: string | null;
  // Ted 09-29 (#1111 AC1–2, lane LG writes; LC reads at drop-off) — the
  // owner's "not in heat" declaration at reservation time. The only storable
  // value is 'not_in_heat'; rendered ONLY when the field is present.
  heat_declaration?: 'not_in_heat' | null;
  heat_declared_at?: string | null;
  // Sawyer 10 customer-booking-verify-policy (C5 C6) — the owner's attestations at reservation (ISO).
  // Optional for deploy skew: absent or null ⇒ the pills render nothing.
  care_attested_at?: string | null;
  medication_policy_ack_at?: string | null;
  // Ted #30 (#1109, lane LR) — recurring series membership. PRESENT (as a
  // value or null) only on a server that ships series; the row's series
  // surfaces render only when `series_id !== undefined` (deploy-window guard).
  series_id?: string | null;
  series_detached_at?: string | null;
};

/**
 * Map a raw server error string to a friendly, human-readable sentence for
 * display. Server messages that are already prose pass through unchanged.
 *
 * #711 — the fallback is FLIPPED: machine-shaped input the dictionary
 * doesn't know maps to the generic copy, NEVER back to the raw input.
 * (Pre-fix this map had 5 keys, none of which the server emits on 401,
 * and unknown codes passed through raw — which is how a logged-in owner
 * saw the literal string `invalid_token` on /schedule.) Delegates to the
 * shared `errorCopy` mapper; the browser-runtime phrases below predate it
 * and keep their friendlier network copy.
 */
export function humanizeApiError(raw: string | null | undefined): string {
  const key = (raw ?? '').toLowerCase().trim();
  const runtimePhrases: Record<string, string> = {
    'not found': "We couldn't find that — it may have been removed.",
    'failed to fetch': 'Network problem — check your connection and try again.',
    'network error': 'Network problem — check your connection and try again.',
  };
  const mapped = runtimePhrases[key];
  if (mapped) return mapped;
  return errorCopy(raw);
}

export function todayIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export async function fetchAdminSchedule(
  apiUrl: string,
  token: string,
  range: {
    startDate: string;
    endDate: string;
    serviceTypeId?: string;
    status?: string;
    /** Ted #26 (#987) — one household's rows, not the whole tenant's. */
    customerId?: string;
  },
): Promise<AdminBooking[]> {
  const params = new URLSearchParams({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  if (range.serviceTypeId) params.set('serviceTypeId', range.serviceTypeId);
  if (range.status) params.set('status', range.status);
  if (range.customerId) params.set('customerId', range.customerId);
  const url = `${apiUrl}/api/bookings/admin/schedule?${params.toString()}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to load schedule (${res.status})`);
  }
  const data = (await res.json()) as { bookings: AdminBooking[] };
  return data.bookings ?? [];
}

/**
 * Ted #27 part 2 (#988) — "Outstanding — past, not completed".
 *
 * `before` is the facility day the server measured the list against, and
 * `total` is the count the tab badge shows: badge and list come from ONE
 * response so they can never disagree (Design Philosophy, Principle 1).
 */
export interface OutstandingBookingsResult {
  readonly bookings: AdminBooking[];
  readonly total: number;
  readonly before: string;
  readonly timezone: string;
}

export async function fetchOutstandingBookings(
  apiUrl: string,
  token: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<OutstandingBookingsResult> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const url = `${apiUrl}/api/bookings/admin/outstanding${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    throw await apiErrorFromResponse(
      res,
      `Failed to load outstanding reservations (${res.status})`,
    );
  }
  const data = (await res.json()) as Partial<OutstandingBookingsResult>;
  const bookings = data.bookings ?? [];
  return {
    bookings,
    // An older server that answers without `total` must not make the badge
    // lie: fall back to what we can actually see, never to 0.
    total: typeof data.total === 'number' ? data.total : bookings.length,
    before: data.before ?? '',
    timezone: data.timezone ?? '',
  };
}

export type ServiceTypeSummary = {
  id: string;
  name: string;
  display_name: string;
  base_price_cents: number;
  duration_minutes: number | null;
  sort_order: number;
  /** Ted #28 Phase 1b — added to a visit as a line, never booked alone (transport). Absent on an older server. */
  leg_only?: boolean;
};

export async function fetchServiceTypes(
  apiUrl: string,
  token: string,
): Promise<ServiceTypeSummary[]> {
  const data = await getJson<{ serviceTypes: ServiceTypeSummary[] }>(
    `${apiUrl}/api/bookings/admin/service-types`,
    token,
  );
  return data.serviceTypes ?? [];
}

type BookingAction =
  | 'confirm'
  | 'check-in'
  | 'check-out'
  | 'cancel'
  | 'undo-check-in'
  | 'undo-check-out';

async function postBookingAction(
  apiUrl: string,
  token: string,
  id: string,
  action: BookingAction,
  body: Record<string, unknown> = {},
): Promise<AdminBooking> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${id}/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to ${action} booking (${res.status})`);
  }
  const data = (await res.json()) as { booking: AdminBooking };
  return data.booking;
}

/**
 * Ted #26 (#987) — confirm ONE pending reservation. Server: POST
 * /api/bookings/admin/:id/confirm (pending → confirmed, and the desk-confirm
 * choke point that queues the customer's confirmation + 24h reminder).
 *
 * The route has existed since the confirmation-notifications wave; what did
 * NOT exist was a caller. `adminBookingApi.confirmBooking` below targets
 * `/api/v2/admin/bookings/:id/confirm`, which is not mounted on this server
 * (the mounted admin booking router is `/api/bookings/admin`), so the admin
 * app could not confirm a booking at all — verified 2026-09-09, re-verified
 * in this branch. This is the real one; Bookings and Front Desk call it.
 */
export function confirmBooking(
  apiUrl: string,
  token: string,
  id: string,
  /** LG (#1114 AC 8) — a manager's reason for confirming a HELD reservation. Omitted on an ordinary confirm. */
  opts: { overrideReason?: string } = {},
): Promise<AdminBooking> {
  return postBookingAction(
    apiUrl,
    token,
    id,
    'confirm',
    opts.overrideReason ? { override_reason: opts.overrideReason } : {},
  );
}

/** Why a reservation in a batch was not confirmed. */
export type ConfirmRefusal =
  | { kind: 'not_found' }
  | { kind: 'not_pending'; current: string }
  /** #1033 — the id is a service added to a visit; the visit is confirmed instead. */
  | { kind: 'service_leg'; parentBookingId: string }
  /** LG (board Contracts §3) — held for staff review; a manager must say why to confirm it. */
  | { kind: 'review_hold'; reason: string }
  /** LG — an override reason was sent by a role the server does not let override. */
  | { kind: 'review_hold_override_forbidden' };

export interface ConfirmBatchResultItem {
  bookingId: string;
  outcome: 'confirmed' | 'refused';
  refusal: ConfirmRefusal | null;
  booking: AdminBooking | null;
}

export interface ConfirmBatchOutcome {
  results: ConfirmBatchResultItem[];
  confirmedCount: number;
  refusedCount: number;
}

interface ConfirmBatchWireItem {
  bookingId?: string;
  booking_id?: string;
  outcome?: string;
  refusal?: ConfirmRefusal | null;
  booking?: AdminBooking | null;
}

/**
 * Ted #26 (#987) — confirm the reservations the staffer left ticked.
 *
 * Per-item, never all-or-nothing: the response is 200 even when some rows
 * are refused, and each carries its own outcome. A caller that treats a
 * non-empty `results` with refusals as a failure has re-introduced exactly
 * the coupling this endpoint exists to remove.
 */
export async function confirmBookingsBatch(
  apiUrl: string,
  token: string,
  args: {
    bookingIds: string[];
    notifyCustomer: boolean;
    /** LG (#1114 AC 8) — applied to every HELD id in the batch; the server audits each. */
    overrideReason?: string;
  },
): Promise<ConfirmBatchOutcome> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/confirm-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      bookingIds: args.bookingIds,
      notifyCustomer: args.notifyCustomer,
      ...(args.overrideReason ? { overrideReason: args.overrideReason } : {}),
    }),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(
      res,
      `Couldn't confirm those reservations (${res.status})`,
    );
  }
  const data = (await res.json()) as { results?: ConfirmBatchWireItem[] };
  const results: ConfirmBatchResultItem[] = (data.results ?? []).map((r) => ({
    bookingId: r.bookingId ?? r.booking_id ?? '',
    outcome: r.outcome === 'confirmed' ? 'confirmed' : 'refused',
    refusal: r.refusal ?? null,
    booking: r.booking ?? null,
  }));
  return {
    results,
    confirmedCount: results.filter((r) => r.outcome === 'confirmed').length,
    refusedCount: results.filter((r) => r.outcome === 'refused').length,
  };
}

/**
 * Ted #26 (#987) — the sentence the desk reads next to a refused
 * reservation. The situation, never the technology (DESIGN-PHILOSOPHY
 * anti-patterns), and never a raw status enum.
 */
export function describeConfirmRefusal(refusal: ConfirmRefusal | null): string {
  if (!refusal) return 'could not be confirmed';
  if (refusal.kind === 'not_found') {
    return 'no longer there — it may have been changed or removed';
  }
  if (refusal.kind === 'service_leg') {
    return 'is a service added to a visit — confirm the visit instead';
  }
  if (refusal.kind === 'review_hold') {
    return `needs staff review first — ${refusal.reason}`;
  }
  if (refusal.kind === 'review_hold_override_forbidden') {
    return 'is held for review — only a manager or owner can confirm it';
  }
  switch (refusal.current) {
    case 'confirmed':
      return 'already confirmed';
    case 'checked_in':
      return 'already checked in';
    case 'checked_out':
    case 'completed':
      return 'already checked out';
    case 'cancelled':
      return 'was cancelled';
    case 'no_show':
      return 'was marked a no-show';
    default:
      return 'is no longer waiting to be confirmed';
  }
}

export function checkInBooking(
  apiUrl: string,
  token: string,
  id: string,
  /**
   * Ted #27 part 2 (#988) — the ACTUAL arrival instant for a visit the desk
   * found days late. Without it the check-in stamps today and the corrected
   * check-out is then refused 422 `before_check_in`, so the charge could
   * only land on the wrong day. Omitted / null ⇒ now (unchanged for a dog
   * standing at the desk).
   */
  checkedInAt?: string | null,
  /**
   * Ted #28 (#1050) — the desk's "yes, the dog is physically here" for a
   * TRANSPORTED arrival. Sent only after the server has refused with 409
   * `arrival_unverified` and a human confirmed in the dialog; the server
   * ignores it on any other stay, so it is never sent speculatively.
   */
  arrivalVerified?: boolean,
  /**
   * Ted 09-29 (#1111 AC5, lane LC) — the desk confirmed "not in heat" at
   * drop-off in the "Before you check in" step. Recorded by the server as an
   * audit row; sent only when the step actually asked.
   */
  heatConfirmed?: boolean,
): Promise<AdminBooking> {
  return postBookingAction(apiUrl, token, id, 'check-in', {
    ...(checkedInAt ? { checked_in_at: checkedInAt } : {}),
    ...(arrivalVerified ? { arrival_verified: true } : {}),
    ...(heatConfirmed ? { heat_confirmed: true } : {}),
  });
}

/**
 * Ted 09-24 (lane LC) — did the server refuse this check-in because a dog on
 * the booking is Do Not Admit? The ONE place the `do_not_admit` code is
 * recognised; there is no field to resubmit with — the stop is the answer.
 */
export function isDoNotAdmit(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'do_not_admit';
}

// ── Ted 09-29 (#1111 AC6–7, lane LC) — the desk's "In heat" action ─────────
// Server: POST /api/admin/dogs/:dogId/in-heat (admin-dogs-in-heat.ts). Phase 1
// `{ bookingId }` isolates the stay and files the incident; phase 2 adds the
// contact log and the outcome. Idempotent on the mark.

export type InHeatOutcome = 'pickup_arranged' | 'pickup_impossible';

export interface InHeatContact {
  name: string | null;
  phone: string;
  homePhone?: string | null;
  relationship?: string | null;
}

export interface InHeatResponse {
  marked: boolean;
  booking: {
    id: string;
    status: string;
    isolated_at: string | null;
    isolated_reason: string | null;
  };
  incident: { id: string; kind: string; severity: string } | null;
  dog: { id: string; name: string };
  contacts: {
    owner: InHeatContact | null;
    emergency: InHeatContact | null;
  };
}

export async function markStayInHeat(
  apiUrl: string,
  token: string,
  dogId: string,
  body: {
    bookingId: string;
    ownerContacted?: boolean;
    emergencyContactContacted?: boolean;
    outcome?: InHeatOutcome;
    incidentId?: string;
  },
): Promise<InHeatResponse> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${encodeURIComponent(dogId)}/in-heat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not mark the stay in heat (${res.status})`);
  }
  return (await res.json()) as InHeatResponse;
}

/**
 * Ted #28 (#1050) — did the server refuse this check-in because a
 * transported dog has not been confirmed present? The one place the
 * `arrival_unverified` code is recognised, so every check-in surface opens
 * the same confirm step for the same reason.
 */
export function isArrivalUnverified(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'arrival_unverified';
}

/**
 * Ted #16 — reverse a mistaken check-in. Server: POST
 * /api/bookings/admin/:id/undo-check-in (same permission as check-in).
 * Lands the booking back on `confirmed` with its check-in time cleared.
 */
export function undoCheckInBooking(
  apiUrl: string,
  token: string,
  id: string,
): Promise<AdminBooking> {
  return postBookingAction(apiUrl, token, id, 'undo-check-in');
}

/**
 * Ted #22 (#983) — reopen a check-out. Server: POST
 * /api/bookings/admin/:id/undo-check-out (same permission as check-out).
 * The booking returns to `checked_in`, its open invoice is voided, and a
 * package day the check-out burned is given back. A reason is required — it
 * lands on the voided invoice and on the audit row. 409 `checkout_has_payment`
 * when the visit is already paid: void or refund it in Payments first.
 */
export async function undoCheckOutBooking(
  apiUrl: string,
  token: string,
  id: string,
  reason: string,
): Promise<AdminBooking> {
  return postBookingAction(apiUrl, token, id, 'undo-check-out', { reason });
}

/**
 * Ted #23 (#984) / Ted #20 Part B (#940) — a SERVICE LEG: an added service
 * that is itself a booking, hanging off the visit it was added to.
 */
export interface AdminServiceLeg {
  id: string;
  status: string;
  date: string;
  startTime: string | null;
  totalCents: number;
  serviceTypeId: string;
  addOnId: string | null;
  parentBookingId: string | null;
  isLeg: boolean;
  notes: string | null;
  /** Ted #29 (#1108) — "performed by". Optional for deploy skew. */
  assignedStaffId?: string | null;
}

/** Ted #28 Phase 1b — what a transport leg does besides exist. */
export interface AdminAddServiceLegOutcome {
  leg: AdminServiceLeg;
  /** The stay's flag moved (from ≠ to). Null for a non-transport leg or an unchanged flag. */
  transport: { from: TransportMode; to: TransportMode } | null;
  /** The leg was born at an overridden price; `leg.total_cents` is the override. */
  priceOverride: { catalogTotalCents: number } | null;
}

/**
 * Add a service to an existing visit.
 *
 * NOTE THE ABSENT PRICE. The server prices the leg from the catalog row or
 * the service type; there is no price field on this call, by design, so no
 * screen can ever quote one the server did not decide.
 *
 * Ted #28 Phase 1b (#1050) — the ONE exception is `priceOverride`, a #906-
 * shaped adjustment (new total + reason) the server accepts on a TRANSPORT
 * service only, inside the desk band unless the caller is an owner/manager.
 * `transport` is required for a transport service and refused otherwise.
 * Server: POST /api/bookings/admin/:id/legs → 201 { leg, transport, priceOverride }.
 */
export async function addServiceLeg(
  apiUrl: string,
  token: string,
  bookingId: string,
  body: {
    addOnId?: string;
    serviceTypeId?: string;
    date?: string;
    startTime?: string;
    groomerId?: string;
    assignedStaffId?: string;
    notes?: string;
    transport?: Exclude<TransportMode, 'none'>;
    priceOverride?: { totalCents: number; reason: string };
  },
): Promise<AdminAddServiceLegOutcome> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${bookingId}/legs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not add the service (${res.status})`);
  }
  const data = (await res.json()) as Partial<AdminAddServiceLegOutcome> & { leg: AdminServiceLeg };
  // An older server answers { leg } only; the outcome fields default to "nothing else happened".
  return { leg: data.leg, transport: data.transport ?? null, priceOverride: data.priceOverride ?? null };
}

/**
 * #984 review fix — ONE server answer to "what can I add to this visit, and
 * what will it cost?".
 *
 * The dialog used to build this itself from `adminGroomingApi.getAddOns`,
 * whose endpoint (`/api/v2/admin/grooming/add-ons`) HAS NEVER EXISTED: the
 * grooming mount registers only /queue*, so the deny-by-default admin guard
 * answered 403 route_not_mapped for every role including owner. `request()`
 * never throws, so `data` came back undefined and the add-on list was
 * silently empty on every desk — Ted's own case (a nail trim on Milo's
 * daycare) could not be done from the UI at all.
 *
 * This helper THROWS on failure on purpose. The old shape degraded a 403 into
 * `[]`, which the dialog then rendered as its EMPTY state — telling the desk
 * to go configure Settings when the real answer was a server error.
 *
 * `totalCents` is what the server WILL charge for this leg on this visit, for
 * this many dogs on this date, from the same function the add path prices
 * with. It is not `base_price_cents`: a weekend or peak pricing rule moves it.
 */
export interface AdminServiceLegOption {
  kind: 'addOn' | 'service';
  id: string;
  label: string;
  totalCents: number;
  durationMinutes: number | null;
  /** Ted #28 Phase 1b — a transport service: needs a direction, may be overridden, sets the stay's flag. */
  transport?: boolean;
  /**
   * Ted 09-29 / lane LG — TRUE on a transport option when this customer is
   * NOT marked grandfathered for transport. The dialog shows a caution naming
   * the rule ONLY when the field is present: an older server omits it, and
   * absent must read as "nothing to say", never as "allowed".
   */
  grandfather_caution?: boolean;
}

export async function fetchServiceLegOptions(
  apiUrl: string,
  token: string,
  bookingId: string,
): Promise<{
  options: AdminServiceLegOption[];
  dogCount: number;
  date: string;
  /** Ted #28 Phase 1b — the stay's current transport mode ('none' on an older server). */
  transport: TransportMode;
  /**
   * Ted #29 (#1108) — every calendar day of the stay, arrival through
   * departure. `[date]` on a single-day visit, and on an older server that
   * does not send it (the dialog then offers "this date" only).
   */
  stayDates: string[];
}> {
  const res = await fetch(
    `${apiUrl}/api/bookings/admin/${bookingId}/service-options`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(
      res,
      `Could not load the service list (${res.status})`,
    );
  }
  const data = (await res.json()) as {
    options: AdminServiceLegOption[];
    dogCount: number;
    date: string;
    transport?: TransportMode;
    stayDates?: string[];
  };
  return {
    ...data,
    transport: data.transport ?? 'none',
    stayDates: Array.isArray(data.stayDates) && data.stayDates.length > 0 ? data.stayDates : [data.date],
  };
}

// ── Ted #29 (#1108) — a service applied across a stay; one date edited ──────

/** One priced line of a stay-wide plan: what THIS date will be charged. */
export interface AdminStayServicePlanLine {
  date: string;
  totalCents: number;
  /** A live leg of the same service already on this date — the server skips it. */
  existingLegId: string | null;
}

export interface AdminApplyServiceAcrossStayOutcome {
  label: string;
  preview: boolean;
  plan: AdminStayServicePlanLine[];
  /** The sum of the lines created (or, on a preview, to be created). */
  totalCents: number;
  created: AdminServiceLeg[];
  skipped: Array<{ date: string; legId: string }>;
}

/**
 * Apply one service to every day of a stay or to chosen dates — one leg per
 * date, one transaction, idempotent per date + service. NO PRICE FIELD, as
 * the single add. `preview: true` prices the same plan through the same
 * server function and writes nothing: the number the dialog shows before
 * Apply IS the number the server charges (principle #5).
 * Server: POST /api/bookings/admin/:id/legs/batch → 201 (created) / 200 (preview, or nothing new).
 */
export async function applyServiceAcrossStay(
  apiUrl: string,
  token: string,
  bookingId: string,
  body: {
    addOnId?: string;
    serviceTypeId?: string;
    everyDay?: boolean;
    dates?: string[];
    startTime?: string;
    assignedStaffId?: string;
    notes?: string;
    preview?: boolean;
  },
): Promise<AdminApplyServiceAcrossStayOutcome> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${bookingId}/legs/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not apply the service across the stay (${res.status})`);
  }
  const data = (await res.json()) as Partial<AdminApplyServiceAcrossStayOutcome>;
  return {
    label: data.label ?? '',
    preview: data.preview === true,
    plan: data.plan ?? [],
    totalCents: data.totalCents ?? 0,
    created: data.created ?? [],
    skipped: data.skipped ?? [],
  };
}

export interface AdminUpdateServiceLegOutcome {
  leg: AdminServiceLeg;
  priceChange: { fromCents: number; toCents: number; reason: string } | null;
  assigneeChange: { from: string | null; to: string | null } | null;
}

/**
 * Edit ONE date's leg: who performed it (`null` clears it), and/or its price
 * with a reason — the same #906 band as Adjust price (inside 20% for the
 * desk; past it an owner/manager). Refused once the leg has an invoice line
 * (adjust it on the checkout then). Server: PATCH /api/bookings/admin/:id/legs/:legId.
 */
export async function updateServiceLeg(
  apiUrl: string,
  token: string,
  bookingId: string,
  legId: string,
  body: {
    assignedStaffId?: string | null;
    priceOverride?: { totalCents: number; reason: string };
  },
): Promise<AdminUpdateServiceLegOutcome> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${bookingId}/legs/${legId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not update the service (${res.status})`);
  }
  const data = (await res.json()) as Partial<AdminUpdateServiceLegOutcome> & { leg: AdminServiceLeg };
  return { leg: data.leg, priceChange: data.priceChange ?? null, assigneeChange: data.assigneeChange ?? null };
}

/** Take an added service back off a visit. Server: DELETE /:id/legs/:legId. */
export async function removeServiceLeg(
  apiUrl: string,
  token: string,
  bookingId: string,
  legId: string,
  reason?: string,
): Promise<AdminServiceLeg> {
  const res = await fetch(
    `${apiUrl}/api/bookings/admin/${bookingId}/legs/${legId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not remove the service (${res.status})`);
  }
  const data = (await res.json()) as { leg: AdminServiceLeg };
  return data.leg;
}

export function checkOutBooking(
  apiUrl: string,
  token: string,
  id: string,
  notes?: string,
  /**
   * Ted #6 — the ACTUAL check-out instant (ISO 8601) when the dog left
   * earlier than the desk is recording it. Omitted ⇒ the server stamps now.
   */
  checkedOutAt?: string,
): Promise<AdminBooking> {
  return postBookingAction(apiUrl, token, id, 'check-out', {
    ...(notes ? { notes } : {}),
    ...(checkedOutAt ? { checked_out_at: checkedOutAt } : {}),
  });
}

// ── Ted #25 (#986) — edit an existing reservation ─────────────────────────
// Two calls, deliberately: PREVIEW re-prices and re-gates and writes nothing,
// COMMIT re-derives everything server-side. The client never sends a price —
// a stale preview can never become the charge (#918 spec §2.3).

/** What the desk is proposing. Absent = leave it alone; null = clear it. */
export interface ReservationEditPatch {
  serviceTypeId?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  dropOffTime?: string | null;
  pickUpTime?: string | null;
  notes?: string | null;
  /** Ted #28 (#1050) — transport on the stay. Absent = leave it alone. */
  transport?: TransportMode;
  /** #1129 (lane LR) — the heat answers an edit that changes the service or date may now need. */
  heatDeclarations?: HeatDeclarations;
  reason?: string;
  notifyCustomer?: boolean;
  /**
   * #718 override — round-4 review fix. The server answers an unassessed dog
   * with 409 `assessment_required` and `override_field:
   * 'assessmentOverrideReason'`, telling the desk to re-submit with a reason.
   * Until this field existed the admin app could not send it, so that 409 was
   * a dead end and the only way past it was cancel-and-recreate — the exact
   * workflow #986 exists to delete. Separate from `reason` (the ledger note)
   * because it is a DIFFERENT record: this one lands in the audit log as an
   * override, attributed to the authenticated staff member. Server schema
   * requires 3–300 characters.
   */
  assessmentOverrideReason?: string;
}

export interface ReservationEditChange {
  field: string;
  from: string | null;
  to: string | null;
}

export interface ReservationEditPreview {
  allowed: boolean;
  booking: AdminBooking;
  changes: ReservationEditChange[];
  price: { currentCents: number; newCents: number; deltaCents: number };
  service_type: { id: string; display_name: string };
  date_changed: boolean;
}

export interface ReservationEditResult {
  booking: AdminBooking;
  changes: ReservationEditChange[];
  price: { beforeCents: number; afterCents: number; deltaCents: number };
  edit_id: string;
  notify_customer: boolean;
}

async function postReservationEdit<T>(
  apiUrl: string,
  token: string,
  id: string,
  path: 'edit' | 'edit/preview',
  patch: ReservationEditPatch,
): Promise<T> {
  const res = await fetch(`${apiUrl}/api/bookings/admin/${id}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    // The server's gate refusals carry plain prose ("No availability for the
    // selected date", "Bella needs a first-visit assessment…") — surface it
    // verbatim rather than inventing client-side copy for a server rule.
    throw await apiErrorFromResponse(res, `Could not update this reservation (${res.status})`);
  }
  return (await res.json()) as T;
}

/** Re-price + re-gate WITHOUT writing. Safe to call on every field change. */
export function previewReservationEdit(
  apiUrl: string,
  token: string,
  id: string,
  patch: ReservationEditPatch,
): Promise<ReservationEditPreview> {
  return postReservationEdit<ReservationEditPreview>(apiUrl, token, id, 'edit/preview', patch);
}

/** Commit. The booking keeps its id; one booking_edits ledger row is written. */
export function editReservation(
  apiUrl: string,
  token: string,
  id: string,
  patch: ReservationEditPatch,
): Promise<ReservationEditResult> {
  return postReservationEdit<ReservationEditResult>(apiUrl, token, id, 'edit', patch);
}

export function cancelBooking(
  apiUrl: string,
  token: string,
  id: string,
  reason?: string,
): Promise<AdminBooking> {
  return postBookingAction(apiUrl, token, id, 'cancel', reason ? { reason } : {});
}

// ─── CRM ───────────────────────────────────────────────────────────────

export const CUSTOMER_SEGMENTS = [
  'all',
  'active',
  'unclaimed',
  'vip',
  'new',
  'lapsed',
] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

export type CustomerSummary = {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone: string;
  points_balance: number;
  account_status: string;
  source: string;
  created_at: string;
  /**
   * #328 A#4 — active dog names for dog-first list framing (server: crm `dogs`).
   * Optional on the wire: older server builds and non-CRM payloads omit it, so
   * the UI must default to [] before reading `.length` (deploy-order safety).
   */
  dogs?: string[];
  /**
   * #751b — the same dogs, in the same order, carrying their ids so a dog-led
   * search result can navigate to `/dogs/:dogId`. Optional for the SAME reason
   * `dogs` is, and it bites harder here: frontends auto-deploy on merge while
   * the server needs a manual Railway deploy, so this build WILL run against a
   * server that omits it. Every consumer must fall back to the customer route
   * rather than routing to `/dogs/undefined`.
   */
  dog_refs?: Array<{ id: string; name: string }>;
};

export type CustomerDetail = CustomerSummary & {
  referral_code: string;
  referred_by: { id: string; name: string } | null;
  referrals_count: number;
  // #593 — profile redesign. All optional on the wire so an older server
  // build (pre-#593) that omits them can't crash the page; the UI treats
  // absent/null as "not on file" and renders honest empty states.
  home_phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  sms_deals_opted_out?: boolean;
  opt_out_photo_sharing?: boolean;
  opt_out_rewards?: boolean;
  acknowledged_agreement_version?: number | null;
  /** Wallet balance in cents; null when the customer has no wallet. */
  wallet_balance_cents?: number | null;
  wallet_tier?: string | null;
  open_invoice_count?: number;
  /** LG (#1112) — grandfathered for transport (board Contracts §4). Absent on an older server. */
  transport_grandfathered?: boolean;
};

/**
 * #715 — where a customer note came from. 'gingr_import' is written only
 * by the Gingr owner-notes import (#326); everything a person types at the
 * desk is 'staff'. Sawyer #1296: 'booking_note' is a booking's note the desk
 * chose to keep on the owner (source_ref = the booking id).
 */
export type CustomerNoteSource = 'staff' | 'gingr_import' | 'booking_note';

/**
 * #715 — a STAFF-ONLY note on the parent record. This shape exists only in
 * the admin app: no customer-app endpoint serves it, and a real-router
 * test (customer-notes-privacy-tenant-isolation.integration.test.ts)
 * asserts the note text is absent from every customer-auth payload.
 */
export type CustomerNote = {
  id: string;
  customer_id: string;
  staff_id: string | null;
  /** Display name of the author; null when unresolvable or imported. */
  author_name: string | null;
  author_role: string | null;
  body: string;
  pinned: boolean;
  source: CustomerNoteSource;
  source_ref: string | null;
  created_at: string;
  // Sawyer 11 client-notes-agreements (F1) — OPTIONAL on purpose: an older
  // server's payload has none of these keys, and the panel keys its pin
  // controls on `'pinned_at' in note` (deploy-window guard).
  /** When it was pinned; null while unpinned and on pins that predate attribution. */
  pinned_at?: string | null;
  pinned_by_staff_id?: string | null;
  pinned_by_name?: string | null;
  /** New to the CALLER (written by someone else, recently, after their last look). */
  is_new?: boolean;
};

export type ActivityEntry =
  | {
      kind: 'booking';
      at: string;
      id: string;
      date: string;
      status: string;
      service_name: string | null;
      total_cents: number;
    }
  | {
      kind: 'redemption';
      at: string;
      id: string;
      redemption_code: string;
      reward_tier: number;
      status: string;
    }
  | {
      kind: 'points';
      at: string;
      id: string;
      type: string;
      amount: number;
      description: string | null;
    }
  | {
      kind: 'note';
      at: string;
      id: string;
      body: string;
      pinned: boolean;
      staff_id: string | null;
      // #715 — same attribution the Notes panel shows.
      author_name?: string | null;
      author_role?: string | null;
      source?: CustomerNoteSource;
    };

async function getJson<T>(url: string, token: string): Promise<T> {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export async function fetchCustomers(
  apiUrl: string,
  token: string,
  args: {
    search?: string;
    segment?: CustomerSegment;
    limit?: number;
    offset?: number;
  } = {},
): Promise<{
  customers: CustomerSummary[];
  pagination: { limit: number; offset: number; total: number; has_more: boolean };
}> {
  const params = new URLSearchParams();
  if (args.search) params.set('search', args.search);
  params.set('segment', args.segment ?? 'all');
  params.set('limit', String(args.limit ?? 20));
  params.set('offset', String(args.offset ?? 0));
  return getJson(`${apiUrl}/api/admin/crm/customers?${params.toString()}`, token);
}

export async function fetchCustomer(
  apiUrl: string,
  token: string,
  id: string,
): Promise<{ customer: CustomerDetail }> {
  return getJson(`${apiUrl}/api/admin/crm/customers/${id}`, token);
}

export async function fetchCustomerActivity(
  apiUrl: string,
  token: string,
  id: string,
  limit = 50,
): Promise<{ activity: ActivityEntry[] }> {
  return getJson(
    `${apiUrl}/api/admin/crm/customers/${id}/activity?limit=${limit}`,
    token,
  );
}

export async function fetchCustomerNotes(
  apiUrl: string,
  token: string,
  id: string,
): Promise<{ notes: CustomerNote[] }> {
  return getJson(`${apiUrl}/api/admin/crm/customers/${id}/notes`, token);
}

// ─── #682 — a customer's notification preferences (staff view + edit) ────────

/** One flag per channel, wire (snake_case) names — the server's shape. */
export type CustomerNotificationPreferences = {
  email_bookings: boolean;
  email_reminders: boolean;
  email_messages: boolean;
  email_marketing: boolean;
  sms_bookings: boolean;
  sms_reminders: boolean;
  push_enabled: boolean;
};

export type NotificationChannel = keyof CustomerNotificationPreferences;

/** Where the current settings came from. Timestamps are ISO strings. */
export type NotificationPreferencesProvenance =
  | { kind: 'defaults' }
  | { kind: 'staff'; staff_name: string; changed_at: string }
  | { kind: 'customer_or_import'; changed_at: string };

export type CustomerNotificationPreferencesView = {
  preferences: CustomerNotificationPreferences;
  provenance: NotificationPreferencesProvenance;
  /** Channels staff may switch ON; any channel may be switched off. */
  staff_may_enable: NotificationChannel[];
};

const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = [
  'email_bookings',
  'email_reminders',
  'email_messages',
  'email_marketing',
  'sms_bookings',
  'sms_reminders',
  'push_enabled',
];

/**
 * Parse (don't trust) the preferences response. A body missing a channel or
 * carrying an unknown provenance is an error, never a card that silently
 * renders nothing or reads a missing flag as "Off".
 */
export function parseNotificationPreferencesView(
  body: unknown,
): CustomerNotificationPreferencesView | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  const prefs = b.preferences as Record<string, unknown> | undefined;
  if (typeof prefs !== 'object' || prefs === null) return null;
  if (!NOTIFICATION_CHANNELS.every((c) => typeof prefs[c] === 'boolean')) return null;
  const p = b.provenance as Record<string, unknown> | undefined;
  if (typeof p !== 'object' || p === null) return null;
  const provenanceOk =
    p.kind === 'defaults' ||
    (p.kind === 'staff' && typeof p.staff_name === 'string' && typeof p.changed_at === 'string') ||
    (p.kind === 'customer_or_import' && typeof p.changed_at === 'string');
  if (!provenanceOk) return null;
  const enable = b.staff_may_enable;
  if (
    !Array.isArray(enable) ||
    !enable.every((c) => NOTIFICATION_CHANNELS.includes(c as NotificationChannel))
  ) {
    return null;
  }
  return body as CustomerNotificationPreferencesView;
}

function notificationPreferencesViewOrThrow(body: unknown): CustomerNotificationPreferencesView {
  const view = parseNotificationPreferencesView(body);
  if (!view) {
    throw new ApiError(
      502,
      "The server's answer about these email settings didn't make sense. Try again.",
      'unexpected_response',
    );
  }
  return view;
}

export async function fetchCustomerNotificationPreferences(
  apiUrl: string,
  token: string,
  customerId: string,
): Promise<CustomerNotificationPreferencesView> {
  return notificationPreferencesViewOrThrow(
    await getJson<unknown>(
      `${apiUrl}/api/admin/customers/${customerId}/notification-preferences`,
      token,
    ),
  );
}

export async function updateCustomerNotificationPreferences(
  apiUrl: string,
  token: string,
  customerId: string,
  patch: Partial<CustomerNotificationPreferences>,
): Promise<CustomerNotificationPreferencesView> {
  const res = await fetch(
    `${apiUrl}/api/admin/customers/${customerId}/notification-preferences`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) {
    // The 422 (customer_consent_required) and 409 carry desk-readable copy.
    throw await apiErrorFromResponse(
      res,
      `Couldn't save notification settings (${res.status})`,
      { preferBodyMessage: true },
    );
  }
  return notificationPreferencesViewOrThrow(await res.json());
}

export async function addCustomerNote(
  apiUrl: string,
  token: string,
  id: string,
  body: { body: string; pinned?: boolean },
): Promise<{ note: CustomerNote }> {
  const res = await fetch(`${apiUrl}/api/admin/crm/customers/${id}/notes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to add note (${res.status})`);
  }
  return (await res.json()) as { note: CustomerNote };
}

// Issue #28 — admin customer create / update. Hits /api/admin/customers
// (live in apps/server/src/routes/admin-customers.ts) — not the CRM module's
// /api/admin/crm/customers reads.

export type CreateCustomerInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type UpdateCustomerInput = Partial<{
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  smsDealsOptedOut: boolean;
  /** LG (#1112) — the desk's "grandfathered for transport" switch. */
  transportGrandfathered: boolean;
}>;

export async function createCustomer(
  apiUrl: string,
  token: string,
  body: CreateCustomerInput,
): Promise<{ customer: CustomerSummary }> {
  const res = await fetch(`${apiUrl}/api/admin/customers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to create customer (${res.status})`);
  }
  return (await res.json()) as { customer: CustomerSummary };
}

export async function updateCustomer(
  apiUrl: string,
  token: string,
  id: string,
  body: UpdateCustomerInput,
): Promise<{ customer: CustomerSummary }> {
  const res = await fetch(`${apiUrl}/api/admin/customers/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to update customer (${res.status})`);
  }
  return (await res.json()) as { customer: CustomerSummary };
}

// Issue #33 — Communication Log (manual customer touchpoints).

export const COMMUNICATION_KINDS = [
  'phone',
  'email',
  'sms',
  'in_app',
  'in_person',
] as const;
export type CommunicationKind = (typeof COMMUNICATION_KINDS)[number];

export type CommunicationLogEntry = {
  id: string;
  customer_id: string;
  dog_id: string | null;
  dog_name: string | null;
  staff_id: string | null;
  staff_name: string | null;
  kind: CommunicationKind;
  reason: string | null;
  notes: string | null;
  occurred_at: string;
  created_at: string;
};

export type CreateCommunicationInput = {
  kind: CommunicationKind;
  reason?: string;
  notes?: string;
  dog_id?: string | null;
  occurred_at?: string;
};

export async function fetchCommunications(
  apiUrl: string,
  token: string,
  customerId: string,
  opts: { dogId?: string; limit?: number } = {},
): Promise<{ communications: CommunicationLogEntry[] }> {
  const params = new URLSearchParams();
  if (opts.dogId) params.set('dog_id', opts.dogId);
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const url = `${apiUrl}/api/admin/customers/${customerId}/communications${
    qs ? `?${qs}` : ''
  }`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to load communications (${res.status})`);
  }
  return (await res.json()) as { communications: CommunicationLogEntry[] };
}

export async function createCommunication(
  apiUrl: string,
  token: string,
  customerId: string,
  body: CreateCommunicationInput,
): Promise<{ communication: CommunicationLogEntry }> {
  const res = await fetch(
    `${apiUrl}/api/admin/customers/${customerId}/communications`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to log communication (${res.status})`);
  }
  return (await res.json()) as { communication: CommunicationLogEntry };
}

// Issue #34 — Grooming Notes with bilingual translation.

export const GROOMING_NOTE_LANGUAGES = ['en', 'es'] as const;
export type GroomingNoteLanguage = (typeof GROOMING_NOTE_LANGUAGES)[number];
export type GroomingNoteTranslationStatus =
  | 'pending'
  | 'translated'
  | 'failed';

export type GroomingNoteEntry = {
  id: string;
  dog_id: string;
  customer_id: string;
  staff_id: string | null;
  staff_name: string | null;
  original_language: GroomingNoteLanguage;
  original_text: string;
  translated_text: string | null;
  translation_status: GroomingNoteTranslationStatus;
  translation_error: string | null;
  created_at: string;
  updated_at: string;
};

export type CreateGroomingNoteInput = {
  original_language: GroomingNoteLanguage;
  original_text: string;
};

export async function fetchGroomingNotes(
  apiUrl: string,
  token: string,
  dogId: string,
): Promise<{ notes: GroomingNoteEntry[] }> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${dogId}/grooming-notes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to load grooming notes (${res.status})`);
  }
  return (await res.json()) as { notes: GroomingNoteEntry[] };
}

export async function createGroomingNote(
  apiUrl: string,
  token: string,
  dogId: string,
  body: CreateGroomingNoteInput,
): Promise<{ note: GroomingNoteEntry }> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${dogId}/grooming-notes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to save grooming note (${res.status})`);
  }
  return (await res.json()) as { note: GroomingNoteEntry };
}

export async function retranslateGroomingNote(
  apiUrl: string,
  token: string,
  noteId: string,
): Promise<{ note: GroomingNoteEntry }> {
  const res = await fetch(
    `${apiUrl}/api/admin/grooming-notes/${noteId}/translate`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to retry translation (${res.status})`);
  }
  return (await res.json()) as { note: GroomingNoteEntry };
}

// ─── Dog profile (admin) ─────────────────────────────────────────────────
//
// Surfaces a customer's dogs (with vaccinations and behavior notes) for the
// receptionist's customer-detail view, and lets staff add behavior notes.
// Backed by `apps/server/src/routes/admin-dogs.ts`. Field shape is snake_case
// to mirror the legacy HTHD admin `CustomerDog` contract so downstream
// components don't have to mediate between camelCase and snake_case.

export type AdminDogVaccination = {
  id: string;
  name: string;
  expires_at: string | null;
  verified: boolean;
  date_given: string | null;
  /**
   * Ted #32a (E1.7, #1156) — who supplied the values. Optional: an older
   * server omits it (read as unknown, which renders what it always did).
   */
  source?: VaccinationSource | null;
};

export type DogBehaviorNoteType = 'positive' | 'concern' | 'info';

export type AdminDogBehaviorNote = {
  id: string;
  type: DogBehaviorNoteType;
  note: string;
  /** #714 — optional only for the merge→server-deploy skew window. */
  audience?: DogNoteAudience;
  created_at: string;
  reported_by: string | null;
};

/**
 * Prescription-tracking record. Distinct from a medication-administration
 * (give-event) log — that table doesn't exist yet; Ted flagged the
 * workflow at the 2026-05-08 kickoff and it needs a separate session
 * with explicit Johnny GO for the migration. For now we surface the
 * active prescription list so receptionists can see "this dog is on
 * meds" at check-in.
 */
export type AdminDogMedication = {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
};

export type AdminDog = {
  id: string;
  name: string;
  breed: string | null;
  birth_date: string | null;
  size_category: string | null;
  weight: number | null;
  temperament: string | null;
  notes: string | null;
  photo_url: string | null;
  care_instructions: string | null;
  vet_name?: string | null;
  vet_phone?: string | null;
  vet_email?: string | null;
  vet_address?: string | null;
  is_neutered: boolean;
  altered_status: string | null;
  special_needs: string | null;
  social_notes: string | null;
  feeding_notes: string | null;
  vaccinations: AdminDogVaccination[];
  behavior_notes: AdminDogBehaviorNote[];
  medications: AdminDogMedication[];
  /**
   * #653 — server-DERIVED (never stored), present on every dog read.
   * Optional in the type only so an older cached response can't crash the
   * page; the server always sends it.
   */
  intake_completeness?: AdminDogIntakeCompleteness;
};

/**
 * #653 — "what is still missing from this pet's file?"
 *
 * NOT a review verdict. #556 owns the staff-reviewed half of the gate;
 * this answers data-completeness only, so a dog a receptionist typed in at
 * the counter is visibly incomplete rather than silently passing as done.
 */
export type AdminDogIntakeCompleteness = {
  is_complete: boolean;
  /** Stable keys: 'vet_contact' | 'vaccination_records'. */
  missing: string[];
  /** Plain sentence for the desk; null when nothing is missing. */
  summary: string | null;
};

export async function fetchCustomerDogs(
  apiUrl: string,
  token: string,
  customerId: string,
): Promise<{ dogs: AdminDog[] }> {
  return getJson(
    `${apiUrl}/api/admin/customers/${customerId}/dogs`,
    token,
  );
}

/**
 * #653 — the front-desk fields for adding a walk-in's dog.
 *
 * Deliberately a SUBSET of what the server accepts. The server validates
 * against the shared `createDogSchema` (every dog field, real per-field
 * rules); the desk form asks only for what a person can reasonably know
 * while a dog is standing in front of them. Widening this list is a
 * product decision, not a schema change — the endpoint already takes the
 * rest.
 *
 * `vetName` / `vetPhone` are OPTIONAL on purpose: vet info is optional at
 * registration everywhere (an owner may require it before a dog's first
 * ONLINE booking — SPEC 2026-10-26 Story 5), and a walk-in must never be
 * turned away over a phone number nobody has on them. The record comes
 * back flagged instead.
 */
export type CreateDogInput = {
  name: string;
  breed?: string | null;
  sizeCategory?: 'small' | 'medium' | 'large' | 'xl' | null;
  weight?: number | null;
  color?: string | null;
  birthDate?: string | null;
  /**
   * #1116 — REQUIRED by the server to create a dog (Ted: "Keep Spayed/
   * Neutered as a required Yes/No field"). Typed optional here only so an
   * older caller still compiles; the form never sends it empty.
   */
  alteredStatus?: 'intact' | 'spayed' | 'neutered' | null;
  /** #1116 — asked when the answer is No; a Yes implies it. */
  sex?: 'male' | 'female' | null;
  vetName?: string | null;
  vetPhone?: string | null;
  allergies?: string | null;
  specialNeeds?: string | null;
  careInstructions?: string | null;
  notes?: string | null;
};

/**
 * POST /api/admin/customers/:customerId/dogs — staff create (`pets:write:all`).
 *
 * Surfaces the SERVER's message on failure rather than a generic string:
 * the endpoint returns a plain sentence ("Name is required"), and
 * replacing it with "Failed to add pet" would throw away the only thing
 * that tells the receptionist what to fix.
 */
export async function createCustomerDog(
  apiUrl: string,
  token: string,
  customerId: string,
  body: CreateDogInput,
): Promise<{ dog: AdminDog }> {
  const res = await fetch(
    `${apiUrl}/api/admin/customers/${customerId}/dogs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Couldn’t add the pet (${res.status})`);
  }
  return (await res.json()) as { dog: AdminDog };
}

// ─── Run cards v2 (printable boarding day-grid, issue #549) ─────────────
//
// One tenant-scoped call returns every printable run card for a day — a
// card per (boarding booking present that day, dog). Replaces the old N+1
// customer+dogs assembly. Backed by GET /api/admin/run-cards.

export type RunCardApiMedication = {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  /** Sawyer 08 meds-feeding (A1) — the times staff set. Absent on an older server. */
  slots?: string[];
  instructions: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  // Sawyer 10 customer-booking-verify-policy (C6) — present (true) only when the lockbox list matches; an older server omits it.
  lockbox?: boolean;
};

export type RunCardApiCard = {
  card_id: string;
  booking_id: string;
  dog_id: string;
  dog_name: string;
  owner_name: string;
  owner_phone: string | null;
  photo_url: string | null;
  breed: string | null;
  birth_date: string | null;
  sex: string | null;
  altered_status: string | null;
  is_neutered: boolean;
  temperament: string | null;
  service_label: string;
  dog_index: number;
  dog_count: number;
  stay_start: string | null;
  stay_end: string | null;
  drop_off_time: string | null;
  pick_up_time: string | null;
  // Sawyer 06 beds-lockers-runcard (B5) — the printed night's bed and the
  // stay's locker. Absent on an older server ⇒ the card prints blank boxes.
  placement?: {
    bed: { name: string; size_class: string | null } | null;
    locker: string | null;
    locker_source: 'stay' | 'bed_default' | 'none';
  };
  feeding: {
    method: string | null;
    food_type: string | null;
    notes: string | null;
    /**
     * #716 — this stay's feeding instructions, captured at check-in. Wins
     * over method/food_type/notes on the printed card. Optional: a server
     * older than #716 omits it, and the card falls back to the profile
     * summary rather than rendering "undefined".
     */
    override?: string | null;
  };
  medications: RunCardApiMedication[];
  medication_notes: string | null;
  /**
   * #716 — what this dog arrived with, already parsed into items by the
   * server (snapshot-or-copied-forward). Optional for deploy-order safety:
   * frontends auto-deploy on merge, the SERVER does not, so this field can
   * legitimately be absent for a window. Absent/empty ⇒ the card prints the
   * blank-line stationery fallback, which is the pre-#716 behaviour.
   */
  belongings?: string[];
  /** ISO timestamp of the check-in confirmation, or null/absent. */
  care_verified_at?: string | null;
  /** Ted #28 (#1050) — the dog's + stay's flags on the printed card; absent on an older server. */
  flags?: DogFlag[];
  /** #597 / B7 — the dog's allergies (free text). Absent on an older server. */
  allergies?: string | null;
  /**
   * #597 / B7 — when the medication / feeding note on this card came from
   * the daily Gingr mirror, the ISO time of the last sync that read it
   * (null when a human typed it in ALA). Absent on an older server — the
   * frontend deploys on merge, the server does not.
   */
  gingr_care?: {
    medication_imported_at: string | null;
    feeding_imported_at: string | null;
  } | null;
  /**
   * Ted #46 (#1218) — every field below is optional for deploy-order safety
   * (frontends ship on merge, the server does not). `allergies` and
   * `medication_notes` above are EFFECTIVE values on a server that sends
   * these: this stay's edit when there is one, else the profile.
   */
  /** The dog's behavior notes (profile `social_notes`, or this stay's edit). */
  behavior_notes?: string | null;
  /** What the desk added to THIS stay's card. */
  stay_edits?: {
    note: string | null;
    fields: Array<{ label: string; value: string }>;
    /** Which care values came from this stay, not the profile. */
    overridden: Array<'allergies' | 'medications' | 'behavior'>;
    edited_at: string | null;
  };
  /** `visit_record` ⇒ this is the card as printed for this visit. */
  source?: 'live' | 'visit_record';
  printed_at?: string | null;
  /** `visit_record` only: today's profile would print something different. */
  changed_since_print?: boolean;
  /**
   * Sawyer 10-30 — false when this card's service skips the check-in prompt
   * (Settings → Operations). Absent on an older server: the prompt then asks,
   * which is what that server always did.
   */
  prompt_on_check_in?: boolean;
};

export type RunCardsResponse = {
  date: string;
  cards: RunCardApiCard[];
};

export async function fetchRunCards(
  apiUrl: string,
  token: string,
  date: string,
  opts: {
    readonly bookingIds?: ReadonlyArray<string>;
    /** Ted #46 — today's card even where the visit has a printed record. */
    readonly live?: boolean;
  } = {},
): Promise<RunCardsResponse> {
  const params = new URLSearchParams({ date });
  // Ted Item 34 — the check-in prompt names the bookings it just checked in.
  // A server older than Item 34 ignores the parameter and answers with the
  // day's boarding deck, so the caller must still filter by booking id.
  if (opts.bookingIds && opts.bookingIds.length > 0) {
    params.set('booking_ids', opts.bookingIds.join(','));
  }
  if (opts.live) params.set('live', '1');
  return getJson(`${apiUrl}/api/admin/run-cards?${params.toString()}`, token);
}

// ─── Run-card edits + the printed visit record (Ted #46, #1218) ──────────
//
// Edits are saved ON THE STAY (booking + dog). Allergy / feeding / meds /
// behavior edits can ALSO go to the dog's profile, opt-in per field. The
// card printed for a visit is kept as that visit's record.

export type RunCardEditsView = {
  dog_id: string;
  dog_name: string;
  note: string | null;
  fields: Array<{ label: string; value: string }>;
  /** This stay's own value per field; null ⇒ the card uses the profile. */
  stay: {
    allergies: string | null;
    feeding: string | null;
    medications: string | null;
    behavior: string | null;
  };
  /** The dog's profile values, so an edit can be told from what's on file. */
  profile: {
    allergies: string | null;
    feeding: string | null;
    feeding_method: string | null;
    food_type: string | null;
    medications: string | null;
    behavior: string | null;
  };
  edited_at: string | null;
  printed_at: string | null;
};

export type RunCardProfileField = 'allergies' | 'feeding' | 'medications' | 'behavior';

export type RunCardEditsInput = {
  note?: string | null;
  fields?: Array<{ label: string; value: string }>;
  allergies?: string | null;
  feeding?: string | null;
  medications?: string | null;
  behavior?: string | null;
  saveToProfile?: Partial<Record<RunCardProfileField, boolean>>;
};

export async function fetchRunCardEdits(
  apiUrl: string,
  token: string,
  bookingId: string,
  dogId: string,
): Promise<{ run_card_edits: RunCardEditsView }> {
  return getJson(
    `${apiUrl}/api/admin/bookings/${bookingId}/dogs/${dogId}/run-card-edits`,
    token,
  );
}

export async function saveRunCardEdits(
  apiUrl: string,
  token: string,
  bookingId: string,
  dogId: string,
  input: RunCardEditsInput,
): Promise<{ run_card_edits: RunCardEditsView; profile_updated: string[] }> {
  const res = await fetch(
    `${apiUrl}/api/admin/bookings/${bookingId}/dogs/${dogId}/run-card-edits`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Request failed (${res.status})`);
  }
  return (await res.json()) as {
    run_card_edits: RunCardEditsView;
    profile_updated: string[];
  };
}

/**
 * Tell the server these cards went to the printer, so each visit keeps the
 * card as printed. The server rebuilds and freezes each card itself (nothing
 * from the client is stored) and leaves an unchanged record alone, so it is
 * safe to send the whole deck. `refresh` ⇒ the deck was today's version
 * (`live`), recorded over an existing record.
 */
export async function recordRunCardsPrinted(
  apiUrl: string,
  token: string,
  cards: ReadonlyArray<{ readonly bookingId: string; readonly dogId: string }>,
  opts: { readonly refresh?: boolean } = {},
): Promise<void> {
  // The server takes at most 50 per request (one deck); a bigger day goes
  // in batches.
  for (let i = 0; i < cards.length; i += 50) {
    const batch = cards.slice(i, i + 50);
    const res = await fetch(`${apiUrl}/api/admin/run-cards/printed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        cards: batch.map((c) => ({ booking_id: c.bookingId, dog_id: c.dogId })),
        refresh: opts.refresh === true,
      }),
    });
    if (!res.ok) {
      throw await apiErrorFromResponse(res, `Request failed (${res.status})`);
    }
  }
}

// ─── Check-in care snapshot (#716) ───────────────────────────────────────
//
// Powers the check-in capture sheet. GET returns snapshot-or-defaults
// (copy-forward from the dog); PUT writes the per-stay snapshot and, unless
// `updateDefaults` is false, pushes changed values back onto the dog so the
// NEXT stay pre-fills.

export type CareSnapshot = {
  dog_id: string;
  dog_name: string;
  /** Effective text — what the sheet's textarea is seeded with. */
  belongings_text: string | null;
  /** Effective list — what the run card prints. */
  belongings: string[];
  /**
   * false ⇒ these values were COPIED FORWARD from the dog's profile and
   * nobody has confirmed them for THIS stay. The sheet says so out loud;
   * presenting copied-forward data as confirmed is the lie this whole
   * feature exists to avoid.
   */
  captured: boolean;
  feeding_override: string | null;
  care_verified_at: string | null;
  care_verified_by: string | null;
  defaults: {
    boarding_belongings: string | null;
    feeding_method: string | null;
    food_type: string | null;
    feeding_notes: string | null;
    medication_notes: string | null;
  };
};

export type CareSnapshotInput = {
  belongings?: string | null;
  feedingOverride?: string | null;
  feedingMethod?: string | null;
  foodType?: string | null;
  feedingNotes?: string | null;
  medicationNotes?: string | null;
  /** Default ON server-side — send false only for a genuinely one-off stay. */
  updateDefaults?: boolean;
};

export async function fetchCareSnapshot(
  apiUrl: string,
  token: string,
  bookingId: string,
  dogId: string,
): Promise<{ care_snapshot: CareSnapshot }> {
  return getJson(
    `${apiUrl}/api/admin/bookings/${bookingId}/dogs/${dogId}/care-snapshot`,
    token,
  );
}

export async function saveCareSnapshot(
  apiUrl: string,
  token: string,
  bookingId: string,
  dogId: string,
  input: CareSnapshotInput,
): Promise<{ care_snapshot: CareSnapshot; defaults_updated: boolean }> {
  const res = await fetch(
    `${apiUrl}/api/admin/bookings/${bookingId}/dogs/${dogId}/care-snapshot`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Request failed (${res.status})`);
  }
  return (await res.json()) as {
    care_snapshot: CareSnapshot;
    defaults_updated: boolean;
  };
}

// ─── Compliance roster (cross-customer vaccination alerts, issue #109) ──
//
// Powers the receptionist-facing `/compliance` page and the dashboard
// summary widget. Returns a flat row per (dog, vaccination) that needs
// attention so it sorts/paginates naturally; the page groups by dog
// client-side if needed. Backed by GET /api/admin/compliance/vaccinations.

export type ComplianceStatus = 'expired' | 'expiring' | 'unverified' | 'current';

export type ComplianceFilter = 'all' | 'expired' | 'expiring' | 'unverified';

export type ComplianceRow = {
  customer_id: string;
  customer_name: string;
  customer_email: string | null;
  dog_id: string;
  dog_name: string;
  vaccination_id: string;
  vaccine_name: string;
  expires_at: string | null;
  verified: boolean;
  status: ComplianceStatus;
  /** Sawyer 09 pet-inactivity (D1) — `YYYY-MM-DD` when an inactive dog is shown; absent on an older server. */
  dog_inactive_since?: string | null;
};

export type ComplianceCounts = {
  all: number;
  expired: number;
  expiring: number;
  unverified: number;
};

export type ComplianceResponse = {
  rows: ComplianceRow[];
  counts: ComplianceCounts;
  days: number;
  // Sawyer 09 pet-inactivity (D1) — all optional: absent on an older server.
  total?: number;
  truncated?: boolean;
  inactive_dogs_hidden?: number;
  /** null = the rule is off; absent = an older server. Either way no toggle. */
  inactivity_months?: number | null;
};

// Named `…Roster` (not `…Alerts`) to stay distinct from the dashboard's
// today-arrivals `fetchComplianceAlerts` in components/dashboard/dashboard-api.ts
// (a different endpoint), and to match the widget's `fetchComplianceSummary` —
// both hit /api/admin/compliance/vaccinations.
export async function fetchComplianceRoster(
  apiUrl: string,
  token: string,
  args: { status?: ComplianceFilter; days?: number; limit?: number; includeInactive?: boolean } = {},
): Promise<ComplianceResponse> {
  const params = new URLSearchParams();
  if (args.status) params.set('status', args.status);
  // Sawyer 09 pet-inactivity (D1) — an older server strips the unknown key.
  if (args.includeInactive) params.set('include_inactive', 'true');
  if (args.days !== undefined) params.set('days', String(args.days));
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  const qs = params.toString();
  return getJson(
    `${apiUrl}/api/admin/compliance/vaccinations${qs ? `?${qs}` : ''}`,
    token,
  );
}

// ── #551 — inbound vaccination-record verification queue ──────────────
//
// Everything on these types is STAGED, unverified data. `parseRawText`,
// `parseNotes`, `subject` and `fromEmail` are untrusted third-party text —
// render them as plain text only (React escapes by default; never
// dangerouslySetInnerHTML).

export type VaxIntakeStatus = 'pending' | 'approved' | 'rejected';
export type VaxIntakeMatchStatus = 'matched' | 'ambiguous' | 'unmatched';
/** A vaccine product term (#1104: any vaccine, not only rabies). */
export type VaxIntakeTerm = 'one_year' | 'three_year';
/** Pre-#1104 name. */
export type VaxIntakeRabiesTerm = VaxIntakeTerm;

/**
 * The customer fields are NULLABLE on the wire: the server nulls every
 * customer-identifying field for a caller without `customers:read:all`
 * (#537 narrowing, admin-vax-intake.ts serializeSubmission). No role that
 * can reach this page lacks that grant today, so in practice they are
 * populated — but the type states what the server may actually send, so a
 * future widening of the read gate surfaces here as a compile error rather
 * than as `undefined` rendered on screen.
 */
export type VaxIntakeCandidate = {
  dogId: string;
  dogName: string;
  customerId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  score: number;
  reason: string;
};

export type VaxIntakeRecord = {
  id: string;
  vaccineName: 'rabies' | 'bordetella' | 'dhpp';
  dateGiven: string | null;
  /**
   * RABIES ONLY (null on every other vaccine). null = the document did not
   * state 1-year vs 3-year. Never defaulted. Kept because a server older than
   * #1104 sends only this.
   */
  rabiesTerm: VaxIntakeRabiesTerm | null;
  /**
   * #1104 — the staged term for ANY vaccine: stated by the document, or
   * implied by the facility's fixed policy. Absent on a pre-#1104 server.
   */
  term?: VaxIntakeTerm | null;
  /**
   * #1104 — the facility's allowed term lengths for this vaccine, in years:
   * [] not calculated, [N] fixed (no question), [1, 3] the reviewer picks.
   * ABSENT on a pre-#1104 server — the page then falls back to exactly the
   * old behavior (a 1-year/3-year picker on rabies only). Frontends
   * auto-deploy before the server does, so the absent case is real.
   */
  termOptions?: number[];
  documentExpiresAt: string | null;
  derivedExpiresAt: string | null;
  /** What the form pre-fills: document expiry if printed, else derived. */
  proposedExpiresAt: string | null;
  expirySource: 'document' | 'derived' | 'unknown';
  needsHumanInput: boolean;
  flags: string[];
};

/** How a record reached the queue (#720). */
export type VaxIntakeSource = 'email' | 'customer_upload';

/** The matched dog's next assessment booking (#720). Derived per request. */
export type VaxIntakeUpcomingAssessment = {
  bookingId: string;
  /** YYYY-MM-DD in the facility's own timezone. */
  date: string;
  /** "HH:MM", or null when the service does not use one. */
  startTime: string | null;
};

export type VaxIntakeSubmission = {
  id: string;
  status: VaxIntakeStatus;
  /**
   * #720 — 'email' (a vet or owner mailed it in) or 'customer_upload' (the
   * owner uploaded it in the app). Drives what the card can honestly say:
   * an app upload has no sender address and no subject line.
   */
  source: VaxIntakeSource;
  receivedAt: string;
  /** Nulled for callers without customers:read:all — see VaxIntakeCandidate. */
  fromEmail: string | null;
  fromName: string | null;
  /** #720 — null on the customer_upload channel: there is no recipient. */
  toEmail: string | null;
  /** #720 — the authenticated uploader. Customer PII, so #537-narrowed. */
  submittedByCustomerId: string | null;
  /**
   * #720 — null unless the matched dog has an assessment booked. Kylie
   * pre-verifies vaccinations before every assessment, so this is the
   * deadline the card is racing.
   */
  upcomingAssessment: VaxIntakeUpcomingAssessment | null;
  subject: string | null;
  parseStatus: string;
  parseError: string | null;
  parseConfidence: number | null;
  parseModel: string | null;
  parseNotes: string | null;
  parseRawText: string | null;
  matchStatus: VaxIntakeMatchStatus;
  matchReason: string | null;
  matchedDogId: string | null;
  matchedCustomerId: string | null;
  candidates: VaxIntakeCandidate[];
  attachments: Array<{
    id: string;
    filename: string | null;
    contentType: string | null;
    size: number;
  }>;
  /**
   * #752 — the originals staff can open. OPTIONAL on purpose: the admin app
   * auto-deploys on merge and the server does not, so a pre-#752 server
   * omits the field and the card simply offers no "view" button.
   */
  documents?: VaxDocumentMeta[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdVaccinationIds: string[];
  records: VaxIntakeRecord[];
};

export type VaxIntakeQueueResponse = {
  rows: VaxIntakeSubmission[];
  counts: { pending: number; approved: number; rejected: number };
};

export type VaxIntakeConfig = {
  /**
   * True once ANY channel can put records in this facility's queue.
   *
   * #551 read this as "the inbound mailbox is provisioned". #720 added a
   * second channel that needs no ops work, so it is now the OR of
   * `channels` — otherwise an owner could upload a record into a queue no
   * staff member could open.
   */
  enabled: boolean;
  /** e.g. `hthd@vax.animalloversapp.com`. Null when the mailbox is unprovisioned. */
  inboundAddress: string | null;
  /**
   * Which ways in are live here.
   *
   * `customerUpload` is a SERVER-CAPABILITY probe, not a setting: only a
   * server with #720's upload route deployed sends this field at all. A
   * pre-#720 server omits it, it reads false, and the surface stays gated
   * on the mailbox exactly as before — which is what stops the
   * auto-deployed admin frontend from opening a page the
   * manually-deployed server cannot yet serve.
   */
  channels: { email: boolean; customerUpload: boolean };
};

const VAX_INTAKE_DISABLED: VaxIntakeConfig = {
  enabled: false,
  inboundAddress: null,
  channels: { email: false, customerUpload: false },
};

/**
 * Probe whether this facility's vaccination mailbox exists yet. Drives the
 * nav item and the route — the surface stays hidden until the server says
 * the mailbox is live (see route-permissions.ts `FeatureKey`).
 *
 * Deliberately does NOT go through `getJson`: that fires the global
 * onUnauthorized auto-logout on any 401, and this runs on every shell
 * mount for every role. A nav-availability probe must never be able to
 * sign a working user out. Every failure — network, 401, 403, 503,
 * malformed body — resolves to "not enabled", which is the fail-closed
 * direction: at worst a provisioned facility briefly hides a nav item.
 */
export async function fetchVaxIntakeConfig(
  apiUrl: string,
  token: string,
): Promise<VaxIntakeConfig> {
  try {
    const res = await fetch(`${apiUrl}/api/admin/vax-intake/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return VAX_INTAKE_DISABLED;
    const body = (await res.json()) as Partial<VaxIntakeConfig> | null;
    if (body?.enabled !== true) return VAX_INTAKE_DISABLED;
    const inboundAddress =
      typeof body.inboundAddress === 'string' && body.inboundAddress.length > 0
        ? body.inboundAddress
        : null;
    return {
      enabled: true,
      inboundAddress,
      channels: {
        // Derived from the address, not from a second flag that could
        // disagree with it: the empty state can only teach the email
        // workflow when it can name the mailbox.
        email: body.channels?.email === true && inboundAddress !== null,
        // Absent on a pre-#720 server ⇒ false. See the type's comment.
        customerUpload: body.channels?.customerUpload === true,
      },
    };
  } catch {
    return VAX_INTAKE_DISABLED;
  }
}

export async function fetchVaxIntakeQueue(
  apiUrl: string,
  token: string,
  args: { status?: VaxIntakeStatus | 'all'; limit?: number } = {},
): Promise<VaxIntakeQueueResponse> {
  const params = new URLSearchParams();
  if (args.status) params.set('status', args.status);
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  const qs = params.toString();
  return getJson(`${apiUrl}/api/admin/vax-intake${qs ? `?${qs}` : ''}`, token);
}

// ── #752 — the original vaccination document ─────────────────────────

/** Metadata for a stored original. The bytes come from `fetchVaxDocument`. */
export type VaxDocumentMeta = {
  id: string;
  /** Owner-typed; null when the server narrowed it (#537). */
  filename: string | null;
  contentType: string;
  size: number;
  /**
   * #1182 — the document was deleted when its submission was rejected.
   * Opening it answers 410; render the sentence, not a button. Optional so
   * a pre-#1182 server (deploy skew: frontends ship first) reads as false.
   */
  deleted?: boolean;
};

/**
 * One submission as the dog profile sees it. No staged values — the parser's
 * draft never reaches the dog page before a human approves it.
 */
export type DogVaxRecord = {
  id: string;
  status: 'pending' | 'approved';
  source: VaxIntakeSource;
  receivedAt: string;
  reviewedAt: string | null;
  parseStatus: string;
  /** Vaccinations this record produced on approval — links a row to its source. */
  createdVaccinationIds: string[];
  documents: VaxDocumentMeta[];
};

export type DogVaxRecordsResult =
  | { kind: 'ok'; records: DogVaxRecord[] }
  /** Pre-#752 server, or a role without the grant: render nothing. */
  | { kind: 'unavailable' }
  | { kind: 'error' };

/**
 * A dog's uploaded vaccination records, for the profile's health section.
 *
 * Raw `fetch`, not `getJson`: this is a secondary panel on a page that has
 * already loaded, and a 401/403 here must never trigger the global
 * sign-out. A 403 or 404 means "this server or this role can't serve it"
 * (see reference: an absent admin route answers 403), so the panel hides.
 */
export async function fetchDogVaxRecords(
  apiUrl: string,
  token: string,
  dogId: string,
): Promise<DogVaxRecordsResult> {
  try {
    const res = await fetch(
      `${apiUrl}/api/admin/vax-intake/by-dog/${encodeURIComponent(dogId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      return { kind: 'unavailable' };
    }
    if (!res.ok) return { kind: 'error' };
    const body = (await res.json()) as { dataStatus?: string; records?: DogVaxRecord[] };
    if (body.dataStatus !== 'ok' || !Array.isArray(body.records)) {
      return { kind: 'unavailable' };
    }
    return { kind: 'ok', records: body.records };
  } catch {
    return { kind: 'error' };
  }
}

/**
 * Where the viewer gets the original from (#1131). Two shapes, because the
 * server has two homes for the bytes:
 *
 *   link — the record lives in private object storage. The server answered
 *          `{url, expiresAt, contentType}`: a 5-minute presigned URL the
 *          browser can load directly as an `<img src>` / `<iframe src>`. No
 *          Bearer token, no blob, no CORS — the signature is the credential.
 *   blob — the record lives in Postgres (every pre-#1131 upload), or the
 *          server predates #1131. The bytes came back; the viewer turns them
 *          into an object URL exactly as #752 did.
 */
export type VaxDocumentSource =
  | { kind: 'link'; url: string; expiresAt: string; contentType: string }
  | { kind: 'blob'; blob: Blob };

function isPresignedLink(
  body: unknown,
): body is { url: string; expiresAt: string; contentType: string } {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  if (typeof b.url !== 'string') return false;
  // Only an http(s) URL may become an src/href — never `javascript:` or
  // `data:`, whatever a response claims.
  try {
    const u = new URL(b.url);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
  } catch {
    return false;
  }
  return typeof b.expiresAt === 'string' && typeof b.contentType === 'string';
}

/**
 * The original document. Bearer-authenticated, so it cannot be a plain
 * `<img src>` to the API.
 *
 * Asks for JSON FIRST (`Accept: application/json, *\/*`): a #1131 server
 * answers an object-store record with a presigned link. Anything that is
 * not a JSON link — a legacy Postgres record, or a pre-#1131 server, which
 * ignores Accept and streams bytes — takes the #752 blob path unchanged.
 * That ordering is what makes the frontend safe to deploy BEFORE the server.
 */
export async function fetchVaxDocument(
  apiUrl: string,
  token: string,
  submissionId: string,
  documentId: string,
): Promise<VaxDocumentSource> {
  const res = await fetch(
    `${apiUrl}/api/admin/vax-intake/${encodeURIComponent(submissionId)}/documents/${encodeURIComponent(documentId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, */*',
      },
    },
  );
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? 'This record is no longer on file.'
        : res.status === 503
          ? "This record can't be opened right now. Try again in a few minutes."
          : `Could not open the record (${res.status}).`,
    );
  }
  const type = res.headers.get('content-type') ?? '';
  if (type.toLowerCase().includes('application/json')) {
    const body: unknown = await res.json().catch(() => null);
    if (isPresignedLink(body)) {
      return { kind: 'link', url: body.url, expiresAt: body.expiresAt, contentType: body.contentType };
    }
    throw new Error('Could not open the record.');
  }
  return { kind: 'blob', blob: await res.blob() };
}

/**
 * Approve a staged submission. The values sent here are what get written —
 * the server does NOT substitute the staged values, so a correction the
 * reviewer makes on screen is the correction that lands on the dog.
 *
 * A rabies record without a term is rejected by the server (400
 * `rabies_term_required`) rather than defaulted to either term; since #1104
 * the same holds for any vaccine the facility asks a term for (400
 * `term_required`). `term` is the #1104 field; `rabiesTerm` is still sent on
 * rabies lines so a pre-#1104 server reads the same answer.
 */
export async function approveVaxIntake(
  apiUrl: string,
  token: string,
  submissionId: string,
  body: {
    dogId: string;
    records: Array<{
      vaccineName: 'rabies' | 'bordetella' | 'dhpp';
      dateGiven: string;
      expiresAt: string;
      term?: VaxIntakeTerm | null;
      rabiesTerm?: VaxIntakeRabiesTerm | null;
      notes?: string;
    }>;
    reviewNotes?: string;
  },
): Promise<{
  ok: true;
  status: 'approved';
  dogId: string;
  createdVaccinationIds: string[];
  reviewedBy: string;
  reviewedAt: string;
}> {
  return postJsonWithAuth(
    `${apiUrl}/api/admin/vax-intake/${submissionId}/approve`,
    token,
    body,
  );
}

/**
 * #720 — pending checks standing between a dog and an upcoming assessment.
 *
 * A strict subset of the queue, factored as its own endpoint so the
 * assessment surface (#719's /today panel) can render the flag without
 * re-deriving the join. `dataStatus` distinguishes "checked, nothing
 * pending" from "never asked", so a consumer can render an honest empty
 * state rather than a fabricated zero.
 */
export type VaxAssessmentAlert = {
  submissionId: string;
  dogId: string;
  dogName: string | null;
  source: VaxIntakeSource;
  receivedAt: string;
  upcomingAssessment: VaxIntakeUpcomingAssessment;
  vaccineNames: string[];
  /** Null for callers without customers:read:all (#537). */
  customerName: string | null;
};

export type VaxAssessmentAlertsResponse = {
  dataStatus: 'ok';
  alerts: VaxAssessmentAlert[];
};

export async function fetchVaxAssessmentAlerts(
  apiUrl: string,
  token: string,
): Promise<VaxAssessmentAlertsResponse> {
  return getJson(`${apiUrl}/api/admin/vax-intake/assessment-alerts`, token);
}

/** Reject. Leaves the dog's authoritative vaccination record untouched. */
export async function rejectVaxIntake(
  apiUrl: string,
  token: string,
  submissionId: string,
  body: { reviewNotes?: string } = {},
): Promise<{ ok: true; status: 'rejected'; reviewedBy: string; reviewedAt: string }> {
  return postJsonWithAuth(
    `${apiUrl}/api/admin/vax-intake/${submissionId}/reject`,
    token,
    body,
  );
}

async function postJsonWithAuth<T>(
  url: string,
  token: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      message = data.error ?? data.message ?? message;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export async function addDogBehaviorNote(
  apiUrl: string,
  token: string,
  dogId: string,
  body: { note: string; type: DogBehaviorNoteType; audience: DogNoteAudience },
): Promise<{ behaviorNote: AdminDogBehaviorNote }> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${dogId}/behavior-note`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to add behavior note (${res.status})`);
  }
  return (await res.json()) as { behaviorNote: AdminDogBehaviorNote };
}

/**
 * Staff edit of a dog's care instructions. Pass `null` to clear the field.
 * Tenant scoping is server-side (the updateMany's own WHERE); a dog outside
 * the caller's tenant answers 404, never a leak.
 */
export async function updateDogCareInstructions(
  apiUrl: string,
  token: string,
  dogId: string,
  careInstructions: string | null,
): Promise<{ careInstructions: string | null }> {
  const res = await fetch(
    `${apiUrl}/api/admin/dogs/${dogId}/care-instructions`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ careInstructions }),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to save care instructions (${res.status})`);
  }
  return (await res.json()) as { careInstructions: string | null };
}

/**
 * SPEC 2026-10-26 (Stories 5 and 6) — the desk edits a dog's vet contacts:
 * the regular vet and the second practice for when it is closed.
 * PATCH /api/admin/dogs/:dogId/vet (`pets:write:all`, the desk trio). Every
 * key optional: send only what changed; '' clears a field; an absent key is
 * left alone. Answers the eight fields as stored.
 *
 * Returns `request`'s `{ data } | { error, code, details, reason }` rather
 * than throwing, so the form can say an older server's 403
 * `route_not_mapped` as "available after the next server update" and show
 * the server's own validation sentence.
 */
export interface DogVetContacts {
  vetName: string | null;
  vetPhone: string | null;
  vetAddress: string | null;
  vetEmail: string | null;
  secondaryVetName: string | null;
  secondaryVetPhone: string | null;
  secondaryVetAddress: string | null;
  secondaryVetEmail: string | null;
}
export type DogVetContactsPatch = Partial<Record<keyof DogVetContacts, string>>;

export function updateDogVetContacts(
  apiUrl: string,
  dogId: string,
  body: DogVetContactsPatch,
): Promise<ApiResponse<DogVetContacts>> {
  return api.patch<DogVetContacts>(apiUrl, `/api/admin/dogs/${encodeURIComponent(dogId)}/vet`, body);
}

/**
 * #1116 AC5 — the Do Not Admit switch. PATCH /api/admin/dogs/:dogId/do-not-admit
 * (`pets:write:all`). Setting needs a reason; the server refuses without one
 * and its sentence is what the form shows. Clearing may carry a note.
 */
export async function updateDogDoNotAdmit(
  apiUrl: string,
  token: string,
  dogId: string,
  body: { active: boolean; reason: string | null },
): Promise<{ isBanned: boolean; bannedReason: string | null }> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${encodeURIComponent(dogId)}/do-not-admit`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Couldn’t save the Do Not Admit decision (${res.status})`);
  }
  return (await res.json()) as { isBanned: boolean; bannedReason: string | null };
}

/**
 * #1116 AC2 — record the spay/neuter answer at the desk.
 * PATCH /api/admin/dogs/:dogId/spay-neuter (`pets:write:all`).
 */
export async function updateDogSpayNeuter(
  apiUrl: string,
  token: string,
  dogId: string,
  body: { alteredStatus: 'intact' | 'spayed' | 'neutered'; sex?: 'male' | 'female' | null },
): Promise<{ alteredStatus: string | null; sex: 'male' | 'female' | null }> {
  const res = await fetch(`${apiUrl}/api/admin/dogs/${encodeURIComponent(dogId)}/spay-neuter`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Couldn’t save the spay/neuter answer (${res.status})`);
  }
  return (await res.json()) as { alteredStatus: string | null; sex: 'male' | 'female' | null };
}

// ─── Ted #32a (E1.6, #1156) — staff vaccination records ─────────────────
//
// The desk's add / correct / verify / delete and the certificate, on the Pet
// Profile. All under /api/admin/dogs/:dogId/vaccinations
// (`vaccinations:write:all` for writes, `pets:read:all` to open a
// certificate). Each throws an ApiError carrying the server's sentence; the
// caller re-reads the profile on success so what renders is what saved.

/** The record as the desk enters it. Dates are `YYYY-MM-DD` calendar days. */
export type DogVaccinationInput = {
  name: string;
  dateGiven: string;
  expiresAt: string;
  /** "I checked these dates against the certificate." */
  verified: boolean;
};

function vaccinationsUrl(apiUrl: string, dogId: string, vaccinationId?: string, tail = ''): string {
  const base = `${apiUrl}/api/admin/dogs/${encodeURIComponent(dogId)}/vaccinations`;
  return vaccinationId ? `${base}/${encodeURIComponent(vaccinationId)}${tail}` : base;
}

async function vaccinationWrite(
  url: string,
  token: string,
  init: { method: string; body?: unknown },
  fallback: string,
): Promise<Response> {
  const res = await fetch(url, {
    method: init.method,
    headers: {
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!res.ok) throw await vaccinationError(res, fallback);
  return res;
}

/**
 * The server's sentence as an ApiError. A 403 `route_not_mapped` means this
 * server predates the route (frontends deploy before the server), so it is
 * said in the desk's words rather than as a permissions problem.
 */
async function vaccinationError(res: Response, fallback: string): Promise<ApiError> {
  if (res.status === 403) {
    const body = (await res.clone().json().catch(() => null)) as { reason?: string } | null;
    if (body?.reason === 'route_not_mapped') {
      return new ApiError(403, 'Editing vaccination records isn’t available yet. Try again after the next update.');
    }
  }
  return apiErrorFromResponse(res, `${fallback} (${res.status})`);
}

/** Returns the new record's id, so the page can open it to attach the certificate. */
export async function addDogVaccination(
  apiUrl: string,
  token: string,
  dogId: string,
  body: DogVaccinationInput,
): Promise<{ id: string }> {
  const res = await vaccinationWrite(
    vaccinationsUrl(apiUrl, dogId),
    token,
    { method: 'POST', body },
    'Couldn’t add the vaccination',
  );
  const json = (await res.json().catch(() => null)) as { vaccination?: { id?: unknown } } | null;
  const id = json?.vaccination?.id;
  if (typeof id !== 'string') throw new ApiError(500, 'The vaccination was saved, but the page could not read it back. Reload to see it.');
  return { id };
}

export async function updateDogVaccination(
  apiUrl: string,
  token: string,
  dogId: string,
  vaccinationId: string,
  body: DogVaccinationInput,
): Promise<void> {
  await vaccinationWrite(
    vaccinationsUrl(apiUrl, dogId, vaccinationId),
    token,
    { method: 'PATCH', body },
    'Couldn’t save the vaccination',
  );
}

export async function verifyDogVaccination(
  apiUrl: string,
  token: string,
  dogId: string,
  vaccinationId: string,
): Promise<void> {
  await vaccinationWrite(
    vaccinationsUrl(apiUrl, dogId, vaccinationId, '/verify'),
    token,
    { method: 'POST' },
    'Couldn’t mark the vaccination verified',
  );
}

export async function deleteDogVaccination(
  apiUrl: string,
  token: string,
  dogId: string,
  vaccinationId: string,
): Promise<void> {
  await vaccinationWrite(
    vaccinationsUrl(apiUrl, dogId, vaccinationId),
    token,
    { method: 'DELETE' },
    'Couldn’t delete the vaccination',
  );
}

/**
 * #1213 — what reading a certificate offers the add form. `unreadable`
 * covers every way the read can fail: no reader configured, a reader error,
 * a refused file, a server that predates the route (frontends deploy before
 * the server), or the network. The form treats it the same way each time:
 * the document stays beside an empty form and the desk types it.
 */
export type VaccinationCertificateRead =
  | {
      kind: 'found';
      vaccines: Array<{ name: string; dateGiven: string | null; expiresAt: string | null }>;
      /** The reader was unsure; the form asks for a closer check. */
      lowConfidence: boolean;
    }
  | { kind: 'nothing_found' }
  | { kind: 'unreadable'; message: string };

const isoDayOrNull = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable();
const certificateReadSchema = z.object({
  read: z.discriminatedUnion('outcome', [
    z.object({
      outcome: z.literal('found'),
      vaccines: z
        .array(z.object({ name: z.string().min(1), dateGiven: isoDayOrNull, expiresAt: isoDayOrNull }))
        .min(1),
      lowConfidence: z.boolean(),
    }),
    z.object({ outcome: z.literal('nothing_found') }),
  ]),
});

const CERTIFICATE_UNREADABLE = "We couldn't read this document automatically. Type the details from it.";

/**
 * Read an uploaded certificate for the add form. Writes nothing on the
 * server. Never throws: any failure is `unreadable`, so the desk is never
 * stuck behind the reader.
 */
export async function readDogVaccinationCertificate(
  apiUrl: string,
  token: string,
  dogId: string,
  file: File,
): Promise<VaccinationCertificateRead> {
  const form = new FormData();
  form.append('document', file);
  try {
    const res = await fetch(vaccinationsUrl(apiUrl, dogId, 'parse'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (res.status === 401) {
      // Through the choke point, so an expired session signs out as it does
      // everywhere else (#711).
      await apiErrorFromResponse(res, CERTIFICATE_UNREADABLE);
      return { kind: 'unreadable', message: CERTIFICATE_UNREADABLE };
    }
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      // A refused file says why (its type, its size); everything else is the
      // one sentence, whatever the technology behind it.
      const sentence =
        (res.status === 413 || res.status === 415) &&
        body !== null &&
        typeof body === 'object' &&
        typeof (body as { error?: unknown }).error === 'string'
          ? (body as { error: string }).error
          : CERTIFICATE_UNREADABLE;
      return { kind: 'unreadable', message: sentence };
    }
    const parsed = certificateReadSchema.safeParse(body);
    if (!parsed.success) return { kind: 'unreadable', message: CERTIFICATE_UNREADABLE };
    const read = parsed.data.read;
    return read.outcome === 'found'
      ? { kind: 'found', vaccines: read.vaccines, lowConfidence: read.lowConfidence }
      : { kind: 'nothing_found' };
  } catch {
    return { kind: 'unreadable', message: CERTIFICATE_UNREADABLE };
  }
}

/**
 * Attach (or replace) the certificate. Multipart, so it sets only the
 * Authorization header and lets the browser write the boundary.
 */
export async function uploadDogVaccinationDocument(
  apiUrl: string,
  token: string,
  dogId: string,
  vaccinationId: string,
  file: File,
): Promise<void> {
  const form = new FormData();
  form.append('document', file);
  const res = await fetch(vaccinationsUrl(apiUrl, dogId, vaccinationId, '/document'), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) throw await vaccinationError(res, 'Couldn’t attach the certificate');
}

/**
 * Open the attached certificate: the server answers a 5-minute presigned
 * link (JSON). Only an http(s) link is ever handed to the viewer.
 */
export async function fetchDogVaccinationDocument(
  apiUrl: string,
  token: string,
  dogId: string,
  vaccinationId: string,
): Promise<VaxDocumentSource> {
  const res = await fetch(vaccinationsUrl(apiUrl, dogId, vaccinationId, '/document'), {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? 'This certificate is no longer on file.'
        : res.status === 503
          ? "This certificate can't be opened right now. Try again in a few minutes."
          : `Could not open the certificate (${res.status}).`,
    );
  }
  const body: unknown = await res.json().catch(() => null);
  if (isPresignedLink(body)) {
    return { kind: 'link', url: body.url, expiresAt: body.expiresAt, contentType: body.contentType };
  }
  throw new Error('Could not open the certificate.');
}

// ─── Report cards ───────────────────────────────────────────────────────

export type ReportCardMood = 'happy' | 'calm' | 'anxious' | 'playful' | 'tired';

export type ReportCard = {
  id: string;
  booking_id: string;
  dog_id: string;
  staff_user_id: string;
  notes: string;
  mood: ReportCardMood | null;
  rating: number | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
};

export async function fetchReportCards(
  apiUrl: string,
  token: string,
  args: { date?: string; limit?: number } = {},
): Promise<{ reportCards: ReportCard[] }> {
  const params = new URLSearchParams();
  if (args.date) params.set('date', args.date);
  params.set('limit', String(args.limit ?? 50));
  return getJson(`${apiUrl}/api/admin/report-cards?${params.toString()}`, token);
}

export async function createReportCard(
  apiUrl: string,
  token: string,
  body: {
    bookingId: string;
    dogId: string;
    notes: string;
    mood?: ReportCardMood;
    rating?: number;
  },
): Promise<{ reportCard: ReportCard }> {
  const res = await fetch(`${apiUrl}/api/admin/report-cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to create report card (${res.status})`);
  }
  return (await res.json()) as { reportCard: ReportCard };
}

// ─── Incidents (issue #139) ──────────────────────────────────────────────
// Server: /api/v2/admin/incidents (incidentReportingModule). Owner-only
// fields (owner_notes/resolution/…) are present ONLY when the caller holds
// incidents.owner-only:write:all — the server strips them otherwise, so they
// are optional on IncidentSummary.

export const INCIDENT_KINDS = [
  'bite',
  'injury',
  'escape',
  'illness',
  'altercation',
  'property_damage',
  'in_heat',
  'other',
] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];

export const INCIDENT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_STATUSES = [
  'open',
  'in_review',
  'resolved',
  'dismissed',
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export type IncidentSummary = {
  id: string;
  dog_id: string;
  dog_name: string | null;
  customer_id: string;
  customer_name: string | null;
  reported_by: string | null;
  reporter_name: string | null;
  kind: string;
  severity: IncidentSeverity;
  narrative: string;
  photo_urls: string[];
  occurred_at: string;
  status: IncidentStatus;
  customer_notified: boolean;
  created_at: string;
  updated_at: string;
  // Owner-only (present only for owner-tier callers)
  owner_notes?: string | null;
  resolution?: string | null;
  resolved_by?: string | null;
  resolver_name?: string | null;
  resolved_at?: string | null;
};

export async function fetchIncidents(
  apiUrl: string,
  token: string,
  args: {
    severity?: IncidentSeverity;
    status?: IncidentStatus;
    dogId?: string;
    limit?: number;
  } = {},
): Promise<{ incidents: IncidentSummary[] }> {
  const params = new URLSearchParams();
  if (args.severity) params.set('severity', args.severity);
  if (args.status) params.set('status', args.status);
  if (args.dogId) params.set('dogId', args.dogId);
  params.set('limit', String(args.limit ?? 50));
  return getJson(
    `${apiUrl}/api/v2/admin/incidents?${params.toString()}`,
    token,
  );
}

export async function createIncident(
  apiUrl: string,
  token: string,
  body: {
    dogId: string;
    kind: IncidentKind;
    severity: IncidentSeverity;
    narrative: string;
    occurredAt?: string;
    photoUrls?: string[];
    ownerNotes?: string;
  },
): Promise<{ incident: IncidentSummary }> {
  const res = await fetch(`${apiUrl}/api/v2/admin/incidents`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to file incident (${res.status})`);
  }
  return (await res.json()) as { incident: IncidentSummary };
}

export async function fetchIncident(
  apiUrl: string,
  token: string,
  id: string,
): Promise<{ incident: IncidentSummary }> {
  return getJson(`${apiUrl}/api/v2/admin/incidents/${id}`, token);
}

// PATCH triage/resolve — owner-only (server-gated on incidents.owner-only:write:all).
export async function resolveIncident(
  apiUrl: string,
  token: string,
  id: string,
  patch: {
    status?: IncidentStatus;
    resolution?: string;
    ownerNotes?: string;
    customerNotified?: boolean;
  },
): Promise<{ incident: IncidentSummary }> {
  const res = await fetch(`${apiUrl}/api/v2/admin/incidents/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to update incident (${res.status})`);
  }
  return (await res.json()) as { incident: IncidentSummary };
}

// ─── Staff schedule ─────────────────────────────────────────────────────

/**
 * JOB FUNCTION — what someone is DOING on a shift, not how they log in.
 *
 * Mirrors SHIFT_ROLES in packages/core/modules/staff-scheduling/schema.ts,
 * which is the zod gate the server enforces on POST/PATCH — if these two lists
 * drift, the dropdown offers a value the server rejects. `receptionist` and
 * `walker` are new in #427 W2.
 *
 * This is NOT the RBAC login role. Scheduling Jorge (login `groomer`) as
 * `receptionist` for a day changes what he DOES, never what he can ACCESS.
 */
export const STAFF_ROLES = [
  'general',
  'manager',
  'receptionist',
  'groomer',
  'walker',
  'kennel_tech',
  // #427 W3.1 / #665 PR-B — `overnight` has been in the server's SHIFT_ROLES
  // since the payroll premium work (it is a durable manager-set marker the
  // payroll engine attaches the flat overnight premium to). The client list
  // had drifted BEHIND the server: a manager could not schedule an overnight
  // shift from this UI at all. This is the drift the comment above warns
  // about, in the direction nobody checked.
  'overnight',
] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** Human-facing labels — users never see raw enum strings (Design Philosophy
 *  anti-pattern: "internal identifiers shown raw to users"). */
export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  general: 'General',
  manager: 'Manager',
  receptionist: 'Front desk',
  groomer: 'Grooming',
  walker: 'Walking',
  kennel_tech: 'Kennel',
  overnight: 'Overnight',
};

export type StaffShift = {
  id: string;
  staff_user_id: string;
  staff_name: string;
  role: StaffRole | string;
  date: string;
  start_time: string;
  end_time: string;
  notes?: string | null;
  /** #665 P1 — NULL = draft (manager noodling, invisible to non-writers).
   *  Absent on rows serialized before the lifecycle shipped: treat a
   *  MISSING field as published (legacy row), never as a draft. */
  published_at?: string | null;
};

// ─── Time off (#427 W2) ─────────────────────────────────────────────────

export const TIME_OFF_TYPES = ['unpaid', 'pto', 'sick'] as const;
export type TimeOffType = (typeof TIME_OFF_TYPES)[number];

export const TIME_OFF_TYPE_LABELS: Record<TimeOffType, string> = {
  unpaid: 'Unpaid',
  pto: 'Paid time off',
  sick: 'Sick',
};

export type TimeOffStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export type TimeOffRequest = {
  id: string;
  staff_user_id: string;
  staff_name: string;
  start_date: string;
  end_date: string;
  type: TimeOffType | string;
  note: string | null;
  status: TimeOffStatus | string;
  reviewed_by_id: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
};

/** A 409 the caller can RECOVER from, rather than a dead end. The server hands
 *  back which shift you collided with, or which time-off request is in the way
 *  plus the field that overrides it. */
export type ShiftConflict =
  | { kind: 'overlapping_shift'; message: string; conflictingShiftId: string }
  | { kind: 'time_off_conflict'; message: string; timeOffRequestId: string };

export async function fetchStaffWeek(
  apiUrl: string,
  token: string,
  startDate: string,
  /** #665 PR-B — layer-chip lens: server-side filter on the shift's JOB
   *  FUNCTION (never the RBAC login role). Absent = all lanes. */
  jobFunction?: StaffRole,
  /** #665 PR-C — `self: true` sends `?self=1`: the server narrows to the
   *  VERIFIED JWT subject's own rows (any caller). /my-schedule sends it —
   *  with the floor roles on :read:all (D1), a plain week read there would
   *  silently become "everyone's schedule". Server-side by design: an
   *  own-week contract enforced by client filtering would be a trust-the-
   *  client contract. */
  self?: boolean,
): Promise<{ shifts: StaffShift[] }> {
  const params = new URLSearchParams({ startDate });
  if (jobFunction) params.set('jobFunction', jobFunction);
  if (self) params.set('self', '1');
  // The server returns a DAY-KEYED grid `{ week: { '<iso>': StaffShift[] } }`
  // (staff-scheduling/routes.ts GET /week), NOT a flat `{ shifts }`. Reading
  // `data.shifts` off that response is `undefined` → the Schedule "Week" view
  // crashed on every load (and, since "Add shift" lives only in Week view,
  // all staff-shift creation was unreachable). Flatten here so callers keep
  // the flat `{ shifts }` contract they already consume.
  const data = await getJson<{ week?: Record<string, StaffShift[]> }>(
    `${apiUrl}/api/admin/staff-schedules/week?${params.toString()}`,
    token,
  );
  return { shifts: Object.values(data.week ?? {}).flat() };
}

export async function createStaffShift(
  apiUrl: string,
  token: string,
  body: {
    staffUserId: string;
    date: string;
    startTime: string;
    endTime: string;
    role?: StaffRole;
    /** #427 W2 — knowingly schedule over APPROVED time off. */
    override?: boolean;
    /** #665 P1 — the server DEFAULTS this to true (back-compat with the
     *  pre-grid UI). The new grid UI sends the explicit `false`: creates are
     *  drafts until the manager hits Publish. */
    publishImmediately?: boolean;
  },
): Promise<{ shift: StaffShift }> {
  const res = await fetch(`${apiUrl}/api/admin/staff-schedules`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const fallback = `Failed to create shift (${res.status})`;
    // #427 W2 — a 409 here is a DECISION POINT, not a failure. Flattening it
    // into a message string would strip the ids the UI needs to point at the
    // conflicting shift, or to offer the manager the override. Design
    // Philosophy P2: errors say how to recover; dead ends are defects.
    if (res.status === 409) {
      let data:
        | {
            error?: string;
            message?: string;
            conflicting_shift_id?: string;
            time_off_request_id?: string;
          }
        | undefined;
      try {
        data = (await res.json()) as typeof data;
      } catch {
        // body not JSON — fall through to the sanitized generic error
      }
      if (data?.conflicting_shift_id) {
        throw new ShiftConflictError({
          kind: 'overlapping_shift',
          message: data.message ?? 'That overlaps an existing shift.',
          conflictingShiftId: data.conflicting_shift_id,
        });
      }
      if (data?.time_off_request_id) {
        throw new ShiftConflictError({
          kind: 'time_off_conflict',
          message: data.message ?? 'This person has approved time off.',
          timeOffRequestId: data.time_off_request_id,
        });
      }
      const parsed = parseErrorBody(res.status, data, {
        fallback,
        preferBodyMessage: true, // parity with the pre-#711 message-first read
      });
      throw new ApiError(res.status, parsed.message, parsed.code);
    }
    throw await apiErrorFromResponse(res, fallback);
  }
  return (await res.json()) as { shift: StaffShift };
}

/** Carries the recovery affordance with the failure. */
export class ShiftConflictError extends Error {
  readonly conflict: ShiftConflict;
  constructor(conflict: ShiftConflict) {
    super(conflict.message);
    this.name = 'ShiftConflictError';
    this.conflict = conflict;
  }
}

/**
 * #665 PR-B — edit an existing shift (times, job function, notes, PERSON).
 *
 * Send ONLY the fields that actually changed. Not load-bearing for
 * correctness — the server ignores an unchanged `staffUserId` (it only
 * treats the field as a RE-ASSIGNMENT when it differs from the current
 * assignee, and only then runs the time-off check against the new person).
 * Sending only dirty fields keeps the reassignment semantics explicit: a
 * body that carries staffUserId MEANS "move this shift".
 *
 * A 409 here is the same decision point as on create: `overlapping_shift`
 * is physics (no override); `time_off_conflict` is policy (re-PATCH with
 * `override: true`).
 */
export async function updateStaffShift(
  apiUrl: string,
  token: string,
  shiftId: string,
  body: {
    staffUserId?: string;
    startTime?: string;
    endTime?: string;
    role?: StaffRole;
    notes?: string | null;
    override?: boolean;
  },
): Promise<{ shift: StaffShift }> {
  const res = await fetch(`${apiUrl}/api/admin/staff-schedules/${shiftId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const fallback = `Failed to update shift (${res.status})`;
    if (res.status === 409) {
      let data:
        | {
            error?: string;
            message?: string;
            conflicting_shift_id?: string;
            time_off_request_id?: string;
          }
        | undefined;
      try {
        data = (await res.json()) as typeof data;
      } catch {
        // body not JSON — fall through to the sanitized generic error
      }
      if (data?.conflicting_shift_id) {
        throw new ShiftConflictError({
          kind: 'overlapping_shift',
          message: data.message ?? 'That overlaps an existing shift.',
          conflictingShiftId: data.conflicting_shift_id,
        });
      }
      if (data?.time_off_request_id) {
        throw new ShiftConflictError({
          kind: 'time_off_conflict',
          message: data.message ?? 'This person has approved time off.',
          timeOffRequestId: data.time_off_request_id,
        });
      }
      const parsed = parseErrorBody(res.status, data, {
        fallback,
        preferBodyMessage: true,
      });
      throw new ApiError(res.status, parsed.message, parsed.code);
    }
    throw await apiErrorFromResponse(res, fallback, {
      preferBodyMessage: true,
    });
  }
  return (await res.json()) as { shift: StaffShift };
}

// ── #665 PR-B — draft→publish ───────────────────────────────────────────

export type PublishNotifyMode = 'all' | 'changed' | 'none';

export type PublishScheduleResult = {
  published_count: number;
  notified: Array<{
    staff_user_id: string;
    staff_name: string;
    shift_count: number;
  }>;
};

/** Stamp every draft shift in the inclusive [from, to] range published and
 *  digest-notify per the manager's chosen mode. */
export async function publishStaffSchedule(
  apiUrl: string,
  token: string,
  body: { from: string; to: string; notify: PublishNotifyMode },
): Promise<PublishScheduleResult> {
  const res = await fetch(`${apiUrl}/api/admin/staff-schedules/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(
      res,
      `Failed to publish the schedule (${res.status})`,
      { preferBodyMessage: true },
    );
  }
  return (await res.json()) as PublishScheduleResult;
}

export type CopyWeekSkip = {
  staff_user_id: string;
  staff_name: string;
  reason: 'time_off' | 'overlapping_shift';
};

export async function copyStaffWeek(
  apiUrl: string,
  token: string,
  body: { fromStartDate: string; toStartDate: string },
): Promise<{ created: StaffShift[]; skipped: CopyWeekSkip[] }> {
  const res = await fetch(`${apiUrl}/api/admin/staff-schedules/copy-week`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to copy week (${res.status})`);
  }
  return (await res.json()) as {
    created: StaffShift[];
    skipped: CopyWeekSkip[];
  };
}

// ─── Time off (#427 W2) ─────────────────────────────────────────────────

export async function fetchTimeOff(
  apiUrl: string,
  token: string,
  query?: { status?: TimeOffStatus; staffUserId?: string },
): Promise<{ requests: TimeOffRequest[] }> {
  const params = new URLSearchParams();
  if (query?.status) params.set('status', query.status);
  if (query?.staffUserId) params.set('staffUserId', query.staffUserId);
  const qs = params.toString();
  return getJson<{ requests: TimeOffRequest[] }>(
    `${apiUrl}/api/v2/admin/time-off${qs ? `?${qs}` : ''}`,
    token,
  );
}

export async function requestTimeOff(
  apiUrl: string,
  token: string,
  body: {
    startDate: string;
    endDate: string;
    type?: TimeOffType;
    note?: string;
  },
): Promise<{ request: TimeOffRequest }> {
  const res = await fetch(`${apiUrl}/api/v2/admin/time-off`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to submit request (${res.status})`, {
      preferBodyMessage: true,
    });
  }
  return (await res.json()) as { request: TimeOffRequest };
}

export async function reviewTimeOff(
  apiUrl: string,
  token: string,
  id: string,
  body: { status: 'approved' | 'denied'; reviewNote?: string },
): Promise<{ request: TimeOffRequest }> {
  const res = await fetch(`${apiUrl}/api/v2/admin/time-off/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to update request (${res.status})`, {
      preferBodyMessage: true,
    });
  }
  return (await res.json()) as { request: TimeOffRequest };
}

export async function cancelTimeOff(
  apiUrl: string,
  token: string,
  id: string,
): Promise<{ request: TimeOffRequest }> {
  const res = await fetch(`${apiUrl}/api/v2/admin/time-off/${id}/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to cancel request (${res.status})`, {
      preferBodyMessage: true,
    });
  }
  return (await res.json()) as { request: TimeOffRequest };
}

export async function deleteStaffShift(
  apiUrl: string,
  token: string,
  id: string,
): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/staff-schedules/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) {
    throw await apiErrorFromResponse(res, `Failed to delete shift (${res.status})`);
  }
}

// ─── Admin Reports (issue #32) ───────────────────────────────────────────

export type EndOfDayGroomer = {
  staffId: string;
  name: string;
  role: string;
  tipCents: number;
  bookingCount: number;
};

// Sawyer 05 end-of-day-tenders (C4 G4) — End of Day "Money taken": every
// completed payment of the facility day, by tender and by source, plus the
// day's checks. Mirrors packages/core/modules/reports/end-of-day-tenders.ts.
export type EndOfDayTender =
  | 'cash'
  | 'check'
  | 'external_terminal'
  | 'card'
  | 'store_credit'
  | 'other'
  | 'unrecorded';

export type EndOfDayCheckLine = {
  paymentId: string;
  customerName: string | null;
  cents: number;
  checkNumber: string | null;
  createdAt: string;
};

export type EndOfDayCollected = {
  /** Payments with money on them. */
  count: number;
  /** New money taken: every tender except store credit. */
  totalCents: number;
  /** Paid from store credit — not new money, shown apart. */
  storeCreditCents: number;
  tipCents: number;
  byTender: Array<{ tender: EndOfDayTender; cents: number; count: number }>;
  bySource: Record<'visits' | 'householdCart' | 'packageSales', { count: number; cents: number }>;
  checks: EndOfDayCheckLine[];
};

export type EndOfDayReport = {
  date: string;
  dogsCheckedIn: number;
  dogsCheckedOut: number;
  invoices: {
    settled: { count: number; totalCents: number };
    outstanding: { count: number; totalCents: number };
  };
  paymentMethods: {
    walletCents: number;
    cardCents: number;
    tipCents: number;
  };
  gratuityByGroomer: EndOfDayGroomer[];
  /** Ted #3 + #9 (#906 #912) — the day's price adjustments. Optional for deploy skew. */
  adjustments?: {
    count: number;
    netCents: number;
    lines: EndOfDayAdjustmentLine[];
  };
  /** Sawyer 05 end-of-day-tenders (C4 G4) — Money taken by tender. Optional for deploy skew. */
  collected?: EndOfDayCollected;
};

export type EndOfDayAdjustmentLine = {
  id: string;
  createdAt: string;
  kind: 'percent' | 'amount' | 'override' | 'package' | 'meal_tracking';
  value: number;
  deltaCents: number;
  originalTotalCents: number;
  adjustedTotalCents: number;
  reason: string;
  staffUsername: string | null;
  customerName: string | null;
  serviceTypeName: string | null;
};

export type VaccinationComplianceIssue = {
  dogId: string;
  dogName: string;
  breed: string | null;
  ownerId: string;
  ownerName: string;
  vaccineName: string;
  issueType: 'expired' | 'unverified';
  expiresAt: string | null;
  nextVisitDate: string | null;
  /** Sawyer 09 pet-inactivity (D1) — `YYYY-MM-DD` when an inactive dog is shown. */
  inactiveSince?: string | null;
};

export type VaccinationComplianceGroup = {
  vaccineName: string;
  issues: VaccinationComplianceIssue[];
};

export type VaccinationComplianceReport = {
  groups: VaccinationComplianceGroup[];
  totalNonCompliant: number;
  totalDogs: number;
  flaggedDogs: number;
  compliantDogs: number;
  compliantPct: number;
  // Sawyer 09 pet-inactivity (D1) — all optional: absent on an older server.
  truncated?: boolean;
  inactiveDogsHidden?: number;
  inactivityMonths?: number | null;
};

export async function fetchEndOfDayReport(
  apiUrl: string,
  token: string,
  date: string,
): Promise<EndOfDayReport> {
  return getJson(
    `${apiUrl}/api/v2/admin/reports/end-of-day?date=${encodeURIComponent(date)}`,
    token,
  );
}

export async function fetchVaccinationComplianceReport(
  apiUrl: string,
  token: string,
  // Sawyer 09 pet-inactivity (D1) — an older server ignores the query key.
  opts: { includeInactive?: boolean } = {},
): Promise<VaccinationComplianceReport> {
  return getJson(
    `${apiUrl}/api/v2/admin/reports/vaccination-compliance${opts.includeInactive ? '?includeInactive=true' : ''}`,
    token,
  );
}

// ─── Schedule day-view (issue #30) ───────────────────────────────────────
// Day-view composes three existing endpoints: staff-on-duty (shifts for
// the day), dashboard/today (arrivals/departures with names), and the
// admin booking schedule (grooming bookings with start_time + service
// type so the calendar can size each block by its actual duration).

export type ScheduleDayArrival = {
  bookingId: string;
  customerName: string;
  dogNames: string[];
  serviceType: string;
  expectedTime: string | null;
  status: string;
};

export type ScheduleDayShift = {
  shiftId: string;
  staffId: string;
  name: string;
  role: string;
  shiftStart: string;
  shiftEnd: string;
};

export async function fetchScheduleDayArrivals(
  apiUrl: string,
  token: string,
  date: string,
): Promise<{ arrivals: ScheduleDayArrival[]; departures: ScheduleDayArrival[] }> {
  return getJson(
    `${apiUrl}/api/admin/dashboard/today?date=${encodeURIComponent(date)}`,
    token,
  );
}

export async function fetchScheduleDayShifts(
  apiUrl: string,
  token: string,
  date: string,
): Promise<{ shifts: ScheduleDayShift[] }> {
  return getJson(
    `${apiUrl}/api/admin/dashboard/staff-on-duty?date=${encodeURIComponent(date)}`,
    token,
  );
}

// ─── Schedule grooming capacity (#283 / Lane E2) ─────────────────────────
// Per-service capacity resolved by the booking engine's own rules, added
// to GET /api/admin/dashboard/facility-status by Lane A2 (2026-07-09).
// `capacity: null` + `capacitySource: 'default'` means the tenant has no
// applicable capacity rules — the server refuses to invent a number
// (DESIGN-PHILOSOPHY Principle 5), so the UI must label any fallback
// denominator as a default rather than dress it up as configured truth.
// `capacity: 0` with `capacitySource: 'rules'` is an honest closed day.

export type ScheduleServiceCapacity = {
  onSite: number;
  capacity: number | null;
  capacitySource: 'rules' | 'default';
};

export async function fetchScheduleGroomingCapacity(
  apiUrl: string,
  token: string,
  date: string,
): Promise<{ services?: { grooming?: ScheduleServiceCapacity } }> {
  return getJson(
    `${apiUrl}/api/admin/dashboard/facility-status?date=${encodeURIComponent(date)}`,
    token,
  );
}

// ─── Platform-admin tenant provisioning ─────────────────────────────────

export type SlugAvailabilityResponse =
  | { available: true; slug: string }
  | { available: false; slug: string; reason: 'taken' };

/**
 * Live slug-uniqueness check for the wizard's Step 1 (D1.5.2).
 *
 * Returns the parsed JSON shape from `GET /api/platform/tenants/slug-availability?slug=...`.
 * The route 404s for any non-platform-admin caller (matching the design's
 * surface-enumeration guard) — surface that as an `ApiError(404)` so the
 * UI can fall back to "we couldn't verify; please try again".
 */
export async function checkTenantSlugAvailability(
  apiUrl: string,
  token: string,
  slug: string,
  signal?: AbortSignal,
): Promise<SlugAvailabilityResponse> {
  const params = new URLSearchParams({ slug });
  const url = `${apiUrl}/api/platform/tenants/slug-availability?${params.toString()}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Slug check failed (${res.status})`);
  }
  return (await res.json()) as SlugAvailabilityResponse;
}

// ─── Brand kit asset upload (D1.5.4) ───────────────────────────────────

export type BrandAssetKind =
  | 'logo-primary-svg'
  | 'logo-primary-png'
  | 'logo-email-header-png'
  | 'logo-dark-mode-svg'
  | 'favicon-png';

export type BrandAssetUploadRequest = {
  tenantSlug: string;
  version: number;
  kind: BrandAssetKind;
  mimeType: string;
  bytesBase64: string;
};

export type BrandAssetUploadResponse = {
  kind: BrandAssetKind;
  url: string;
  path: string;
  size: number;
};

export type BrandAssetUploadErrorBody = {
  error: string;
  reason?: string;
  detail?: string;
};

/**
 * Upload a single brand-kit asset to the platform-admin gated route. The
 * server route validates the asset (SVG parseable, PNG dimensions, mime
 * match), uploads to Vercel Blob, and returns the canonical public URL.
 *
 * Body is JSON with base64-encoded bytes (rather than multipart) so the
 * admin-app stays dependency-free and the route can stay on Express's
 * built-in JSON parser, mounted with an 8 MB limit on this prefix only.
 */
export async function uploadBrandAsset(
  apiUrl: string,
  token: string,
  body: BrandAssetUploadRequest,
  signal?: AbortSignal,
): Promise<BrandAssetUploadResponse> {
  const res = await fetch(`${apiUrl}/api/platform/tenants/brand-asset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let data: BrandAssetUploadErrorBody | undefined;
    try {
      data = (await res.json()) as BrandAssetUploadErrorBody;
    } catch {
      // body wasn't JSON
    }
    const parsed = parseErrorBody(res.status, data, {
      fallback: `Upload failed (${res.status})`,
    });
    const detail = data?.detail ? ` — ${data.detail}` : '';
    throw new ApiError(res.status, `${parsed.message}${detail}`, parsed.code);
  }
  return (await res.json()) as BrandAssetUploadResponse;
}

// ─── Tenant provisioning commit (D1.5.8) ───────────────────────────────

export type TenantCommitTenant = {
  id: string;
  slug: string;
  legalName: string;
  displayName: string;
  vertical: string;
  status: string;
  createdAt: string;
};

export type TenantCommitModules = {
  finalSet: ReadonlyArray<string>;
  autoActivated: ReadonlyArray<string>;
  newlyActivatedCount: number;
};

export type TenantCommitResponse =
  | {
      kind: 'created';
      tenant: TenantCommitTenant;
      modules: TenantCommitModules;
      ownerEmail: string;
    }
  | {
      kind: 'already_exists';
      tenant: TenantCommitTenant;
    };

export type TenantCommitIssue = {
  path: string;
  message: string;
};

export type TenantCommitErrorBody = {
  error: string;
  kind?: string;
  hostname?: string;
  issues?: ReadonlyArray<TenantCommitIssue>;
  cause?: unknown;
};

/**
 * POST the assembled provisioning spec to the commit route. On success
 * the server returns either the newly-created tenant or an already-exists
 * marker (the D1.6.1 service is idempotent on slug). On failure throws
 * `ApiError(status, message)` — the review screen reads status + issues
 * from the raw body via `parseTenantCommitError` below.
 */
export async function commitTenant(
  apiUrl: string,
  token: string,
  spec: unknown,
  signal?: AbortSignal,
): Promise<TenantCommitResponse> {
  const res = await fetch(`${apiUrl}/api/platform/tenants/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ spec }),
    signal,
  });
  if (!res.ok) {
    let body: TenantCommitErrorBody | null = null;
    try {
      body = (await res.json()) as TenantCommitErrorBody;
    } catch {
      // body wasn't JSON; keep status-based message
    }
    const parsed = parseErrorBody(res.status, body, {
      fallback: `Commit failed (${res.status})`,
    });
    const err = new ApiError(res.status, parsed.message, parsed.code);
    (err as ApiError & { body?: TenantCommitErrorBody | null }).body = body;
    throw err;
  }
  return (await res.json()) as TenantCommitResponse;
}

/**
 * Read a browser File into base64 (no data-URI prefix). Used by the
 * brand-kit step uploader before POSTing to /brand-asset. Uses
 * FileReader rather than Blob.arrayBuffer because jsdom (the test
 * environment) implements FileReader but not arrayBuffer on Blob —
 * production browsers support both, so the choice is purely about
 * keeping the same path testable.
 */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      // FileReader.readAsDataURL returns `data:<mime>;base64,<payload>` —
      // strip the prefix so the caller can pass the raw base64 to JSON.
      const commaIdx = result.indexOf(',');
      resolve(commaIdx === -1 ? result : result.slice(commaIdx + 1));
    };
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('FileReader failed'));
    };
    reader.readAsDataURL(file);
  });
}

// ─── Owner invite acceptance (D1.7.2) ──────────────────────────────────

export type InviteAcceptRequest = {
  token: string;
  password: string;
};

export type InviteAcceptResponse = {
  ok: true;
  userId: string;
  tenantId: string;
  tenantSlug: string;
  tenantStatus: string;
  tenantActivated: boolean;
  acceptedAt: string;
  activatedAt: string | null;
};

export type InviteAcceptErrorKind =
  | 'validation'
  | 'invalid_token'
  | 'already_accepted'
  | 'expired'
  | 'db_error';

export type InviteAcceptErrorBody = {
  error?: string;
  kind?: InviteAcceptErrorKind;
  issues?: ReadonlyArray<{ path: string; message: string }>;
  acceptedAt?: string;
  sentAt?: string;
  expiresAt?: string;
};

export class InviteAcceptError extends ApiError {
  public readonly kind: InviteAcceptErrorKind | 'unknown';
  public readonly body: InviteAcceptErrorBody | null;
  constructor(
    status: number,
    message: string,
    kind: InviteAcceptErrorKind | 'unknown',
    body: InviteAcceptErrorBody | null,
  ) {
    super(status, message);
    this.name = 'InviteAcceptError';
    this.kind = kind;
    this.body = body;
  }
}

export async function acceptOwnerInvite(
  apiUrl: string,
  body: InviteAcceptRequest,
  signal?: AbortSignal,
): Promise<InviteAcceptResponse> {
  const res = await fetch(`${apiUrl}/api/platform/invites/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let bodyParsed: InviteAcceptErrorBody | null = null;
    let message = `Invite acceptance failed (${res.status})`;
    try {
      bodyParsed = (await res.json()) as InviteAcceptErrorBody;
      message = bodyParsed.error ?? message;
    } catch {
      // body wasn't JSON
    }
    const kind: InviteAcceptErrorKind | 'unknown' =
      (bodyParsed?.kind as InviteAcceptErrorKind | undefined) ?? 'unknown';
    throw new InviteAcceptError(res.status, message, kind, bodyParsed);
  }
  return (await res.json()) as InviteAcceptResponse;
}

// ─── #405 — staff forgot-password ──────────────────────────────────────

export type StaffForgotPasswordRequest = { email: string };
export type StaffForgotPasswordResponse = { message: string };

/**
 * Always resolves (never throws on a 4xx enumeration-safe response —
 * the server intentionally 200s whether or not the email matched an
 * account). Only network failures / malformed responses / the DB-backed
 * 429 rate-limit surface as thrown errors here.
 */
export async function requestStaffPasswordReset(
  apiUrl: string,
  body: StaffForgotPasswordRequest,
  signal?: AbortSignal,
): Promise<StaffForgotPasswordResponse> {
  const res = await fetch(`${apiUrl}/api/auth/staff/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Request failed (${res.status})`, {
      preferBodyMessage: true,
    });
  }
  return (await res.json()) as StaffForgotPasswordResponse;
}

export type StaffResetPasswordRequest = { token: string; newPassword: string };
export type StaffResetPasswordResponse = { message: string };

export class StaffResetPasswordError extends ApiError {
  // #711 — ApiError now carries an optional `code`; this subclass narrows
  // it to required (its flow always has one).
  public override readonly code: string;
  constructor(status: number, message: string, code: string) {
    super(status, message, code);
    this.name = 'StaffResetPasswordError';
    this.code = code;
  }
}

export async function confirmStaffPasswordReset(
  apiUrl: string,
  body: StaffResetPasswordRequest,
  signal?: AbortSignal,
): Promise<StaffResetPasswordResponse> {
  const res = await fetch(`${apiUrl}/api/auth/staff/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let message = `Reset failed (${res.status})`;
    let code = 'unknown';
    try {
      const parsed = (await res.json()) as { error?: string; code?: string };
      message = parsed.error ?? message;
      code = parsed.code ?? code;
    } catch {
      // body wasn't JSON
    }
    throw new StaffResetPasswordError(res.status, message, code);
  }
  return (await res.json()) as StaffResetPasswordResponse;
}

// ─── Platform-admin intake invites (D2.2.1) ────────────────────────────

export type CreateIntakeInviteRequest = {
  email: string;
  prospectFirstName?: string;
  companyDisplayName?: string;
  inviterName?: string;
  expiresInDays?: number;
};

export type CreateIntakeInviteResponse = {
  ok: true;
  intakeId: string;
  inviteEmail: string;
  inviteUrl: string;
  inviteExpiresAt: string;
  state: string;
  providerMessageId: string;
};

export type CreateIntakeInviteErrorKind =
  | 'validation'
  | 'token_collision'
  | 'db_error'
  | 'send_failed'
  | 'unknown';

export type CreateIntakeInviteErrorBody = {
  error?: string;
  kind?: CreateIntakeInviteErrorKind;
  issues?: ReadonlyArray<{ path: string; message: string }>;
};

export class CreateIntakeInviteError extends ApiError {
  public readonly kind: CreateIntakeInviteErrorKind;
  public readonly body: CreateIntakeInviteErrorBody | null;
  constructor(
    status: number,
    message: string,
    kind: CreateIntakeInviteErrorKind,
    body: CreateIntakeInviteErrorBody | null,
  ) {
    super(status, message);
    this.name = 'CreateIntakeInviteError';
    this.kind = kind;
    this.body = body;
  }
}

export async function createIntakeInvite(
  apiUrl: string,
  token: string,
  body: CreateIntakeInviteRequest,
  signal?: AbortSignal,
): Promise<CreateIntakeInviteResponse> {
  const res = await fetch(`${apiUrl}/api/platform/intakes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let parsed: CreateIntakeInviteErrorBody | null = null;
    let message = `Intake invite failed (${res.status})`;
    try {
      parsed = (await res.json()) as CreateIntakeInviteErrorBody;
      message = parsed.error ?? message;
    } catch {
      // body wasn't JSON
    }
    const kind: CreateIntakeInviteErrorKind =
      (parsed?.kind as CreateIntakeInviteErrorKind | undefined) ?? 'unknown';
    throw new CreateIntakeInviteError(res.status, message, kind, parsed);
  }
  return (await res.json()) as CreateIntakeInviteResponse;
}

// ─── Prospect intake portal (D2.3.1) ───────────────────────────────────

export type IntakePortalState = {
  id: string;
  inviteEmail: string;
  inviteSentAt: string | null;
  inviteExpiresAt: string;
  state: 'draft' | 'in_progress' | 'submitted' | 'rejected' | 'provisioned';
  lastStep: number;
  payload: Record<string, unknown>;
  rejectionReason: string | null;
  rejectedAt: string | null;
  submittedAt: string | null;
  provisionedTenantId: string | null;
  updatedAt: string;
};

export type IntakePortalStateResponse = {
  ok: true;
  intake: IntakePortalState;
};

export class IntakePortalNotFoundError extends ApiError {
  constructor(message = 'Not found') {
    super(404, message);
    this.name = 'IntakePortalNotFoundError';
  }
}

/**
 * Fetch the hydrated `TenantIntake` row for a given invite token.
 * The server gate 404s every failure mode (expired, rejected,
 * provisioned, unknown, malformed) to avoid token enumeration —
 * we surface a distinct `IntakePortalNotFoundError` so the shell
 * can render a generic "link no longer valid" state without trying
 * to distinguish between them.
 */
export async function fetchIntakePortalState(
  apiUrl: string,
  token: string,
  signal?: AbortSignal,
): Promise<IntakePortalState> {
  const res = await fetch(`${apiUrl}/api/intake/${encodeURIComponent(token)}/state`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (res.status === 404) {
    throw new IntakePortalNotFoundError();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Intake load failed (${res.status})`);
  }
  const body = (await res.json()) as IntakePortalStateResponse;
  return body.intake;
}

export type IntakeStepSubmissionRequest = {
  stepId: string;
  payload: unknown;
};

export type IntakeStepSubmissionResponse = {
  ok: true;
  id: string;
  state: 'draft' | 'in_progress' | 'submitted' | 'rejected' | 'provisioned';
  fromState: 'draft' | 'in_progress' | 'submitted' | 'rejected' | 'provisioned';
  stateChanged: boolean;
  lastStep: number;
  stepId: string;
  updatedAt: string;
};

export type IntakeStepSubmissionIssue = { path: string; message: string };

export type IntakeStepSubmissionErrorKind =
  | 'validation'
  | 'unknown_step_id'
  | 'illegal_state'
  | 'state_transition_failed'
  | 'db_error'
  | 'network'
  | 'unknown';

export type IntakeStepSubmissionErrorBody = {
  error?: string;
  kind?: IntakeStepSubmissionErrorKind;
  stepId?: string;
  state?: string;
  issues?: ReadonlyArray<IntakeStepSubmissionIssue>;
};

export class IntakeStepSubmissionError extends ApiError {
  public readonly kind: IntakeStepSubmissionErrorKind;
  public readonly body: IntakeStepSubmissionErrorBody | null;
  constructor(
    status: number,
    message: string,
    kind: IntakeStepSubmissionErrorKind,
    body: IntakeStepSubmissionErrorBody | null,
  ) {
    super(status, message);
    this.name = 'IntakeStepSubmissionError';
    this.kind = kind;
    this.body = body;
  }
}

/**
 * POST a step payload to the prospect-facing portal. On `TypeError`
 * (network failure in the browser's fetch) throws with
 * `kind: 'network'` so the shell can queue the submission for replay.
 */
export async function submitIntakeStep(
  apiUrl: string,
  token: string,
  body: IntakeStepSubmissionRequest,
  signal?: AbortSignal,
): Promise<IntakeStepSubmissionResponse> {
  let res: Response;
  try {
    res = await fetch(`${apiUrl}/api/intake/${encodeURIComponent(token)}/step`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    // Fetch only throws on network failures / CORS / abort. Map these
    // to a typed network error so the shell can enqueue + retry.
    throw new IntakeStepSubmissionError(
      0,
      cause instanceof Error ? cause.message : 'Network error',
      'network',
      null,
    );
  }
  if (res.status === 404) {
    throw new IntakePortalNotFoundError();
  }
  if (!res.ok) {
    let parsed: IntakeStepSubmissionErrorBody | null = null;
    let message = `Step submission failed (${res.status})`;
    try {
      parsed = (await res.json()) as IntakeStepSubmissionErrorBody;
      message = parsed.error ?? message;
    } catch {
      // body wasn't JSON
    }
    const kind: IntakeStepSubmissionErrorKind =
      (parsed?.kind as IntakeStepSubmissionErrorKind | undefined) ?? 'unknown';
    throw new IntakeStepSubmissionError(res.status, message, kind, parsed);
  }
  return (await res.json()) as IntakeStepSubmissionResponse;
}

// ─── Intake portal submit for review (D2.3.12) ─────────────────────────

export type IntakeSubmitRequest = Record<string, never>;

export type IntakeSubmitResponse = {
  ok: true;
  id: string;
  state: 'submitted';
  fromState: 'in_progress';
  submittedAt: string;
  updatedAt: string;
};

export type IntakeSubmitErrorKind =
  | 'illegal_state'
  | 'incomplete_steps'
  | 'step10_invalid'
  | 'state_transition_failed'
  | 'db_error'
  | 'network'
  | 'unknown';

export type IntakeSubmitErrorBody = {
  error?: string;
  kind?: IntakeSubmitErrorKind;
  state?: string;
  missing?: ReadonlyArray<string>;
  issues?: ReadonlyArray<IntakeStepSubmissionIssue>;
};

export class IntakeSubmitError extends ApiError {
  public readonly kind: IntakeSubmitErrorKind;
  public readonly body: IntakeSubmitErrorBody | null;
  constructor(
    status: number,
    message: string,
    kind: IntakeSubmitErrorKind,
    body: IntakeSubmitErrorBody | null,
  ) {
    super(status, message);
    this.name = 'IntakeSubmitError';
    this.kind = kind;
    this.body = body;
  }
}

/**
 * POST the "Submit for Review" action on a prospect intake. No body —
 * the server validates the full payload against `intakeStep10Schema`
 * plus the completeness gate, then transitions `in_progress →
 * submitted`. On `TypeError` (network failure in the browser's fetch)
 * throws with `kind: 'network'` so the UI can prompt a retry without
 * dropping the user into a confusing state.
 */
export async function submitIntakeForReview(
  apiUrl: string,
  token: string,
  signal?: AbortSignal,
): Promise<IntakeSubmitResponse> {
  let res: Response;
  try {
    res = await fetch(
      `${apiUrl}/api/intake/${encodeURIComponent(token)}/submit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: '{}',
        signal,
      },
    );
  } catch (cause) {
    throw new IntakeSubmitError(
      0,
      cause instanceof Error ? cause.message : 'Network error',
      'network',
      null,
    );
  }
  if (res.status === 404) {
    throw new IntakePortalNotFoundError();
  }
  if (!res.ok) {
    let parsed: IntakeSubmitErrorBody | null = null;
    let message = `Submit failed (${res.status})`;
    try {
      parsed = (await res.json()) as IntakeSubmitErrorBody;
      message = parsed.error ?? message;
    } catch {
      // body wasn't JSON
    }
    const kind: IntakeSubmitErrorKind =
      (parsed?.kind as IntakeSubmitErrorKind | undefined) ?? 'unknown';
    throw new IntakeSubmitError(res.status, message, kind, parsed);
  }
  return (await res.json()) as IntakeSubmitResponse;
}

// ─── Intake portal brand-asset upload (D2.2.4) ─────────────────────────

export type IntakeUploadKind =
  | 'logo-primary-svg'
  | 'logo-primary-png'
  | 'logo-email-header-png'
  | 'logo-dark-mode-svg'
  | 'favicon-png';

export type IntakeUploadRequest = {
  kind: IntakeUploadKind;
  mimeType: string;
  bytesBase64: string;
};

export type IntakeUploadResponse = {
  ok: true;
  kind: IntakeUploadKind;
  url: string;
  path: string;
  size: number;
  uploadedAt: string;
  updatedAt: string;
};

export type IntakeUploadErrorBody = {
  error: string;
  reason?: string;
  detail?: string;
  kind?: string;
};

/**
 * Upload a single brand-kit asset through the prospect-facing intake
 * endpoint. Authorization is the intake token in the URL path (no
 * Bearer header) — every failure mode of the gate returns 404 to avoid
 * enumeration, so a 404 here means "this intake link is no longer
 * valid" rather than a distinct asset-level error.
 */
export async function uploadIntakeBrandAsset(
  apiUrl: string,
  token: string,
  body: IntakeUploadRequest,
  signal?: AbortSignal,
): Promise<IntakeUploadResponse> {
  const res = await fetch(
    `${apiUrl}/api/intake/${encodeURIComponent(token)}/upload`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    },
  );
  if (res.status === 404) {
    throw new IntakePortalNotFoundError();
  }
  if (!res.ok) {
    let data: IntakeUploadErrorBody | undefined;
    try {
      data = (await res.json()) as IntakeUploadErrorBody;
    } catch {
      // body wasn't JSON
    }
    const parsed = parseErrorBody(res.status, data, {
      fallback: `Upload failed (${res.status})`,
    });
    const detail = data?.detail ? ` — ${data.detail}` : '';
    throw new ApiError(res.status, `${parsed.message}${detail}`, parsed.code);
  }
  return (await res.json()) as IntakeUploadResponse;
}

// ─── Intake portal Stripe Connect account link (D2.2.5) ─────────────────

export type IntakeStripeLinkRequest = {
  returnUrl: string;
  refreshUrl?: string;
};

export type IntakeStripeLinkResponse = {
  ok: true;
  url: string;
  expiresAt: string;
  stripeAccountId: string;
  accountCreated: boolean;
  accountLinkRequestCount: number;
  generatedAt: string;
};

export type IntakeStripeLinkErrorKind =
  | 'validation'
  | 'stripe_not_configured'
  | 'stripe_error'
  | 'db_error'
  | 'unknown';

export type IntakeStripeLinkErrorBody = {
  error?: string;
  kind?: IntakeStripeLinkErrorKind;
  message?: string;
  code?: string;
  details?: ReadonlyArray<{ field: string; message: string }>;
};

export class IntakeStripeLinkError extends ApiError {
  public readonly kind: IntakeStripeLinkErrorKind;
  public readonly body: IntakeStripeLinkErrorBody | null;
  constructor(
    status: number,
    message: string,
    kind: IntakeStripeLinkErrorKind,
    body: IntakeStripeLinkErrorBody | null,
  ) {
    super(status, message);
    this.name = 'IntakeStripeLinkError';
    this.kind = kind;
    this.body = body;
  }
}

/**
 * Mint (or reuse) a Stripe Connect Express account and return an
 * Account Link URL. Token-gated through `/api/intake/:token/stripe-link`
 * (D2.2.5). The endpoint returns 503 with `kind: 'stripe_not_configured'`
 * when no provider is wired (local dev without STRIPE_SECRET_KEY) — the
 * portal surfaces that distinctly so the prospect sees an explicit "not
 * available yet" state instead of a silent failure.
 */
export async function getIntakeStripeLink(
  apiUrl: string,
  token: string,
  body: IntakeStripeLinkRequest,
  signal?: AbortSignal,
): Promise<IntakeStripeLinkResponse> {
  let res: Response;
  try {
    res = await fetch(
      `${apiUrl}/api/intake/${encodeURIComponent(token)}/stripe-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      },
    );
  } catch (cause) {
    throw new IntakeStripeLinkError(
      0,
      cause instanceof Error ? cause.message : 'Network error',
      'unknown',
      null,
    );
  }
  if (res.status === 404) {
    throw new IntakePortalNotFoundError();
  }
  if (!res.ok) {
    let parsed: IntakeStripeLinkErrorBody | null = null;
    let message = `Stripe link request failed (${res.status})`;
    try {
      parsed = (await res.json()) as IntakeStripeLinkErrorBody;
      message = parsed.message ?? parsed.error ?? message;
    } catch {
      // body wasn't JSON
    }
    const kind: IntakeStripeLinkErrorKind =
      parsed?.kind === 'validation'
        || parsed?.kind === 'stripe_not_configured'
        || parsed?.kind === 'stripe_error'
        || parsed?.kind === 'db_error'
        ? parsed.kind
        : 'unknown';
    throw new IntakeStripeLinkError(res.status, message, kind, parsed);
  }
  return (await res.json()) as IntakeStripeLinkResponse;
}

// ─── Platform-admin tenant directory (wd-3.5) ─────────────────────────

export type PlatformTenantSummary = {
  id: string;
  slug: string;
  legalName: string;
  displayName: string;
  vertical: string;
  status: string;
  activatedAt: string | null;
  suspendedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListPlatformTenantsQuery = {
  limit?: number;
  offset?: number;
  status?: string;
};

export type ListPlatformTenantsResponse = {
  ok: true;
  tenants: PlatformTenantSummary[];
  total: number;
  query: { limit: number; offset: number; status: string | null };
};

export type PlatformTenantDetail = PlatformTenantSummary & {
  contractSignedAt: string | null;
  deactivatedAt: string | null;
  billingAccountId: string | null;
};

export async function getPlatformTenant(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<PlatformTenantDetail> {
  const res = await fetch(
    `${apiUrl}/api/platform/tenants/${encodeURIComponent(tenantId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Tenant fetch failed (${res.status})`);
  }
  const body = (await res.json()) as { ok: true; tenant: PlatformTenantDetail };
  return body.tenant;
}

export async function listPlatformTenants(
  apiUrl: string,
  token: string,
  query: ListPlatformTenantsQuery = {},
  signal?: AbortSignal,
): Promise<ListPlatformTenantsResponse> {
  const params = new URLSearchParams();
  if (typeof query.limit === 'number') params.set('limit', String(query.limit));
  if (typeof query.offset === 'number') params.set('offset', String(query.offset));
  if (query.status) params.set('status', query.status);
  const qs = params.toString();
  const url = qs
    ? `${apiUrl}/api/platform/tenants?${qs}`
    : `${apiUrl}/api/platform/tenants`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Tenants list failed (${res.status})`);
  }
  return (await res.json()) as ListPlatformTenantsResponse;
}

// ─── RBAC audit-log read (wa-2.4) ─────────────────────────────────────

export type RbacAuditEvent = {
  id: string;
  tenantId: string;
  actorUserId: string | null;
  actorKind: string;
  impersonationId: string | null;
  eventType: string;
  targetUserId: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
};

export type RbacAuditListQuery = {
  limit?: number;
  offset?: number;
  eventType?: string;
};

export type RbacAuditListResponse = {
  ok: true;
  events: RbacAuditEvent[];
  total: number;
  query: { limit: number; offset: number; eventType: string | null };
};

export async function fetchRbacAuditEvents(
  apiUrl: string,
  token: string,
  query: RbacAuditListQuery = {},
  signal?: AbortSignal,
): Promise<RbacAuditListResponse> {
  const params = new URLSearchParams();
  if (typeof query.limit === 'number') params.set('limit', String(query.limit));
  if (typeof query.offset === 'number') params.set('offset', String(query.offset));
  if (query.eventType) params.set('eventType', query.eventType);
  const qs = params.toString();
  const url = qs
    ? `${apiUrl}/api/admin/rbac-audit?${qs}`
    : `${apiUrl}/api/admin/rbac-audit`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Audit fetch failed (${res.status})`);
  }
  return (await res.json()) as RbacAuditListResponse;
}

export function mondayIsoDate(now: Date = new Date()): string {
  const d = new Date(now);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function addIsoDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ─── Platform-admin intake dashboard (D2.5.1) ──────────────────────────

export type IntakeListSortField =
  | 'updatedAt'
  | 'createdAt'
  | 'state'
  | 'submittedAt';
export type IntakeListSortDirection = 'asc' | 'desc';

export type ListIntakesQuery = {
  state?: string;
  limit?: number;
  offset?: number;
  sortBy?: IntakeListSortField;
  sortDir?: IntakeListSortDirection;
};

export type IntakeSummary = {
  id: string;
  inviteEmail: string;
  inviteSentAt: string | null;
  inviteExpiresAt: string;
  state: string;
  lastStep: number;
  rejectionReason: string | null;
  rejectedAt: string | null;
  submittedAt: string | null;
  provisionedTenantId: string | null;
  createdByAdminId: string;
  companyName: string | null;
  nudgeCount: number;
  lastNudgeAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListIntakesResponse = {
  ok: true;
  intakes: IntakeSummary[];
  total: number;
  query: {
    state: string | null;
    limit: number;
    offset: number;
    sortBy: IntakeListSortField;
    sortDir: IntakeListSortDirection;
  };
};

export type IntakeAuditEvent = {
  id: string;
  intakeId: string;
  eventType: string;
  fromState: string | null;
  toState: string | null;
  actorUserId: string | null;
  actorKind: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
};

export type IntakeDetail = {
  id: string;
  inviteToken: string;
  inviteEmail: string;
  inviteSentAt: string | null;
  inviteExpiresAt: string;
  state: string;
  lastStep: number;
  payload: Record<string, unknown>;
  rejectionReason: string | null;
  rejectedAt: string | null;
  submittedAt: string | null;
  provisionedTenantId: string | null;
  createdByAdminId: string;
  nudgeCount: number;
  lastNudgeAt: string | null;
  createdAt: string;
  updatedAt: string;
  auditEvents: IntakeAuditEvent[];
};

export type IntakeDetailResponse = {
  ok: true;
  intake: IntakeDetail;
};

/**
 * Throws `ApiError` on non-2xx so the page can render a typed error
 * state. Network failures bubble up as the raw `TypeError` the fetch
 * API produces — same pattern as the other admin-app helpers.
 */
export async function listIntakes(
  apiUrl: string,
  token: string,
  query: ListIntakesQuery = {},
  signal?: AbortSignal,
): Promise<ListIntakesResponse> {
  const params = new URLSearchParams();
  if (query.state) params.set('state', query.state);
  if (typeof query.limit === 'number') params.set('limit', String(query.limit));
  if (typeof query.offset === 'number')
    params.set('offset', String(query.offset));
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDir) params.set('sortDir', query.sortDir);
  const qs = params.toString();
  const url = qs
    ? `${apiUrl}/api/platform/intakes?${qs}`
    : `${apiUrl}/api/platform/intakes`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Intake list failed (${res.status})`);
  }
  return (await res.json()) as ListIntakesResponse;
}

export async function getIntakeDetail(
  apiUrl: string,
  token: string,
  intakeId: string,
  signal?: AbortSignal,
): Promise<IntakeDetailResponse> {
  const res = await fetch(
    `${apiUrl}/api/platform/intakes/${encodeURIComponent(intakeId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Intake load failed (${res.status})`);
  }
  return (await res.json()) as IntakeDetailResponse;
}

// D3.3.2 — commit-intake response shapes.
export type IntakeCommitResponse = {
  ok: true;
  intakeId: string;
  intakeState: string;
  tenantId: string;
  tenantSlug: string;
  tenantCreated: boolean;
  modules: {
    finalSet: ReadonlyArray<string>;
    autoActivated: ReadonlyArray<string>;
  };
  owner: {
    userId: string | null;
    email: string;
    inviteSent: boolean;
    inviteProviderMessageId: string | null;
    inviteError: string | null;
  };
  assetCopies: ReadonlyArray<{ slot: string; targetUrl: string }>;
};

export type IntakeCommitErrorBody = {
  error: string;
  kind: string;
  /** Present on illegal_state. */
  state?: string;
  /** Present on provisioning_failed. */
  cause?: { kind: string; hostname?: string; issues?: unknown[]; cause?: unknown };
  /** Present on asset_copy_failed. */
  failures?: Array<{ slot: string; sourceUrl: string; targetPath: string; message: string }>;
  /** Present on finalize_failed. */
  tenantId?: string;
  tenantSlug?: string;
};

export async function commitIntakeToTenant(
  apiUrl: string,
  token: string,
  intakeId: string,
  options: { adminHostname?: string } = {},
  signal?: AbortSignal,
): Promise<IntakeCommitResponse> {
  const res = await fetch(
    `${apiUrl}/api/platform/intakes/${encodeURIComponent(intakeId)}/commit`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        options.adminHostname ? { adminHostname: options.adminHostname } : {},
      ),
      signal,
    },
  );
  if (!res.ok) {
    let body: IntakeCommitErrorBody | null = null;
    try {
      body = (await res.json()) as IntakeCommitErrorBody;
    } catch {
      // not JSON
    }
    const parsed = parseErrorBody(res.status, body, {
      fallback: `Intake commit failed (${res.status})`,
    });
    const apiErr = new ApiError(res.status, parsed.message, parsed.code);
    // Surface structured body on the thrown error so the UI can render
    // typed messaging (e.g. "state is in_progress, not submitted").
    (apiErr as ApiError & { body?: IntakeCommitErrorBody }).body = body ?? undefined;
    throw apiErr;
  }
  return (await res.json()) as IntakeCommitResponse;
}

// D3.4.1 — reject-intake response shapes.
export type IntakeRejectResponse = {
  ok: true;
  intakeId: string;
  intakeState: string;
  fromState: string;
  rejectionReason: string;
  rejectedAt: string;
  updatedAt: string;
  notifier: {
    attempted: boolean;
    sent: boolean;
    error: string | null;
  };
};

export type IntakeRejectErrorBody = {
  error: string;
  kind: string;
  /** Present on illegal_state. */
  state?: string;
  /** Present on validation. */
  issues?: Array<{ path: string; message: string }>;
  /** Present on intake_not_found. */
  intakeId?: string;
};

export async function rejectIntake(
  apiUrl: string,
  token: string,
  intakeId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<IntakeRejectResponse> {
  const res = await fetch(
    `${apiUrl}/api/platform/intakes/${encodeURIComponent(intakeId)}/reject`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      signal,
    },
  );
  if (!res.ok) {
    let body: IntakeRejectErrorBody | null = null;
    try {
      body = (await res.json()) as IntakeRejectErrorBody;
    } catch {
      // not JSON
    }
    const parsed = parseErrorBody(res.status, body, {
      fallback: `Intake reject failed (${res.status})`,
    });
    const apiErr = new ApiError(res.status, parsed.message, parsed.code);
    (apiErr as ApiError & { body?: IntakeRejectErrorBody }).body = body ?? undefined;
    throw apiErr;
  }
  return (await res.json()) as IntakeRejectResponse;
}

// ─── HTHD-parity admin namespaces (W0.5) ───────────────────────────────
// Everything below mirrors HTHD's admin-app/src/lib/api.ts surface so
// pages ported in Wave 2 drop in unchanged. Each namespace takes
// `apiUrl` first (matching the platform's existing convention) and
// returns `ApiResponse<T>` via the shared `request` helper above.
//
// Excluded by W0.5 master plan: adminAimApi, adminGingrApi,
// adminDemoApi (all Phase 3 / HTHD-specific; out of scope).
//
// `@todo tighten` markers flag types HTHD itself uses `any` for —
// we'll harden them once the corresponding server contracts firm up.
//
// `@todo Server endpoint pending W0.6` markers flag functions whose
// server route doesn't exist on the platform yet; W0.6 audits and
// fills these in.

// ─── adminAuthApi ──────────────────────────────────────────────────────

export interface StaffAuthResponse {
  token: string;
  staff: StaffUser;
}

export const adminAuthApi = {
  /**
   * Username + password staff login. Wraps the existing standalone
   * `loginStaff` helper but returns the ApiResponse<T> shape so new
   * pages can consume it via the same pattern as the rest of the
   * admin namespaces. Existing Login.tsx still uses `loginStaff`
   * directly; both paths are supported.
   *
   * @todo Server endpoint pending W0.6 — confirm POST /api/admin/auth/login
   *       vs the existing /api/auth/staff/login route used by loginStaff.
   */
  login: (
    apiUrl: string,
    data: { username: string; password: string },
  ): Promise<ApiResponse<StaffAuthResponse>> =>
    api.post<StaffAuthResponse>(apiUrl, '/api/admin/auth/login', data),
};

// ─── adminCustomersApi (search) ────────────────────────────────────────

export interface CustomerSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string;
  email: string;
  points_balance: number;
}

export interface CustomerSearchResponse {
  customers: CustomerSearchResult[];
  count: number;
}

/**
 * Quick-search endpoint used by LoyaltyPage and PaymentModal in HTHD —
 * loose-text query against name/phone/email returning a small batch of
 * matches.
 *
 * Served by buildAdminLoyaltyRouter (w2.b) — GET /api/admin/customers/search.
 */
export const adminCustomersApi = {
  search: (apiUrl: string, query: string): Promise<ApiResponse<CustomerSearchResponse>> =>
    api.get<CustomerSearchResponse>(
      apiUrl,
      `/api/admin/customers/search?q=${encodeURIComponent(query)}`,
    ),
};

// ─── adminCustomersListApi (list, get, transactions, redemptions) ─────
// HTHD's listing + per-customer drill-down. The platform's existing
// `fetchCustomers`/`fetchCustomer` helpers above hit a different
// `/api/admin/crm/customers` route shape and stay in tree for the
// existing pages; these new helpers target HTHD's `/api/admin/customers`
// shape so ports drop in unchanged.

export interface AdminCustomerListItem {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string;
  email: string;
  points_balance: number;
  join_date: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_more: boolean;
}

export interface AdminCustomerListResponse {
  customers: AdminCustomerListItem[];
  pagination: PaginationInfo;
}

export interface AdminCustomerListParams {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  search?: string;
}

export interface CustomerReferralInfo {
  id: string;
  name: string;
}

export interface CustomerReferral {
  id: string;
  name: string;
  join_date: string;
}

export interface DogVaccination {
  id: string;
  name: string;
  date_given: string;
  expires_at: string | null;
  verified: boolean;
}

export interface CustomerDog {
  id: string;
  name: string;
  breed: string | null;
  size_category: string | null;
  weight: number | null;
  birth_date: string | null;
  notes: string | null;
  temperament: string | null;
  care_instructions: string | null;
  vet_name?: string | null;
  vet_phone?: string | null;
  vet_email?: string | null;
  vet_address?: string | null;
  is_neutered: boolean;
  photo_url: string | null;
  vaccinations: DogVaccination[];
}

export interface CustomerBooking {
  id: string;
  date: string;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  status: string;
  total_cents: number;
  notes: string | null;
  service_name: string;
  service_display_name: string;
  dogs: { id: string; name: string }[];
  created_at: string;
}

/**
 * HTHD-shaped customer detail. Distinct from the platform's CRM-shaped
 * `CustomerDetail` exported above; ported pages should consume this
 * one for parity with HTHD's existing components.
 */
export interface AdminCustomerDetail {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string;
  email: string;
  points_balance: number;
  referral_code: string;
  account_status: string;
  source: string;
  join_date: string;
  referred_by: CustomerReferralInfo | null;
  referrals: CustomerReferral[];
  dogs: CustomerDog[];
  upcoming_bookings: CustomerBooking[];
  recent_bookings: CustomerBooking[];
}

export interface AdminCustomerTransaction {
  id: string;
  type: string;
  points_amount: number;
  description: string;
  dollar_amount: number | null;
  service_type: string | null;
  date: string;
}

export interface AdminCustomerTransactionsResponse {
  transactions: AdminCustomerTransaction[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface AdminCustomerRedemption {
  id: string;
  redemption_code: string;
  reward_tier: number;
  discount_value: number;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  approved_at: string | null;
}

export interface AdminCustomerRedemptionsResponse {
  pending: AdminCustomerRedemption[];
  completed: AdminCustomerRedemption[];
}

/**
 * #252 review fix (BLOCKER 2) — the loyalty-at-settle balance read.
 *
 * The settle flow previously read GET /api/admin/customers/:id via
 * adminCustomersListApi.get — a route that does NOT exist on the server
 * (a W0.6 stub; the deny-by-default /api/admin guard fails it closed),
 * so in prod every queue-selected checkout rendered "loyalty balance
 * unavailable" and the loyalty leg was dead. This helper targets the
 * REAL route — GET /api/admin/crm/customers/:id (crm module, mapped to
 * customers:read:all in ADMIN_ROUTE_PERMISSIONS) — whose response is a
 * `{ customer }` envelope; `customerSummaryView` exposes
 * `points_balance`. Route existence is pinned by execution in
 * apps/server/src/__tests__/crm-customer-detail.route.test.ts.
 */
export const adminCrmCustomersApi = {
  get: (
    apiUrl: string,
    id: string,
  ): Promise<ApiResponse<{ customer: CustomerSummary }>> =>
    api.get<{ customer: CustomerSummary }>(
      apiUrl,
      `/api/admin/crm/customers/${id}`,
    ),
};

export const adminCustomersListApi = {
  /** @todo Server endpoint pending W0.6 — `/api/admin/customers`. */
  list: (
    apiUrl: string,
    params: AdminCustomerListParams = {},
  ): Promise<ApiResponse<AdminCustomerListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params.sort_order) searchParams.set('sort_order', params.sort_order);
    if (params.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    return api.get<AdminCustomerListResponse>(
      apiUrl,
      `/api/admin/customers${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * ⚠️ PHANTOM ROUTE — GET /api/admin/customers/:id does NOT exist on
   * the server (no router serves it; the deny-by-default /api/admin
   * guard fails it closed). Do NOT consume this until the W0.6 endpoint
   * ships. For a customer's points balance use adminCrmCustomersApi.get
   * (the real CRM route) instead — see #252 review fix.
   * @todo Server endpoint pending W0.6.
   */
  get: (apiUrl: string, id: string): Promise<ApiResponse<AdminCustomerDetail>> =>
    api.get<AdminCustomerDetail>(apiUrl, `/api/admin/customers/${id}`),
  /** Served by buildAdminLoyaltyRouter (w2.b). */
  getTransactions: (
    apiUrl: string,
    id: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<ApiResponse<AdminCustomerTransactionsResponse>> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return api.get<AdminCustomerTransactionsResponse>(
      apiUrl,
      `/api/admin/customers/${id}/transactions${qs ? `?${qs}` : ''}`,
    );
  },
  /** @todo Server endpoint pending W0.6. */
  getRedemptions: (
    apiUrl: string,
    id: string,
  ): Promise<ApiResponse<AdminCustomerRedemptionsResponse>> =>
    api.get<AdminCustomerRedemptionsResponse>(
      apiUrl,
      `/api/admin/customers/${id}/redemptions`,
    ),
};

// ─── adminPointsApi ────────────────────────────────────────────────────

export interface AddPointsRequest {
  customer_id: string;
  dollar_amount: number;
  service_type: 'daycare' | 'boarding' | 'grooming';
}

export interface AddPointsResponse {
  success: boolean;
  transaction: {
    id: string;
    type: string;
    points_earned: number;
    dollar_amount: number;
    service_type: string;
    description: string;
    created_at: string;
  };
  customer: {
    id: string;
    name: string;
    previous_balance: number;
    new_balance: number;
  };
}

export const adminPointsApi = {
  /**
   * Manual points award by staff — used by LoyaltyPage and the AddPoints
   * modal in CustomerDetail. Server-side applies the loyalty config's
   * service-type multipliers (HTHD: 1.5x grooming).
   *
   * Served by buildAdminLoyaltyRouter (w2.b) — POST /api/admin/points/add.
   */
  add: (
    apiUrl: string,
    data: AddPointsRequest,
  ): Promise<ApiResponse<AddPointsResponse>> =>
    api.post<AddPointsResponse>(apiUrl, '/api/admin/points/add', data),
};

// ─── adminLoyaltyConfigApi (#524) ──────────────────────────────────────
// Tenant-configurable point economics: the SINGLE source of truth for the
// checkout redemption leg and the desk redemption tiers.

export interface LoyaltyConfigTier {
  points: number;
  discount_cents: number;
}

export interface LoyaltyConfigResponse {
  redemption_value_cents_per_point: number;
  earn_rate_points_per_dollar: number;
  /** Rungs with dollars DERIVED server-side from the value (one source). */
  reward_tiers: LoyaltyConfigTier[];
  example: LoyaltyConfigTier;
}

export interface LoyaltyConfigUpdateRequest {
  redemptionValueCentsPerPoint: number;
  earnRatePointsPerDollar: number;
}

export const adminLoyaltyConfigApi = {
  /** GET /api/admin/loyalty-config — business-config:read:all (owner/manager). */
  get: (apiUrl: string): Promise<ApiResponse<LoyaltyConfigResponse>> =>
    api.get<LoyaltyConfigResponse>(apiUrl, '/api/admin/loyalty-config'),
  /** PUT /api/admin/loyalty-config — business-config.pricing:write:all (owner). */
  update: (
    apiUrl: string,
    data: LoyaltyConfigUpdateRequest,
  ): Promise<ApiResponse<LoyaltyConfigResponse>> =>
    api.put<LoyaltyConfigResponse>(apiUrl, '/api/admin/loyalty-config', data),
};

// ─── adminRedemptionsApi ───────────────────────────────────────────────

export interface RedemptionLookupResponse {
  id: string;
  redemption_code: string;
  reward_tier: number;
  discount_value: number;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  approved_at: string | null;
  customer: {
    id: string;
    name: string;
    phone: string;
    email: string;
    points_balance: number;
  };
}

export interface CompleteRedemptionResponse {
  success: boolean;
  message: string;
  redemption: {
    id: string;
    redemption_code: string;
    reward_tier: number;
    discount_value: number;
    status: string;
    approved_at: string;
  };
  customer: {
    id: string;
    name: string;
    previous_balance: number;
    new_balance: number;
  };
  discount_to_apply: number;
}

export interface CreateRedemptionRequest {
  customer_id: string;
  reward_tier: '100' | '250' | '500';
}

export interface CreateRedemptionResponse {
  success: boolean;
  message: string;
  redemption: {
    id: string;
    redemption_code: string;
    reward_tier: number;
    discount_value: number;
    status: string;
    approved_at: string;
  };
  customer: {
    id: string;
    name: string;
    previous_balance: number;
    new_balance: number;
  };
  discount_to_apply: number;
}

export const adminRedemptionsApi = {
  /** Served by buildAdminLoyaltyRouter (w2.b) — GET /api/admin/redemptions/lookup. */
  lookup: (
    apiUrl: string,
    code: string,
  ): Promise<ApiResponse<RedemptionLookupResponse>> =>
    api.get<RedemptionLookupResponse>(
      apiUrl,
      `/api/admin/redemptions/lookup?code=${encodeURIComponent(code)}`,
    ),
  /** @todo Server endpoint pending W0.6. */
  complete: (
    apiUrl: string,
    redemption_code: string,
  ): Promise<ApiResponse<CompleteRedemptionResponse>> =>
    api.post<CompleteRedemptionResponse>(
      apiUrl,
      '/api/admin/redemptions/complete',
      { redemption_code },
    ),
  /** @todo Server endpoint pending W0.6. */
  create: (
    apiUrl: string,
    data: CreateRedemptionRequest,
  ): Promise<ApiResponse<CreateRedemptionResponse>> =>
    api.post<CreateRedemptionResponse>(apiUrl, '/api/admin/redemptions/create', data),
};

// ─── adminBookingApi (V2) ─────────────────────────────────────────────
// HTHD's V2 admin booking surface — richer than the existing
// `fetchAdminSchedule` / `checkInBooking` helpers above which target
// the platform's `/api/bookings/admin/...` shape. This namespace
// targets `/api/v2/admin/bookings/...` matching HTHD verbatim.

export interface ServiceType {
  id: string;
  name: string;
  displayName: string;
  basePriceCents: number;
  durationMinutes: number | null;
  isActive: boolean;
  sortOrder: number;
}

export interface BookingDog {
  id: string;
  bookingId: string;
  dogId: string;
  conditionRating: number | null;
  conditionPhoto: string | null;
  quotedPriceCents: number | null;
  notes: string | null;
  dog: {
    id: string;
    name: string;
    breed: string | null;
    sizeCategory: string | null;
  };
}

/**
 * V2 admin booking shape — note the camelCase fields and richer dog +
 * customer nested objects vs the existing snake_case `AdminBooking`
 * type used by Bookings.tsx / Dashboard.tsx. New ports use this; the
 * older type stays for back-compat.
 */
export interface AdminBookingV2 {
  id: string;
  customerId: string;
  serviceTypeId: string;
  date: string;
  endDate?: string;
  startTime: string | null;
  // Becky-T2.7 — same optional time windows the V1 AdminBooking carries.
  dropOffTime?: string | null;
  pickUpTime?: string | null;
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  totalCents: number;
  notes: string | null;
  createdAt: string;
  serviceType: ServiceType;
  dogs: BookingDog[];
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface ScheduleResponse {
  bookings: AdminBookingV2[];
  total: number;
}

export interface ServiceTypesResponse {
  serviceTypes: ServiceType[];
}

export interface AvailabilityDay {
  date: string;
  available: boolean;
  spotsRemaining: number;
  totalCapacity: number;
}

export interface AvailabilityResponse {
  availability: AvailabilityDay[];
}

export interface CreateBookingRequest {
  customerId: string;
  serviceTypeId: string;
  dogIds: string[];
  date: string;
  startTime?: string;
  notes?: string;
}

export const adminBookingApi = {
  /** @todo Server endpoint pending W0.6 — `/api/v2/admin/bookings/service-types`. */
  getServiceTypes: (apiUrl: string): Promise<ApiResponse<ServiceTypesResponse>> =>
    api.get<ServiceTypesResponse>(apiUrl, '/api/v2/admin/bookings/service-types'),
  /** @todo Server endpoint pending W0.6. */
  getSchedule: (
    apiUrl: string,
    date: string,
    serviceType?: string,
  ): Promise<ApiResponse<ScheduleResponse>> => {
    const params = new URLSearchParams({ date });
    if (serviceType) params.set('serviceType', serviceType);
    return api.get<ScheduleResponse>(
      apiUrl,
      `/api/v2/admin/bookings/schedule?${params}`,
    );
  },
  /**
   * @deprecated DEAD — `/api/v2/admin/bookings/:id/confirm` is not mounted on
   * this server (the admin booking router is `/api/bookings/admin`), and this
   * helper has never had a caller. Use the module-level `confirmBooking`
   * above, which posts to the route that exists. Kept only so the rest of
   * this V2 block stays one shape; delete with the block. (Ted #26, #987.)
   */
  confirmBooking: (
    apiUrl: string,
    bookingId: string,
  ): Promise<ApiResponse<{ booking: AdminBookingV2 }>> =>
    api.post<{ booking: AdminBookingV2 }>(
      apiUrl,
      `/api/v2/admin/bookings/${bookingId}/confirm`,
      {},
    ),
  /** @todo Server endpoint pending W0.6. */
  checkIn: (
    apiUrl: string,
    bookingId: string,
  ): Promise<ApiResponse<{ booking: AdminBookingV2 }>> =>
    api.post<{ booking: AdminBookingV2 }>(
      apiUrl,
      `/api/v2/admin/bookings/${bookingId}/check-in`,
      {},
    ),
  /** @todo Server endpoint pending W0.6. */
  checkOut: (
    apiUrl: string,
    bookingId: string,
  ): Promise<ApiResponse<{ booking: AdminBookingV2 }>> =>
    api.post<{ booking: AdminBookingV2 }>(
      apiUrl,
      `/api/v2/admin/bookings/${bookingId}/check-out`,
      {},
    ),
  /**
   * Ted #32c / E3.2 (#1158) — WIRED. Mark an expected reservation a no-show:
   * POST /api/bookings/admin/:id/no-show { reason } (admin-bookings-no-show.ts;
   * the old `/api/v2/admin/bookings/...` path was never mounted). Only a dog
   * that has not arrived, never before its first day; no money moves. The
   * body is the V1 `AdminBooking` wire shape, like every other desk lifecycle
   * route — callers go through `markBookingNoShow` (components/booking/no-show.ts).
   */
  markNoShow: (
    apiUrl: string,
    bookingId: string,
    reason: string,
  ): Promise<ApiResponse<{ booking: AdminBooking }>> =>
    api.post<{ booking: AdminBooking }>(
      apiUrl,
      `/api/bookings/admin/${encodeURIComponent(bookingId)}/no-show`,
      { reason },
    ),
  /** @todo Server endpoint pending W0.6. */
  create: (
    apiUrl: string,
    data: CreateBookingRequest,
  ): Promise<ApiResponse<{ booking: AdminBookingV2 }>> =>
    api.post<{ booking: AdminBookingV2 }>(apiUrl, '/api/v2/admin/bookings', data),
  /** @todo Server endpoint pending W0.6. */
  checkAvailability: (
    apiUrl: string,
    serviceTypeId: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<AvailabilityResponse>> =>
    api.get<AvailabilityResponse>(
      apiUrl,
      `/api/v2/admin/bookings/availability?serviceTypeId=${serviceTypeId}&startDate=${startDate}&endDate=${endDate}`,
    ),
};

// ─── adminGroomingApi ──────────────────────────────────────────────────

export interface GroomingPriceTier {
  id: string;
  sizeCategory: 'small' | 'medium' | 'large' | 'xl';
  conditionRating: number;
  label: string;
  estimatedMinutes: number;
  priceCents: number;
  isActive: boolean;
}

export interface GroomingMatrixResponse {
  matrix: GroomingPriceTier[];
}

export interface RateConditionResponse {
  bookingDog: BookingDog;
  priceTier: GroomingPriceTier;
}

export const adminGroomingApi = {
  /** @todo Server endpoint pending W0.6. */
  rateCondition: (
    apiUrl: string,
    bookingDogId: string,
    conditionRating: number,
  ): Promise<ApiResponse<RateConditionResponse>> =>
    api.post<RateConditionResponse>(
      apiUrl,
      `/api/v2/grooming/rate/${bookingDogId}`,
      { conditionRating },
    ),
  /** @todo Server endpoint pending W0.6. */
  getMatrix: (apiUrl: string): Promise<ApiResponse<GroomingMatrixResponse>> =>
    api.get<GroomingMatrixResponse>(apiUrl, '/api/v2/grooming/matrix'),
  /** @todo Server endpoint pending W0.6. */
  updatePrice: (
    apiUrl: string,
    tierId: string,
    data: { priceCents?: number; estimatedMinutes?: number },
  ): Promise<ApiResponse<GroomingPriceTier>> =>
    api.put<GroomingPriceTier>(apiUrl, `/api/v2/grooming/matrix/${tierId}`, data),
  // Ted #1208 — the grooming SERVICES' size prices and the ADD-ONS are
  // edited through `adminGroomingMenuApi` (Settings → Services & prices →
  // Grooming menu, /api/v2/admin/tenant/grooming-menu). The stubs that used
  // to sit here pointed at /api/v2/admin/grooming/{prices,add-ons}, which
  // never existed (#1019) and had no callers.
};

// ─── adminStaffApi ─────────────────────────────────────────────────────

export interface AdminStaffUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  /**
   * Primary role slug. Free string: the server returns any of the 7
   * canonical tenant roles plus legacy values ('staff'/'admin') on old
   * rows — the old 5-value union here was stale and lied about that.
   */
  role: string;
  /**
   * #428 — additive role grants (never includes the primary). Optional
   * for older server responses; treat absent as [].
   */
  granted_roles?: string[];
  is_active: boolean;
  created_at: string;
  /** Null until StaffUser → TenantUser migration ships (R-15). */
  last_login_at?: string | null;
}

export interface AdminStaffListResponse {
  staff: AdminStaffUser[];
}

export interface AdminStaffInviteResponse {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_slug: string;
  status: string;
  invite_sent_at: string;
  invite_expires_at: string;
  // #788 — the send the server just made, as the provider reported it.
  last_send_at?: string | null;
  last_send_outcome?: string | null;
  last_send_provider_message_id?: string | null;
}

/**
 * A5 — a pending (status='invited') TenantUser row. `invite_expires_at`
 * is derived server-side from inviteSentAt + 7 days so the Staff page
 * can render an honest Invited-vs-Expired state.
 */
export interface AdminPendingInvite {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_slug: string;
  status: string;
  invite_sent_at: string | null;
  invite_expires_at: string | null;
  // #788 — latest send attempt for this invite, from transactional_sends.
  // Null for invites sent before send-recording existed.
  last_send_at: string | null;
  last_send_outcome: string | null;
  last_send_provider_message_id: string | null;
}

export interface AdminPendingInvitesResponse {
  invites: AdminPendingInvite[];
}

/** #666 — response from revoking a pending invite. */
export interface AdminRevokedInviteResponse {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_slug: string;
  status: string;
  revoked: boolean;
}

export const adminStaffApi = {
  getStaff: (apiUrl: string): Promise<ApiResponse<AdminStaffListResponse>> =>
    api.get<AdminStaffListResponse>(apiUrl, '/api/v2/admin/staff'),
  createStaff: (
    apiUrl: string,
    data: {
      username: string;
      password: string;
      firstName: string;
      lastName: string;
      role?: string;
    },
  ): Promise<ApiResponse<AdminStaffUser>> =>
    api.post<AdminStaffUser>(apiUrl, '/api/v2/admin/staff', data),
  /**
   * Issue #31 — invite-based add-staff flow. Server creates a
   * TenantUser (status=invited) and dispatches a magic-link email.
   * The accept flow already bridges TenantUser → StaffUser on first
   * password set, so login works as soon as the invitee clicks
   * through.
   */
  inviteStaff: (
    apiUrl: string,
    data: {
      email: string;
      firstName: string;
      lastName: string;
      role?: string;
    },
  ): Promise<ApiResponse<AdminStaffInviteResponse>> =>
    api.post<AdminStaffInviteResponse>(
      apiUrl,
      '/api/v2/admin/staff/invite',
      data,
    ),
  /** A5 — pending (not-yet-accepted) invites for the caller's tenant. */
  getPendingInvites: (
    apiUrl: string,
  ): Promise<ApiResponse<AdminPendingInvitesResponse>> =>
    api.get<AdminPendingInvitesResponse>(apiUrl, '/api/v2/admin/staff/invite'),
  /**
   * A5 — reissue token + expiry for an invited member and resend the
   * branded email. 409 when already accepted.
   */
  resendInvite: (
    apiUrl: string,
    email: string,
  ): Promise<ApiResponse<AdminStaffInviteResponse>> =>
    api.post<AdminStaffInviteResponse>(
      apiUrl,
      '/api/v2/admin/staff/invite/resend',
      { email },
    ),
  /**
   * #666 — kill a pending invite. Nulls the token server-side, so the
   * emailed magic link stops working immediately.
   *
   * Scoped by invite ID, not email, because that is what identifies the
   * exact credential to destroy. (Resend is by email because it targets
   * "the invite for this person"; for a MISTYPED address resending just
   * mails the stranger a fresh owner-access link — rotation is not
   * revocation, which is the whole reason this endpoint exists.)
   */
  revokeInvite: (
    apiUrl: string,
    inviteId: string,
  ): Promise<ApiResponse<AdminRevokedInviteResponse>> =>
    api.post<AdminRevokedInviteResponse>(
      apiUrl,
      `/api/v2/admin/staff/invite/${inviteId}/revoke`,
      {},
    ),
  updateRole: (
    apiUrl: string,
    staffId: string,
    role: string,
  ): Promise<ApiResponse<AdminStaffUser>> =>
    api.put<AdminStaffUser>(apiUrl, `/api/v2/admin/staff/${staffId}/role`, { role }),
  /**
   * Issue #31 — activate/deactivate. Server flips isActive and (on
   * deactivate) bumps sessionInvalidatedAt so any outstanding staff
   * JWT is rejected on its next request.
   */
  setActive: (
    apiUrl: string,
    staffId: string,
    isActive: boolean,
  ): Promise<ApiResponse<AdminStaffUser>> =>
    api.patch<AdminStaffUser>(apiUrl, `/api/v2/admin/staff/${staffId}`, {
      isActive,
    }),
  /**
   * #428 — additive multi-role grants (owner-only). Primary role +
   * grant list for one staff member.
   */
  getRoles: (
    apiUrl: string,
    staffId: string,
  ): Promise<ApiResponse<AdminStaffRolesResponse>> =>
    api.get<AdminStaffRolesResponse>(
      apiUrl,
      `/api/v2/admin/staff/${staffId}/roles`,
    ),
  /**
   * #428 — grant an additional role. Server answers 409 on duplicate /
   * primary-role grants, 400 on invalid or non-grantable slugs, and
   * bumps the target's sessionInvalidatedAt (their next request
   * re-logs-in with the widened roles claim).
   */
  grantRole: (
    apiUrl: string,
    staffId: string,
    roleSlug: string,
  ): Promise<ApiResponse<AdminStaffRoleGrant & { staff_id: string }>> =>
    api.post<AdminStaffRoleGrant & { staff_id: string }>(
      apiUrl,
      `/api/v2/admin/staff/${staffId}/roles`,
      { roleSlug },
    ),
  /** #428 — revoke a granted role (also bumps sessionInvalidatedAt). */
  revokeRole: (
    apiUrl: string,
    staffId: string,
    roleSlug: string,
  ): Promise<ApiResponse<{ staff_id: string; role_slug: string; revoked: boolean }>> =>
    api.delete<{ staff_id: string; role_slug: string; revoked: boolean }>(
      apiUrl,
      `/api/v2/admin/staff/${staffId}/roles/${roleSlug}`,
    ),
};

/** #428 — one additive role grant as the server serializes it. */
export interface AdminStaffRoleGrant {
  role_slug: string;
  granted_by_staff_id: string | null;
  created_at: string;
}

/** #428 — GET /api/v2/admin/staff/:id/roles response. */
export interface AdminStaffRolesResponse {
  staff_id: string;
  primary_role: string;
  grants: AdminStaffRoleGrant[];
}

// ─── adminBundleApi ────────────────────────────────────────────────────

export interface ServiceBundleItem {
  id: string;
  serviceType: {
    id: string;
    name: string;
    displayName: string;
    basePriceCents: number;
  };
}

export interface ServiceBundle {
  id: string;
  name: string;
  description: string | null;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  isActive: boolean;
  sortOrder: number;
  items: ServiceBundleItem[];
  createdAt: string;
}

export interface BundlesResponse {
  bundles: ServiceBundle[];
}

export const adminBundleApi = {
  /** @todo Server endpoint pending W0.6. */
  list: (apiUrl: string): Promise<ApiResponse<BundlesResponse>> =>
    api.get<BundlesResponse>(apiUrl, '/api/v2/bundles'),
  /** @todo Server endpoint pending W0.6. */
  create: (
    apiUrl: string,
    data: {
      name: string;
      description?: string;
      discountType: string;
      discountValue: number;
      serviceTypeIds: string[];
      sortOrder?: number;
    },
  ): Promise<ApiResponse<ServiceBundle>> =>
    api.post<ServiceBundle>(apiUrl, '/api/v2/bundles', data),
  /** @todo Server endpoint pending W0.6. */
  update: (
    apiUrl: string,
    bundleId: string,
    data: Partial<{
      name: string;
      description: string;
      discountType: string;
      discountValue: number;
      serviceTypeIds: string[];
      sortOrder: number;
    }>,
  ): Promise<ApiResponse<ServiceBundle>> =>
    api.put<ServiceBundle>(apiUrl, `/api/v2/bundles/${bundleId}`, data),
  /**
   * Toggle the active flag on a bundle. HTHD overloads DELETE for this
   * (server-side soft-delete flips isActive); we mirror that contract.
   * @todo Server endpoint pending W0.6.
   */
  toggle: (apiUrl: string, bundleId: string): Promise<ApiResponse<ServiceBundle>> =>
    api.delete<ServiceBundle>(apiUrl, `/api/v2/bundles/${bundleId}`),
};

// ─── adminCheckoutApi ──────────────────────────────────────────────────


// Ted #7 (#910) — the `adminCheckoutApi` stub that lived here
// (getWalletBalance → /api/v2/admin/checkout/wallet-balance/:id, processPayment
// → /api/v2/admin/checkout/process) was DELETED: neither path was ever
// mounted on the server, nothing called them, and the store-credit balance
// now rides `/api/admin/dashboard/today` rows (`walletBalanceCents`) and the
// cart read (`adminPaymentsApi.getCart`). Applying credit is a settle tender.

// ─── adminScheduleApi (V2): DELETED (#665 PR-B) ───────────────────────
// The namespace targeted `/api/v2/admin/schedules/*` — 11 methods against
// endpoints that were NEVER mounted (spec §1 "dead client namespace…
// delete or adopt"). The real schedule surface is `fetchStaffWeek` /
// `createStaffShift` / `updateStaffShift` / `deleteStaffShift` /
// `publishStaffSchedule` against `/api/admin/staff-schedules` above.

// ─── adminDogApi ───────────────────────────────────────────────────────
//
// @todo tighten — return types are `any` to mirror HTHD; harden once
//       the dogs/vaccinations modules' server contracts firm up.

export const adminDogApi = {
  /** @todo Server endpoint pending W0.6. */
  getDog: (apiUrl: string, dogId: string): Promise<ApiResponse<any>> =>
    api.get<any>(apiUrl, `/api/v2/admin/dogs/${dogId}`),
  /** @todo Server endpoint pending W0.6. */
  addBehaviorNote: (
    apiUrl: string,
    dogId: string,
    data: any,
  ): Promise<ApiResponse<any>> =>
    api.post<any>(apiUrl, `/api/v2/admin/dogs/${dogId}/behavior-notes`, data),
  /** @todo Server endpoint pending W0.6. */
  verifyVaccination: (
    apiUrl: string,
    dogId: string,
    vaccinationId: string,
  ): Promise<ApiResponse<any>> =>
    api.put<any>(
      apiUrl,
      `/api/v2/admin/dogs/${dogId}/vaccinations/${vaccinationId}/verify`,
      {},
    ),
  /** @todo Server endpoint pending W0.6. */
  getComplianceReport: (apiUrl: string): Promise<ApiResponse<any>> =>
    api.get<any>(apiUrl, '/api/v2/admin/dogs/compliance/report'),
};

// ─── adminReportCardApi (V2) ──────────────────────────────────────────
// HTHD's V2 report-card admin surface — distinct from the existing
// `fetchReportCards` / `createReportCard` helpers above which target
// `/api/admin/report-cards`. This namespace targets HTHD's
// `/api/v2/admin/report-cards/...` shape with the bake-in normalization
// fix from HTHD's commit `2eb6bc8` (always handle undefined on
// optional fields downstream — see notes/photos in the UI).
//
// @todo tighten — return types are `any` to mirror HTHD; harden once
//       the report-cards module's contracts firm up.

export const adminReportCardApi = {
  /** @todo Server endpoint pending W0.6. */
  create: (apiUrl: string, data: any): Promise<ApiResponse<any>> =>
    api.post<any>(apiUrl, '/api/v2/admin/report-cards', data),
  /** @todo Server endpoint pending W0.6. */
  update: (apiUrl: string, id: string, data: any): Promise<ApiResponse<any>> =>
    api.put<any>(apiUrl, `/api/v2/admin/report-cards/${id}`, data),
  /** @todo Server endpoint pending W0.6. */
  delete: (apiUrl: string, id: string): Promise<ApiResponse<any>> =>
    api.delete<any>(apiUrl, `/api/v2/admin/report-cards/${id}`),
  /** @todo Server endpoint pending W0.6. */
  send: (apiUrl: string, id: string, channel: string): Promise<ApiResponse<any>> =>
    api.post<any>(apiUrl, `/api/v2/admin/report-cards/${id}/send`, { channel }),
  /** @todo Server endpoint pending W0.6. */
  getUnsent: (apiUrl: string): Promise<ApiResponse<any>> =>
    api.get<any>(apiUrl, '/api/v2/admin/report-cards/unsent'),
  /** @todo Server endpoint pending W0.6. */
  getByBooking: (apiUrl: string, bookingId: string): Promise<ApiResponse<any>> =>
    api.get<any>(apiUrl, `/api/v2/admin/report-cards/booking/${bookingId}`),
};

// ─── adminMessagingApi ─────────────────────────────────────────────────
//
// @todo tighten — return types are `any` to mirror HTHD; harden once
//       the messaging module's contracts firm up.

export const adminMessagingApi = {
  /** @todo Server endpoint pending W0.6. */
  getConversations: (apiUrl: string, status?: string): Promise<ApiResponse<any>> =>
    api.get<any>(
      apiUrl,
      `/api/v2/admin/messaging/conversations${status ? `?status=${status}` : ''}`,
    ),
  /** @todo Server endpoint pending W0.6. */
  getConversation: (apiUrl: string, id: string): Promise<ApiResponse<any>> =>
    api.get<any>(apiUrl, `/api/v2/admin/messaging/conversations/${id}`),
  /** @todo Server endpoint pending W0.6. */
  assignStaff: (
    apiUrl: string,
    id: string,
    staffUserId?: string,
  ): Promise<ApiResponse<any>> =>
    api.post<any>(
      apiUrl,
      `/api/v2/admin/messaging/conversations/${id}/assign`,
      staffUserId ? { staffUserId } : {},
    ),
  /**
   * Never retried (Woofers Phase 2): on a text thread this sends a real text,
   * and a retry after a slow Twilio answer would text the client twice. A
   * failure restores the draft; staff tap Send again.
   */
  sendMessage: (
    apiUrl: string,
    id: string,
    content: string,
  ): Promise<ApiResponse<any>> =>
    request<any>(
      apiUrl,
      `/api/v2/admin/messaging/conversations/${id}/messages`,
      { method: 'POST', body: JSON.stringify({ content }) },
      { maxRetries: 0 },
    ),
  /** @todo Server endpoint pending W0.6. */
  escalate: (apiUrl: string, id: string): Promise<ApiResponse<any>> =>
    api.post<any>(apiUrl, `/api/v2/admin/messaging/conversations/${id}/escalate`, {}),
  /** @todo Server endpoint pending W0.6. */
  deEscalate: (apiUrl: string, id: string): Promise<ApiResponse<any>> =>
    api.post<any>(
      apiUrl,
      `/api/v2/admin/messaging/conversations/${id}/de-escalate`,
      {},
    ),
};

// ─── adminAnalyticsApi (W2.D) ─────────────────────────────────────────────
// Dashboard insights, revenue chart, and segment cards.

export interface AimInsight {
  id: string;
  type: string;
  title: string;
  summary: string;
  generatedAt: string;
}

export interface RevenueSnapshot {
  today: number;
  yesterday: number;
  todayChange: number;
  thisWeek: number;
  lastWeek: number;
  weekChange: number;
  thisMonth: number;
  lastMonth: number;
  monthChange: number;
}

export interface CustomerSegments {
  total: number;
  new: number;
  active: number;
  atRisk: number;
  churned: number;
}

export const adminAnalyticsApi = {
  /** @todo Server endpoint: GET /api/v2/admin/analytics/revenue */
  getRevenue: (apiUrl: string): Promise<ApiResponse<RevenueSnapshot>> =>
    api.get<RevenueSnapshot>(apiUrl, '/api/v2/admin/analytics/revenue'),
  /** @todo Server endpoint: GET /api/v2/admin/analytics/segments */
  getSegments: (apiUrl: string): Promise<ApiResponse<CustomerSegments>> =>
    api.get<CustomerSegments>(apiUrl, '/api/v2/admin/analytics/segments'),
  /** @todo Server endpoint: GET /api/v2/admin/analytics/insights */
  getInsights: (apiUrl: string): Promise<ApiResponse<{ insights: AimInsight[] }>> =>
    api.get<{ insights: AimInsight[] }>(apiUrl, '/api/v2/admin/analytics/insights'),
  /** @todo Server endpoint: POST /api/v2/admin/analytics/insights/generate */
  generateInsights: (apiUrl: string): Promise<ApiResponse<{ ok: boolean }>> =>
    api.post<{ ok: boolean }>(apiUrl, '/api/v2/admin/analytics/insights/generate'),
};

// ─── Tenant Stripe Connect (post-provisioning) ─────────────────────────
// Pairs with apps/server/src/routes/tenant-stripe-connect.ts. Tenant is
// resolved server-side from the request host (Stage 1.5 middleware), so
// the admin-app passes no tenantId — it implicitly targets whichever
// tenant the user is logged into.

export interface TenantStripeStatusResponse {
  ok: true;
  tenantId: string;
  slug: string;
  /**
   * #243 — the tenant's configured payment gateway. 'authorizenet'
   * tenants (e.g. HTHD) report the Stripe fields below as null/false;
   * `null` means the TenantConfig carries an unrecognized gateway value.
   */
  gateway?: 'stripe' | 'authorizenet' | null;
  /**
   * #243 — stripe: a Connect account is attached; authorizenet: the
   * sealed credential envelope is present.
   */
  gatewayConfigured?: boolean;
  /**
   * #492 — the resolved execution mode for this tenant's gateway
   * ('live' | 'test' | 'simulation'). For a stripe tenant this is the
   * deployment Stripe mode; the Payments page HIDES the Stripe Connect tab
   * when it is 'simulation' so a sim-gateway tenant (Switchback) never sees a
   * real onboarding surface. Optional for back-compat with an un-upgraded
   * server (treated as 'simulation' → hidden, the safe default).
   */
  gatewayMode?: 'live' | 'test' | 'simulation';
  stripeAccountId: string | null;
  stripeAccountStatus:
    | 'onboarding'
    | 'active'
    | 'restricted'
    | 'verification_required'
    | 'disconnected'
    | string
    | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  updatedAt: string | null;
}

export interface TenantStripeAccountLinkResponse {
  ok: true;
  /** Stripe-hosted onboarding URL — caller redirects window.location.href to it. */
  url: string;
  /** ISO-8601. Stripe Account Links expire within minutes. */
  expiresAt: string;
  stripeAccountId: string;
  /** True if this call created a new Connect account; false if it reused one. */
  accountCreated: boolean;
}

export const adminTenantStripeApi = {
  getStatus: (
    apiUrl: string,
  ): Promise<ApiResponse<TenantStripeStatusResponse>> =>
    api.get<TenantStripeStatusResponse>(
      apiUrl,
      '/api/tenant/stripe-connect/status',
    ),
  createAccountLink: (
    apiUrl: string,
    returnUrl: string,
    refreshUrl?: string,
  ): Promise<ApiResponse<TenantStripeAccountLinkResponse>> =>
    api.post<TenantStripeAccountLinkResponse>(
      apiUrl,
      '/api/tenant/stripe-connect/account-link',
      { returnUrl, ...(refreshUrl ? { refreshUrl } : {}) },
    ),
};

// ─── adminPaymentsApi (Issue #29 — Becky payment surface) ─────────────────
// Staff-driven payment processing for the admin app. Targets the V2
// admin checkout module (matches adminCheckoutApi.processPayment for
// the actual settlement leg) plus payment-method enumeration,
// terminal-connection bootstrap, and an "open invoices" queue.
//
// Server endpoints land in @ferro/core's admin-payments module under
// `/api/v2/admin/payments/*`. They are coordinated with night-stripe-server
// (issues #19/#20 closed) and the night-becky-checkout settle-later track.

export interface AdminSavedCard {
  id: string;
  brand: string; // 'visa', 'mastercard', 'amex', etc.
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault?: boolean;
}

export interface AdminOpenInvoice {
  id: string; // server-generated invoice id
  bookingId: string;
  customerId: string;
  customerName: string;
  customerPhone: string | null;
  serviceTypeName: string;
  serviceDate: string; // ISO date
  totalCents: number;
  createdAt: string;
  notes: string | null;
  /** Lifecycle state — `open` until settled (issue #140 server-of-record). */
  status: 'open' | 'settled' | 'voided';
  /**
   * Ted #23 (#984) — the add-on catalog name when this line is a SERVICE LEG
   * ("Nail Trim"), null/absent on an ordinary visit line. Printed INSTEAD of
   * `serviceTypeName` so the checkout itemizes: Ted's complaint was that the
   * add-ons had "no dollar amounts" and were not "clearly listed", and a
   * second line reading "Grooming" would not have fixed that. Optional for
   * deploy skew — an older server simply omits it.
   */
  serviceLegLabel?: string | null;
  /** #984 — the visit this line hangs off, when it is a leg. */
  parentBookingId?: string | null;
  /**
   * Ted #29 (#1108) — who performed the service on this line ("Ted Gualtier"),
   * null when nobody is recorded. Optional for deploy skew: an older server
   * omits it and the line prints without a name.
   */
  performedBy?: string | null;
  /** #813 — dogs on the invoice's booking (cart line detail). Optional for deploy skew. */
  dogNames?: string[];
  /** #813 — the Payment that covered this invoice. Optional for deploy skew. */
  settledPaymentId?: string | null;
  /** Ted #3 + #9 (#906 #912) — the amount due before any adjustment. Optional for deploy skew. */
  originalTotalCents?: number;
  /** Ted #3 + #9 — the price adjustments behind `totalCents`, oldest first. */
  adjustments?: AdminInvoiceAdjustment[];
}

/** Ted #3 + #9 (#906 #912) — one price adjustment on an open invoice. */
export interface AdminInvoiceAdjustment {
  id: string;
  /**
   * #926 — `meal_tracking` is the server's per-night line on a boarding stay.
   * #914 — `late_pickup` is the boarding-cutoff disposition: ONE row however
   * the desk resolved it (a charge carries the daycare rate as its delta; a
   * package day and a waiver carry 0, so the invoice still says what happened).
   * Ted #34 E6.2 (#1161) — `first_groom` is the owner's first-groom discount,
   * applied by the server (value = −percent); `first_groom_removed` is the
   * desk taking it back off with a reason (value 0).
   * #948 — `multi_dog` is the owner's multi-dog discount, applied by the
   * server when two or more of the family's dogs are here together (value =
   * −percent); `multi_dog_removed` is the desk taking it back off (value 0).
   */
  kind:
    | 'percent'
    | 'amount'
    | 'override'
    | 'package'
    | 'meal_tracking'
    | 'late_pickup'
    | 'first_groom'
    | 'first_groom_removed'
    | 'agreed_rate'
    | 'multi_dog'
    | 'multi_dog_removed';
  /** percent: whole percent, signed; amount: signed cents; override: the new total; package: 0; meal_tracking: the per-night rate; late_pickup: the daycare rate; agreed_rate (Ted #35): the new total, priced by the server from the client's agreement. */
  value: number;
  deltaCents: number;
  originalTotalCents: number;
  adjustedTotalCents: number;
  reason: string;
  staffUsername: string | null;
  createdAt: string;
}

/** Ted #3 + #9 — what the desk sends to adjust a price. */
export type AdminInvoiceAdjustmentInput = {
  kind: 'percent' | 'amount' | 'override';
  value: number;
  reason: string;
};

export interface AdminOpenInvoicesResponse {
  invoices: AdminOpenInvoice[];
  count: number;
}

/**
 * #812 — staff-selectable desk tenders for "mark as paid". Mirrors the
 * server's DESK_PAYMENT_METHODS (payments-admin/schema.ts). 'package' is
 * sent only by the $0 covered-settle leg, never staff-picked.
 */
export type AdminDeskPaymentMethod =
  | 'cash'
  | 'check'
  | 'external_terminal'
  | 'package'
  | 'other';

/**
 * Ted #7 (#910) — tenders a SETTLE accepts: the desk set plus
 * 'store_credit' (the owner's wallet covered the whole amount). A partial
 * credit names the tender for the remainder and sends `walletAmountCents`.
 */
export type AdminSettleTender = AdminDeskPaymentMethod | 'store_credit';

// ── Ted #35 (his Item 33; #1190 story 35.3, #948 story 1) — a client's agreed price ──

export type AdminRateAgreementBasis = 'per_dog' | 'per_household_day';

/** One saved deal on a customer's profile. Matches `rateAgreementView` in payments-admin/routes.ts. */
export interface AdminRateAgreement {
  id: string;
  customerId: string;
  serviceTypeId: string;
  serviceTypeName: string | null;
  serviceTypeDisplayName: string | null;
  /** The catalogue price beside the agreed one ("$70.00 (standard $85.00)"); null if the row is gone. */
  standardPriceCents: number | null;
  priceCents: number;
  basis: AdminRateAgreementBasis;
  /** null = any number of dogs; N = exactly N household dogs on this service that day. */
  dogCount: number | null;
  /** 0 = Sun … 6 = Sat; empty = any day. */
  weekdays: number[];
  note: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What the owner sends to save a deal. */
export interface AdminRateAgreementInput {
  serviceTypeId: string;
  priceCents: number;
  basis: AdminRateAgreementBasis;
  dogCount: number | null;
  weekdays: number[];
  note: string;
}

/** Why an agreement does not apply to an invoice — the dialog turns the code into a sentence. */
export type AdminAgreementWhyNot =
  | 'inactive'
  | 'wrong_service'
  | 'no_date'
  | 'wrong_weekday'
  | 'dog_count'
  | 'already_applied';

/** The cart read's judgement of one agreement against every invoice in the cart. */
export interface AdminCartAgreement {
  agreement: AdminRateAgreement;
  /** True when at least one invoice can take it right now. */
  applicable: boolean;
  invoices: Array<{
    invoiceId: string;
    applicable: boolean;
    amountCents: number | null;
    whyNot: AdminAgreementWhyNot | null;
  }>;
}

/** #813 cart read + Ted #7 (#910) store credit (null = server cannot say). */
export interface AdminCartResponse {
  invoices: AdminOpenInvoice[];
  totalCents: number;
  count: number;
  walletBalanceCents?: number | null;
  /** Ted #35 — the client's agreed prices, judged per invoice. Optional for deploy skew. */
  agreements?: AdminCartAgreement[];
}

/**
 * #251 should-fix 4 — a checked-out booking missing from EVERY money
 * surface (no completed Payment, no OpenInvoice). Served by
 * GET /api/v2/admin/payments/reconciliation/escaped-visits.
 */
export interface AdminEscapedVisit {
  bookingId: string;
  customerId: string;
  customerName: string;
  serviceTypeName: string;
  serviceDate: string;
  totalCents: number;
  checkedOutAt: string | null;
}

/**
 * #572 — an UNRESOLVED pending desk charge stub. A live desk charge whose
 * gateway outcome was indeterminate (timeout) or duplicate left a KEPT
 * pending Payment row; it never appears in Transactions and no charge/refund
 * endpoint can resolve it, so the booking is stranded. Served by
 * GET /api/v2/admin/payments/reconciliation/pending-stubs.
 */
export interface AdminPendingStub {
  paymentId: string;
  customerId: string;
  customerName: string;
  bookingId: string | null;
  serviceTypeName: string;
  serviceDate: string;
  totalCents: number;
  tipCents: number;
  gatewayRef: string | null;
  createdAt: string;
}

/** Outcome of resolving a pending desk stub (complete-if-charged / release). */
export interface AdminDeskStubResolution {
  paymentId: string;
  outcome: 'completed' | 'released';
  status: string;
  settledInvoices: number;
}

/**
 * A money-moved transaction on the desk Transactions tab. `settlement`,
 * `voidable`, and `refundable` are server-derived (the gateway is the
 * arbiter); the UI uses `settlement` to pick the primary action (Void for
 * 'unsettled', Refund for 'settled') and the booleans to gate which
 * actions are offered at all.
 */
export interface AdminTransaction {
  paymentId: string;
  customerId: string;
  customerName: string;
  totalCents: number;
  refundedCents: number;
  tipCents: number;
  status: string;
  /**
   * #491 — which reversal a `status === 'refunded'` row was: a 'voided'
   * charge moved no money, a 'refunded' one returned settled money. The
   * server derives it from refunded_cents (a void never touches it).
   * Optional for deploy-skew safety (frontends auto-deploy ahead of the
   * manual server deploy): when absent, the client re-derives it from
   * `status` + `refundedCents`, which are already on the wire.
   */
  reversal?: 'voided' | 'refunded' | null;
  settlement: 'settled' | 'unsettled';
  gatewayBacked: boolean;
  /**
   * Simulation-rail card charge (`sim_*` gateway ref) — a card
   * transaction on the simulation gateway, not a cash/wallet/redemption
   * settle. Optional for deploy-skew safety: frontends auto-deploy on
   * merge while the server deploys manually, so this field may be
   * absent until the server catches up (absent ⇒ treated as false —
   * the pre-existing rendering).
   */
  simulated?: boolean;
  refundable: boolean;
  voidable: boolean;
  /**
   * #812 — stored tender ('cash' | 'check' | 'external_terminal' |
   * 'package' | 'redemption' | 'card_gateway' | 'other'). Null =
   * unrecorded (pre-#812 rows / customer-checkout rows, whose label the
   * UI derives from gatewayBacked/simulated). Optional for deploy-skew
   * safety (frontends auto-deploy ahead of the manual server deploy).
   */
  paymentMethod?: string | null;
  // Sawyer 05 end-of-day-tenders (C4) — the check's number; null/absent = none
  // (absent on an older server).
  checkNumber?: string | null;
  bookingId: string | null;
  createdAt: string;
}

/**
 * GET /api/v2/admin/payments/transactions payload. `gatewayMode` is the
 * tenant's resolved gateway environment — 'simulation' lets the surface
 * show one honest Simulation badge and render sim rows as the card
 * charges they stand in for. Optional/null for deploy skew and for
 * resolution failures (both treated as live-strict rendering).
 */
export interface AdminTransactionList {
  transactions: AdminTransaction[];
  count: number;
  gatewayMode?: 'live' | 'test' | 'simulation' | null;
}

export interface AdminVoidResult {
  paymentId: string;
  voidTransactionId: string;
  paymentStatus: string;
  simulation: boolean;
}

export interface AdminRefundResult {
  paymentId: string;
  refundTransactionId: string;
  amountCents: number;
  paymentStatus: string;
  simulation: boolean;
}

/** Pre-paid day-package balance (issue #140 — server-of-record). */
/** Ted #5 (#908) — echoed by a redeem tied to a booking; null on a bare decrement. */
export interface AdminPackageRedemption {
  bookingId: string;
  days: number;
  remainingBefore: number;
  remainingAfter: number;
}

/**
 * #1157 — the package kinds the server accepts. 'daycare' is a FULL-day
 * daycare package; 'daycare_half' is a half-day one (Ted 10-06: different
 * pricing, separate balances). Mirrors `PACKAGE_SERVICE_KINDS` on the server.
 */
export type AdminPackageServiceKind = 'daycare' | 'daycare_half' | 'boarding' | 'grooming';

export interface AdminCustomerPackage {
  id: string;
  customerId: string;
  serviceKind: AdminPackageServiceKind;
  totalDays: number;
  remainingDays: number;
  label: string | null;
  /** Staff id who sold/granted the package. */
  soldBy: string | null;
  /** #815 — what the customer paid; null/absent = grant or legacy row. */
  priceCents?: number | null;
  /** #815 — the Payment that collected the sale. */
  salePaymentId?: string | null;
  purchasedAt: string;
  /**
   * #1157 E2.6 — the last facility day the package can be used, `YYYY-MM-DD`;
   * null/absent = never expires (or an older server).
   */
  expiresOn?: string | null;
}

/**
 * #1157 E2.3 — one package as the settle dialog's "Use package" lists it:
 * `usable` = not expired and holds every day the visit needs.
 */
export interface AdminPackageCoverPackage extends AdminCustomerPackage {
  expired: boolean;
  usable: boolean;
}

/** #1157 E2.3 — what "Use package" would do for an open invoice (a READ). */
export interface AdminPackageCoverOptions {
  invoiceId: string;
  bookingId: string;
  serviceKind: AdminPackageServiceKind | null;
  /** One per dog on the booking — the server's count. */
  daysNeeded: number;
  amountDueCents: number;
  /** What stays due after the package (meal tracking, a late charge). */
  amountDueAfterCents: number;
  notApplicable:
    | null
    | 'invoice_not_open'
    | 'already_covered'
    | 'nothing_to_cover'
    | 'no_package_for_service'
    | 'service_leg';
  packages: AdminPackageCoverPackage[];
}

/**
 * Ted #1 (#904) — one row of a package's ledger: every change to its
 * remaining days. `checkout` = redeemed at the desk (bookingId = the visit);
 * `manual_deduct` / `manual_restore` = a correction from the customer profile
 * with a reason. Mirrors `packageLedgerView` in payments-admin/routes.ts.
 */
export type AdminPackageLedgerKind =
  | 'checkout'
  | 'manual_deduct'
  | 'manual_restore'
  /** #1157 E2.1 — the opening balance carried over from Gingr (migration script only). */
  | 'migration'
  /** #1157 E2.6 — days added with a reason (the package grows). */
  | 'manual_add'
  /** #1157 E2.6 — expiry set or cleared; `serviceDate` carries the new expiry. */
  | 'expiry_change';
export interface AdminPackageLedgerEntry {
  id: string;
  packageId: string;
  bookingId: string | null;
  kind: AdminPackageLedgerKind;
  /** Magnitude, always positive. */
  days: number;
  /** Signed effect on remaining days (−1 for a deduction, +1 for a restore). */
  deltaDays: number;
  remainingBefore: number;
  remainingAfter: number;
  /** The visit this row refers to, `YYYY-MM-DD`; null when unknown. */
  serviceDate: string | null;
  reason: string | null;
  staffUsername: string | null;
  createdAt: string;
}

/** Ted #1 (#904) — the body of a manual package correction. */
export interface AdminAdjustPackageInput {
  direction: 'deduct' | 'restore';
  days: number;
  /** `YYYY-MM-DD` — the visit the correction refers to (never in the future). */
  serviceDate: string;
  /** Optional: the visit, verified server-side against the package's customer. */
  bookingId?: string;
  reason: string;
}

/** A recorded grooming price-tier change (issue #140 — server-of-record, AC6). */
export interface AdminTierAuditEntry {
  id: string;
  bookingId: string;
  customerId: string;
  serviceTypeId: string;
  originalTier: string;
  finalTier: string;
  originalTierLabel: string;
  finalTierLabel: string;
  originalTotalCents: number;
  finalTotalCents: number;
  confirmedByStaffId: string | null;
  confirmedByStaffUsername: string | null;
  operatorNotes: string | null;
  confirmedAt: string;
  createdAt: string;
}

export interface AdminTerminalConnectionToken {
  /** Stripe Terminal connection token (from /v1/terminal/connection_tokens). */
  secret: string;
  /** ISO-8601 when this token expires (Stripe issues 1-hour tokens). */
  expiresAt: string;
  /** True when running in simulation/test mode — UI may show a label. */
  simulation: boolean;
}

export interface AdminTerminalPaymentIntent {
  paymentId: string;
  paymentIntentId: string;
  /** Status for the UI to poll/render: 'requires_payment_method', 'requires_capture', 'succeeded', 'failed'. */
  status: string;
  /** Server-recalculated total (cents). Never trust client-supplied total. */
  totalCents: number;
  simulation: boolean;
}

/**
 * #248 review fix — MONEY MUTATIONS NEVER TRANSPORT-RETRY.
 *
 * The cold-start wrapper auto-re-POSTs on 502/503/504 — but on the desk
 * money endpoints 504 is `charge_indeterminate` ("the gateway MAY have
 * charged — never blind-retry"), so an automatic identical re-POST is a
 * fresh gateway charge attempt with only AuthNet's duplicateWindow
 * standing between the customer and a double charge. Every helper that
 * charges/refunds/voids/settles (or mints money-adjacent rows whose
 * duplication corrupts the ledger: open invoices, package sales/redeems)
 * goes through these no-retry verbs instead of `api.post`/`api.patch`.
 * Failures surface immediately; the STAFF decides whether to retry, and
 * the server's idempotency-key + pending-stub machinery absorbs the
 * deliberate ones. Future refund/void helpers MUST use these too.
 */
const moneyPost = <T>(
  apiUrl: string,
  endpoint: string,
  body?: unknown,
): Promise<ApiResponse<T>> =>
  request<T>(
    apiUrl,
    endpoint,
    {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    { maxRetries: 0 },
  );

const moneyPatch = <T>(
  apiUrl: string,
  endpoint: string,
  body?: unknown,
): Promise<ApiResponse<T>> =>
  request<T>(
    apiUrl,
    endpoint,
    {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    { maxRetries: 0 },
  );

/**
 * #522 — staff-authed gateway discovery for the desk. Mirrors the shapes
 * of GET /api/v2/admin/payments/gateway-config: the authorizenet+
 * configured arm carries the BROWSER-PUBLIC Accept.js bootstrap values
 * (never transactionKey/signatureKey — pinned server-side).
 */
export type AdminGatewayConfig =
  | { gateway: null; configured: false }
  | { gateway: 'stripe'; configured: true; mode: 'live' | 'test' | 'simulation' }
  | { gateway: 'authorizenet'; configured: false }
  | {
      gateway: 'authorizenet';
      configured: true;
      /** Accept.js authData casing is `apiLoginID` (capital ID). */
      apiLoginID: string;
      publicClientKey: string;
      mode: 'sandbox' | 'production';
    };

/**
 * Parse-don't-trust narrowing for the keyed-entry form: returns the
 * fully-usable authorizenet bootstrap or null for every other/partial
 * shape (a deploy-skew body missing a key must read as not-configured,
 * never as a half-bootable form).
 */
export function asAdminAcceptJsConfig(
  cfg: AdminGatewayConfig | undefined,
): Extract<AdminGatewayConfig, { gateway: 'authorizenet'; configured: true }> | null {
  if (
    cfg &&
    cfg.gateway === 'authorizenet' &&
    cfg.configured === true &&
    typeof cfg.apiLoginID === 'string' &&
    cfg.apiLoginID.length > 0 &&
    typeof cfg.publicClientKey === 'string' &&
    cfg.publicClientKey.length > 0 &&
    (cfg.mode === 'sandbox' || cfg.mode === 'production')
  ) {
    return cfg;
  }
  return null;
}

/**
 * Ted #11 (#914) — the boarding-cutoff proposal the desk is shown.
 * `applicable: false` (with a `reason`) means no offer exists here at all;
 * `late: false` means not yet. The row renders only on `applicable && late`.
 */
export interface AdminLatePickup {
  bookingId: string;
  applicable: boolean;
  reason:
    | 'charge_disabled'
    | 'not_boarding'
    | 'not_departing_today'
    | 'no_daycare_service'
    | null;
  late: boolean;
  /** `HH:MM` for logic. */
  checkoutLocalTime: string;
  graceLocalTime: string;
  nowLocalTime: string;
  /** The 12-hour form the row prints — never build a clock string locally. */
  checkoutLabel: string;
  graceLabel: string;
  nowLabel: string;
  timezone: string;
  minutesPastGrace: number;
  /** The FULL-day total for this stay (per-dog price × dogs since #1220). */
  chargeCents: number;
  daycareServiceName: string | null;
  /** Full-day (`daycare`) packages — the #914 name for `fullDayPackages`. */
  packages: AdminCustomerPackage[];
  // ── #1220 (Ted, 10-09) — a half or a full day, per dog. OPTIONAL: a server
  // from before #1220 sends none of these (frontends deploy on merge, the
  // server by hand), and the row then offers the full day only.
  dogCount?: number;
  fullDayAvailable?: boolean;
  fullDayChargeCentsPerDog?: number;
  fullDayChargeCents?: number;
  halfDayAvailable?: boolean;
  halfDayChargeCentsPerDog?: number;
  halfDayChargeCents?: number;
  halfDayServiceName?: string | null;
  packageDaysNeeded?: number;
  fullDayPackages?: AdminCustomerPackage[];
  /** Half-day (`daycare_half`, #1221) packages. */
  halfDayPackages?: AdminCustomerPackage[];
}

/** The desk's choice. There is no "apply automatically" — Ted's review-first rule. */
export type AdminLatePickupDisposition =
  | { action: 'charge'; length?: 'half' | 'full' }
  | { action: 'package'; packageId: string; length?: 'half' | 'full' }
  | { action: 'waive'; reason: string };

export const adminPaymentsApi = {
  /**
   * #522 — desk gateway discovery (which processor + Accept.js public
   * values). Read-shaped and PUBLIC-values-only; normal retry budget.
   */
  getGatewayConfig: (
    apiUrl: string,
  ): Promise<ApiResponse<AdminGatewayConfig>> =>
    api.get<AdminGatewayConfig>(
      apiUrl,
      '/api/v2/admin/payments/gateway-config',
    ),
  /** List a customer's saved cards (card-on-file payments). */
  getSavedCards: (
    apiUrl: string,
    customerId: string,
  ): Promise<ApiResponse<{ cards: AdminSavedCard[] }>> =>
    api.get<{ cards: AdminSavedCard[] }>(
      apiUrl,
      `/api/v2/admin/payments/customers/${customerId}/saved-cards`,
    ),
  /**
   * Open invoices queue (issue #140 server-of-record). Lists settle-later
   * invoices; defaults to `status='open'` server-side when unspecified.
   */
  getOpenInvoices: (
    apiUrl: string,
    options?: {
      limit?: number;
      status?: 'open' | 'settled' | 'voided';
    },
  ): Promise<ApiResponse<AdminOpenInvoicesResponse>> => {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.status) params.set('status', options.status);
    const qs = params.toString();
    return api.get<AdminOpenInvoicesResponse>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * Queue a settle-later invoice. The booking is already checked out
   * server-side; this appends the open balance to the server-of-record
   * queue (replaces the old localStorage `appendOpenInvoice`).
   */
  createOpenInvoice: (
    apiUrl: string,
    data: {
      bookingId: string;
      totalCents: number;
      notes?: string | null;
      /**
       * Ted #3 + #9 (#906 #912) — REQUIRED when totalCents differs from the
       * booking total; the server recomputes and refuses a bare lower total.
       * The covered visit sends `{ kind: 'package', reason }`.
       */
      adjustment?: AdminInvoiceAdjustmentInput | { kind: 'package'; reason: string };
      /**
       * Ted #11 (#914) — how the desk resolved a late boarding pickup.
       * Absent = nothing is charged. No amount is sent: the daycare rate is
       * the tenant's own price and the server reads it, and the server
       * re-decides lateness against its own clock before writing anything.
       */
      latePickup?: AdminLatePickupDisposition;
    },
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice }>> =>
    // Money-adjacent mutation: a transport-retried duplicate invoice
    // can be cash-settled twice → no auto-retry (#248).
    moneyPost<{ invoice: AdminOpenInvoice }>(
      apiUrl,
      '/api/v2/admin/payments/open-invoices',
      data,
    ),
  /**
   * Ted #3 + #9 (#906 #912) — adjust an OPEN invoice's amount due with a
   * reason: a signed percent, a signed amount, or a new total. The server
   * refuses beyond the desk band (403) without owner/manager authority,
   * below zero or no-change (422), and a non-open invoice (409). Nothing
   * moves on a refusal. Money-adjacent: no transport retry.
   */
  adjustOpenInvoice: (
    apiUrl: string,
    invoiceId: string,
    body: AdminInvoiceAdjustmentInput,
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>> =>
    moneyPost<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/adjustments`,
      body,
    ),
  /**
   * Ted #34 E6.2 (#1161) — take the owner's first-groom discount back off an
   * open invoice, with the desk's reason. The server reverses exactly the row
   * it wrote (the amount never comes from here).
   */
  removeFirstGroomDiscount: (
    apiUrl: string,
    invoiceId: string,
    adjustmentId: string,
    reason: string,
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>> =>
    moneyPost<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/adjustments/${adjustmentId}/remove`,
      { reason },
    ),
  // ── Ted #35 (#1190 story 35.3) — a client's agreed price ──────────────
  /** The customer's active agreements (the profile card). */
  listRateAgreements: (
    apiUrl: string,
    customerId: string,
  ): Promise<ApiResponse<{ agreements: AdminRateAgreement[] }>> =>
    api.get<{ agreements: AdminRateAgreement[] }>(
      apiUrl,
      `/api/v2/admin/payments/customers/${encodeURIComponent(customerId)}/rate-agreements`,
    ),
  /** Save a deal once (owner — business-config.pricing:write:all). */
  createRateAgreement: (
    apiUrl: string,
    customerId: string,
    body: AdminRateAgreementInput,
  ): Promise<ApiResponse<{ agreement: AdminRateAgreement }>> =>
    api.post<{ agreement: AdminRateAgreement }>(
      apiUrl,
      `/api/v2/admin/payments/customers/${encodeURIComponent(customerId)}/rate-agreements`,
      body,
    ),
  updateRateAgreement: (
    apiUrl: string,
    customerId: string,
    agreementId: string,
    patch: Partial<AdminRateAgreementInput>,
  ): Promise<ApiResponse<{ agreement: AdminRateAgreement }>> =>
    api.patch<{ agreement: AdminRateAgreement }>(
      apiUrl,
      `/api/v2/admin/payments/customers/${encodeURIComponent(customerId)}/rate-agreements/${encodeURIComponent(agreementId)}`,
      patch,
    ),
  /** Soft remove — the deal stops being offered; its ledger history stays. */
  removeRateAgreement: (
    apiUrl: string,
    customerId: string,
    agreementId: string,
  ): Promise<ApiResponse<{ agreement: AdminRateAgreement }>> =>
    api.delete<{ agreement: AdminRateAgreement }>(
      apiUrl,
      `/api/v2/admin/payments/customers/${encodeURIComponent(customerId)}/rate-agreements/${encodeURIComponent(agreementId)}`,
    ),
  /**
   * Apply the client's agreed rate to an open invoice. Only WHICH deal is
   * sent — the server prices it from the agreement and the household's
   * visits that day, and refuses a non-fit (409, nothing written). Money:
   * no transport retry.
   */
  applyAgreedRate: (
    apiUrl: string,
    invoiceId: string,
    agreementId: string,
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>> =>
    moneyPost<{ invoice: AdminOpenInvoice; adjustment: AdminInvoiceAdjustment }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/agreed-rate`,
      { agreementId },
    ),
  /**
   * Settle a single open invoice (end-of-day reconciliation).
   * #252 should-fix 6 — pass `{ settledVia: 'redemption' }` when loyalty
   * points covered the visit: the server then mints a ZERO-value ledger
   * row (ref `invoice_redeem_<id>`) instead of a full-value desk Payment,
   * so points-covered revenue is never double-recorded.
   * #812 — a DESK settle must say HOW it was paid: the server 400s a desk
   * settle without `paymentMethod` (that's what makes "how much cash this
   * week?" answerable from the ledger).
   */
  settleOpenInvoice: (
    apiUrl: string,
    invoiceId: string,
    body:
      | {
          paymentMethod: AdminSettleTender;
          /** Ted #7 (#910) — store credit applied (cents); omitted ⇒ none. */
          walletAmountCents?: number;
          /** Sawyer 05 end-of-day-tenders (C4) — the check's number; only with 'check'. */
          checkNumber?: string;
        }
      | { settledVia: 'redemption'; redemptionCode?: string },
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice; payment?: { id?: string; totalCents?: number; paymentMethod?: string | null; walletAmountCents?: number; checkNumber?: string | null } }>> =>
    moneyPatch<{ invoice: AdminOpenInvoice; payment?: { id?: string; totalCents?: number; paymentMethod?: string | null; walletAmountCents?: number; checkNumber?: string | null } }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/settle`,
      body,
    ),
  /**
   * Batch-settle a set of open invoices in one call. `paymentMethod` (#812)
   * stamps every row in the sweep; omitted ⇒ the rows record NO tender
   * (honest "unrecorded" for the end-of-day sweep, never a fabricated one).
   */
  batchSettleOpenInvoices: (
    apiUrl: string,
    invoiceIds: ReadonlyArray<string>,
    paymentMethod?: AdminDeskPaymentMethod,
  ): Promise<ApiResponse<{ settled: number }>> =>
    moneyPost<{ settled: number }>(
      apiUrl,
      '/api/v2/admin/payments/open-invoices/settle-batch',
      { invoiceIds, ...(paymentMethod ? { paymentMethod } : {}) },
    ),
  /**
   * #813 — the visit cart: one owner's open invoices + the server-derived
   * total the desk must quote. A 404 from an older server means the cart
   * feature isn't deployed — callers treat that as "no cart", never an error.
   */
  getCart: (
    apiUrl: string,
    customerId: string,
  ): Promise<ApiResponse<AdminCartResponse>> =>
    api.get<AdminCartResponse>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/cart?customerId=${encodeURIComponent(customerId)}`,
    ),
  /**
   * #813 — settle the whole cart in ONE transaction into ONE Payment.
   * expectedTotalCents is the fail-before-money cross-check: the server
   * derives the sum inside the settle transaction and refuses on mismatch
   * (422 cart_total_mismatch) — nothing settles on a stale cart.
   */
  settleCart: (
    apiUrl: string,
    invoiceIds: ReadonlyArray<string>,
    paymentMethod: AdminSettleTender,
    expectedTotalCents: number,
    /** Ted #7 (#910) — store credit applied to the whole cart (cents). */
    walletAmountCents?: number,
    /** Sawyer 05 end-of-day-tenders (C4) — the check's number; sent only when non-empty. */
    checkNumber?: string,
  ): Promise<ApiResponse<{ invoices: AdminOpenInvoice[]; payment: { id: string; totalCents: number; paymentMethod: string | null; walletAmountCents?: number; checkNumber?: string | null } }>> =>
    moneyPost<{ invoices: AdminOpenInvoice[]; payment: { id: string; totalCents: number; paymentMethod: string | null; walletAmountCents?: number; checkNumber?: string | null } }>(
      apiUrl,
      '/api/v2/admin/payments/open-invoices/settle-cart',
      {
        invoiceIds,
        paymentMethod,
        expectedTotalCents,
        ...(walletAmountCents && walletAmountCents > 0 ? { walletAmountCents } : {}),
        ...(checkNumber ? { checkNumber } : {}),
      },
    ),
  /**
   * #251 should-fix 4 — escaped-visits reconciliation read: checked-out
   * bookings with NEITHER a completed Payment NOR any OpenInvoice row
   * (the client-orchestrated money leg's crash window). Surfaced on the
   * Payments page so staff can re-queue each visit's invoice.
   */
  getEscapedVisits: (
    apiUrl: string,
    limit?: number,
  ): Promise<ApiResponse<{ visits: AdminEscapedVisit[]; count: number }>> =>
    api.get<{ visits: AdminEscapedVisit[]; count: number }>(
      apiUrl,
      `/api/v2/admin/payments/reconciliation/escaped-visits${
        limit != null ? `?limit=${limit}` : ''
      }`,
    ),
  /**
   * #572 — unresolved pending desk stubs: KEPT pending Payment rows from a
   * desk charge whose gateway outcome was indeterminate/duplicate. Invisible
   * to Transactions and unresolvable via charge/refund; this worklist is the
   * only in-app path to close them.
   */
  getPendingStubs: (
    apiUrl: string,
    limit?: number,
  ): Promise<ApiResponse<{ stubs: AdminPendingStub[]; count: number }>> =>
    api.get<{ stubs: AdminPendingStub[]; count: number }>(
      apiUrl,
      `/api/v2/admin/payments/reconciliation/pending-stubs${
        limit != null ? `?limit=${limit}` : ''
      }`,
    ),
  /**
   * #572 — resolve one pending desk stub after checking the gateway
   * dashboard. `complete` (money confirmed → pin the real transaction id +
   * settle the invoice) requires gatewayTransactionId; `release` (no charge)
   * deletes the stub. Reconciles a booking → moneyPost (never transport-
   * retried; the server guards status='pending' so a retry can't double-act).
   */
  resolvePendingStub: (
    apiUrl: string,
    paymentId: string,
    data: { action: 'complete' | 'release'; gatewayTransactionId?: string },
  ): Promise<ApiResponse<{ resolution: AdminDeskStubResolution }>> =>
    moneyPost<{ resolution: AdminDeskStubResolution }>(
      apiUrl,
      `/api/v2/admin/payments/reconciliation/pending-stubs/${encodeURIComponent(
        paymentId,
      )}/resolve`,
      data,
    ),
  /**
   * Transactions tab — money-moved Payment rows for the tenant, each with
   * its settlement heuristic + void/refund eligibility. The reporting
   * surface (owner/accountant never need the Authorize.net portal) AND
   * the home for the Void/Refund actions below. `customerId` narrows to
   * one customer's history.
   */
  listTransactions: (
    apiUrl: string,
    options?: { limit?: number; customerId?: string },
  ): Promise<ApiResponse<AdminTransactionList>> => {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.customerId) params.set('customerId', options.customerId);
    const qs = params.toString();
    return api.get<AdminTransactionList>(
      apiUrl,
      `/api/v2/admin/payments/transactions${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * Void an UNSETTLED gateway charge (same-day cancel before the batch).
   * Returns money → moneyPost (NEVER transport-retried: a retried void on
   * an already-voided txn is a gateway error, not a no-op). If the charge
   * already settled the gateway rejects it and the desk routes the user to
   * Refund instead.
   */
  voidPayment: (
    apiUrl: string,
    paymentId: string,
  ): Promise<ApiResponse<{ void: AdminVoidResult }>> =>
    moneyPost<{ void: AdminVoidResult }>(
      apiUrl,
      '/api/v2/admin/payments/voids',
      { paymentId },
    ),
  /**
   * Refund a SETTLED gateway charge — full (omit amountCents) or partial.
   * Cumulative refunds are capped at the charge server-side. Returns money
   * → moneyPost.
   */
  refundPayment: (
    apiUrl: string,
    data: { paymentId: string; amountCents?: number; reason?: string },
  ): Promise<ApiResponse<{ refund: AdminRefundResult }>> =>
    moneyPost<{ refund: AdminRefundResult }>(
      apiUrl,
      '/api/v2/admin/payments/refunds',
      data,
    ),
  /**
   * Read the tenant's settlement (card-batch) cutoff config — the local
   * batch time + timezone that drive the Void/Refund clock heuristic.
   */
  getSettlementConfig: (
    apiUrl: string,
  ): Promise<
    ApiResponse<{ timezone: string; settlementCutoffLocalTime: string | null }>
  > =>
    api.get<{ timezone: string; settlementCutoffLocalTime: string | null }>(
      apiUrl,
      '/api/v2/admin/payments/settlement-config',
    ),
  /** Set the tenant's batch time (owner-gated). null clears → default. */
  setSettlementConfig: (
    apiUrl: string,
    settlementCutoffLocalTime: string | null,
  ): Promise<ApiResponse<{ settlementCutoffLocalTime: string | null }>> =>
    api.put<{ settlementCutoffLocalTime: string | null }>(
      apiUrl,
      '/api/v2/admin/payments/settlement-config',
      { settlementCutoffLocalTime },
    ),
  /**
   * List a customer's pre-paid day-packages, optionally scoped to a
   * service kind. Replaces the old localStorage `listCustomerPackages`.
   */
  listPackages: (
    apiUrl: string,
    customerId: string,
    serviceKind?: AdminPackageServiceKind,
  ): Promise<ApiResponse<{ packages: AdminCustomerPackage[] }>> => {
    const params = new URLSearchParams({ customerId });
    if (serviceKind) params.set('serviceKind', serviceKind);
    return api.get<{ packages: AdminCustomerPackage[] }>(
      apiUrl,
      `/api/v2/admin/payments/packages?${params.toString()}`,
    );
  },
  /**
   * Ted #11 (#914) — is this boarder being collected after the owner's grace
   * time, what would a daycare day cost, and does the customer hold a
   * daycare package to use instead? A READ: nothing is charged or redeemed.
   */
  latePickup: (
    apiUrl: string,
    bookingId: string,
  ): Promise<ApiResponse<{ latePickup: AdminLatePickup }>> =>
    api.get<{ latePickup: AdminLatePickup }>(
      apiUrl,
      `/api/v2/admin/payments/late-pickup/${bookingId}`,
    ),
  /** Sell/grant a customer a day-package (issue #140 — the new create path). */
  sellPackage: (
    apiUrl: string,
    data: {
      customerId: string;
      serviceKind: AdminPackageServiceKind;
      totalDays: number;
      label?: string | null;
      // #815 — a SALE carries price + tender; an explicit grant carries
      // `grant: true` instead. The server refuses a silent grant (400).
      priceCents?: number;
      paymentMethod?: AdminDeskPaymentMethod;
      grant?: boolean;
      /** #1157 E2.6 — optional expiry, `YYYY-MM-DD`. */
      expiresOn?: string;
    },
  ): Promise<ApiResponse<{ package: AdminCustomerPackage }>> =>
    // A transport-retried duplicate creates a second sold package (#248).
    moneyPost<{ package: AdminCustomerPackage }>(
      apiUrl,
      '/api/v2/admin/payments/packages',
      data,
    ),
  /**
   * Redeem days from a package — atomic server-side decrement that refuses
   * to go negative (409 with `remainingDays` when insufficient).
   */
  redeemPackage: (
    apiUrl: string,
    packageId: string,
    days = 1,
    /**
     * Ted #5 (#908) — the booking this day pays for. The server verifies the
     * booking is this tenant's and the package's customer's, and records the
     * redemption against it (the package history's "which visit").
     */
    bookingId?: string,
  ): Promise<ApiResponse<{ package: AdminCustomerPackage; redemption?: AdminPackageRedemption | null }>> =>
    // A transport-retried duplicate double-decrements paid days (#248).
    moneyPost<{ package: AdminCustomerPackage; redemption?: AdminPackageRedemption | null }>(
      apiUrl,
      `/api/v2/admin/payments/packages/${packageId}/redeem`,
      { days, ...(bookingId ? { bookingId } : {}) },
    ),
  /**
   * Ted #1 (#904) — correct a package balance from the customer profile:
   * deduct a day for a past visit that was charged when it should have used
   * one, or restore a day used when it should not have been. A reason is
   * required; the server writes the ledger row with the counter change.
   * Refusals: 409 (not enough days), 422 (restore beyond the package), 400.
   */
  adjustPackage: (
    apiUrl: string,
    packageId: string,
    data: AdminAdjustPackageInput,
  ): Promise<ApiResponse<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>> =>
    // A transport-retried duplicate double-corrects paid days (#248).
    moneyPost<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>(
      apiUrl,
      `/api/v2/admin/payments/packages/${packageId}/adjust`,
      data,
    ),
  /**
   * #1157 E2.6 — add days to a package with a reason (a `manual_add` History
   * row; the package grows). No retries: a duplicate would add the days twice.
   */
  addPackageDays: (
    apiUrl: string,
    packageId: string,
    data: { days: number; reason: string },
  ): Promise<ApiResponse<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>> =>
    moneyPost<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>(
      apiUrl,
      `/api/v2/admin/payments/packages/${packageId}/add-days`,
      data,
    ),
  /** #1157 E2.6 — set (`YYYY-MM-DD`) or clear (null) a package's expiry, with a reason. */
  setPackageExpiry: (
    apiUrl: string,
    packageId: string,
    data: { expiresOn: string | null; reason: string },
  ): Promise<ApiResponse<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>> =>
    request<{ package: AdminCustomerPackage; entry: AdminPackageLedgerEntry }>(
      apiUrl,
      `/api/v2/admin/payments/packages/${packageId}/expiry`,
      { method: 'PUT', body: JSON.stringify(data) },
      { maxRetries: 0 },
    ),
  /** #1157 E2.3 — the settle dialog's "Use package (N left)" read. Redeems nothing. */
  packageCover: (
    apiUrl: string,
    invoiceId: string,
  ): Promise<ApiResponse<{ packageCover: AdminPackageCoverOptions }>> =>
    api.get<{ packageCover: AdminPackageCoverOptions }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/package-cover`,
    ),
  /**
   * #1157 E2.3 — "Use package": the server draws one day per dog and takes
   * the visit off the bill in one transaction. Names only the package. No
   * retries: a transport-retried duplicate is refused server-side anyway
   * (already covered), but the desk should see the first answer.
   */
  coverWithPackage: (
    apiUrl: string,
    invoiceId: string,
    packageId: string,
  ): Promise<ApiResponse<{ invoice: AdminOpenInvoice; package: AdminCustomerPackage; days: number }>> =>
    moneyPost<{ invoice: AdminOpenInvoice; package: AdminCustomerPackage; days: number }>(
      apiUrl,
      `/api/v2/admin/payments/open-invoices/${invoiceId}/package-cover`,
      { packageId },
    ),
  /** Ted #1 (#904) — a package's history, newest first (which visit, how many days, why, who). */
  listPackageHistory: (
    apiUrl: string,
    packageId: string,
  ): Promise<ApiResponse<{ entries: AdminPackageLedgerEntry[] }>> =>
    api.get<{ entries: AdminPackageLedgerEntry[] }>(
      apiUrl,
      `/api/v2/admin/payments/packages/${packageId}/history`,
    ),
  /**
   * List grooming price-tier changes for a booking or a customer (AC6).
   * Exactly one of bookingId / customerId is required by the server.
   */
  listTierAudit: (
    apiUrl: string,
    filter: { bookingId?: string; customerId?: string; limit?: number },
  ): Promise<ApiResponse<{ entries: AdminTierAuditEntry[] }>> => {
    const params = new URLSearchParams();
    if (filter.bookingId) params.set('bookingId', filter.bookingId);
    if (filter.customerId) params.set('customerId', filter.customerId);
    if (filter.limit != null) params.set('limit', String(filter.limit));
    return api.get<{ entries: AdminTierAuditEntry[] }>(
      apiUrl,
      `/api/v2/admin/payments/tier-audit?${params.toString()}`,
    );
  },
  /** Record a grooming tier-change (best-effort from checkout). */
  recordTierAudit: (
    apiUrl: string,
    data: {
      bookingId: string;
      originalTier: string;
      finalTier: string;
      originalTierLabel: string;
      finalTierLabel: string;
      originalTotalCents: number;
      finalTotalCents: number;
      operatorNotes?: string | null;
      confirmedAt?: string;
    },
  ): Promise<ApiResponse<{ entry: AdminTierAuditEntry }>> =>
    api.post<{ entry: AdminTierAuditEntry }>(
      apiUrl,
      '/api/v2/admin/payments/tier-audit',
      data,
    ),
  /** Mint a Stripe Terminal connection token. Required for the Terminal SDK to discover and connect to a Reader. */
  getTerminalConnectionToken: (
    apiUrl: string,
  ): Promise<ApiResponse<AdminTerminalConnectionToken>> =>
    api.post<AdminTerminalConnectionToken>(
      apiUrl,
      '/api/v2/admin/payments/terminal/connection-token',
      {},
    ),
  /**
   * Create a PaymentIntent destined for a Stripe Terminal Reader
   * (`payment_method_types: ['card_present']`). The total is always
   * recalculated server-side — never trust the client's totalCents.
   */
  createTerminalPaymentIntent: (
    apiUrl: string,
    data: {
      bookingId: string;
      tipCents?: number;
      idempotencyKey: string;
    },
  ): Promise<ApiResponse<AdminTerminalPaymentIntent>> =>
    moneyPost<AdminTerminalPaymentIntent>(
      apiUrl,
      '/api/v2/admin/payments/terminal/payment-intent',
      data,
    ),
  /**
   * Capture a previously-tapped Terminal PaymentIntent. Splits this from
   * the create call so the Reader's tap can complete asynchronously
   * before the staff confirms capture.
   */
  captureTerminalPayment: (
    apiUrl: string,
    paymentIntentId: string,
  ): Promise<ApiResponse<{ status: string; amountCaptured: number }>> =>
    moneyPost<{ status: string; amountCaptured: number }>(
      apiUrl,
      `/api/v2/admin/payments/terminal/${paymentIntentId}/capture`,
      {},
    ),
  /**
   * Create a PaymentIntent for the manual-card-entry flow. UI mounts
   * Stripe Elements (card element) and confirms with `client_secret`
   * client-side — the staff types the card number, never the server.
   */
  createManualPaymentIntent: (
    apiUrl: string,
    data: {
      bookingId: string;
      tipCents?: number;
      idempotencyKey: string;
    },
  ): Promise<
    ApiResponse<{
      paymentId: string;
      paymentIntentId: string;
      clientSecret: string;
      totalCents: number;
      simulation: boolean;
    }>
  > =>
    moneyPost(
      apiUrl,
      '/api/v2/admin/payments/manual/payment-intent',
      data,
    ),
  /**
   * Charge a saved card on file. Server recalculates the total from the
   * booking row; client supplies tip + savedCardId. NEVER transport-
   * retried: a 504 here is charge_indeterminate — the gateway may have
   * charged (#248).
   */
  chargeCardOnFile: (
    apiUrl: string,
    data: {
      bookingId: string;
      savedCardId: string;
      tipCents?: number;
      /**
       * Ted #34 E6.3 (#1161) — the tip percentage the desk tapped (15 / 18 /
       * 20). The server re-derives the tip from the booking, refuses a
       * mismatch before the gateway, and records it on the payment. An older
       * server strips the key and records the cents only.
       */
      tipPercent?: number;
      idempotencyKey: string;
      /** #522 — cross-check only; the server derives what it charges. */
      expectedTotalCents?: number;
    },
  ): Promise<
    ApiResponse<{
      paymentId: string;
      status: string;
      totalCents: number;
      simulation: boolean;
    }>
  > =>
    moneyPost(apiUrl, '/api/v2/admin/payments/card-on-file/charge', data),
  /**
   * #522 — charge a hand-keyed card via its Accept.js opaque nonce
   * (POST /manual/charge, one-shot AUTHCAPTURE; card-not-present rates
   * apply — disclosed in the UI). The PAN/CVC were tokenized in the
   * browser; this body carries ONLY the nonce. Server recalculates the
   * total from the booking row. NEVER transport-retried: a 504 here is
   * charge_indeterminate — the gateway may have charged (#248).
   */
  chargeManualCard: (
    apiUrl: string,
    data: {
      bookingId: string;
      tipCents?: number;
      idempotencyKey: string;
      opaqueData: { dataDescriptor: string; dataValue: string };
      allowAdditionalCharge?: boolean;
      /** #522 — cross-check only; the server derives what it charges. */
      expectedTotalCents?: number;
    },
  ): Promise<
    ApiResponse<{
      paymentId: string;
      status: string;
      totalCents: number;
      simulation: boolean;
    }>
  > =>
    moneyPost(apiUrl, '/api/v2/admin/payments/manual/charge', data),
};

// ─── adminGatewayCredentialsApi (#233 — AuthNet Slice 2) ───────────────────
// Owner-only set/rotate of the tenant's Authorize.net gateway credentials
// (`payments-ops.credentials:write:all`). SECRETS ARE WRITE-ONLY: the server
// returns a MASKED status (last-4 recognition hints + set/unset flags) and
// never echoes transactionKey/signatureKey back. The status union mirrors
// GatewayCredentialStatus in @ferro/core modules/payments-admin/service.ts.

export type AdminGatewayCredentialStatus =
  | { state: 'not_configured'; gateway: string }
  | {
      state: 'configured';
      gateway: string;
      mode: 'sandbox' | 'production';
      apiLoginIdLast4: string;
      /** False ⇒ envelope sealed before Slice 2 — rotate to add the Accept.js key. */
      publicClientKeySet: boolean;
      publicClientKeyLast4: string | null;
    }
  | {
      state: 'sealed_unreadable';
      gateway: string;
      reason:
        | 'key_missing'
        | 'key_invalid'
        | 'invalid_envelope'
        | 'decrypt_failed'
        | 'invalid_credentials';
    };

export interface AdminGatewayCredentialsInput {
  apiLoginId: string;
  transactionKey: string;
  signatureKey: string;
  /** Browser-public Accept.js Client Key — the 4th credential. */
  publicClientKey: string;
  mode: 'sandbox' | 'production';
}

export const adminGatewayCredentialsApi = {
  /** Masked credential status for the settings card. Never contains secrets. */
  getStatus: (
    apiUrl: string,
  ): Promise<ApiResponse<{ credentials: AdminGatewayCredentialStatus }>> =>
    api.get<{ credentials: AdminGatewayCredentialStatus }>(
      apiUrl,
      '/api/v2/admin/payments/gateway-credentials',
    ),
  /**
   * Set or rotate the full 4-credential set (one operation — the new
   * envelope replaces the old). Resolves to the fresh masked status.
   *
   * Review nit (#244) — the endpoint's 503
   * (credentials_encryption_unavailable) is a DETERMINISTIC ops state
   * (PAYMENT_CREDS_ENCRYPTION_KEY missing on the deploy), not a
   * cold-start signature: retrying 3× just delays the real error ~7s.
   * `maxRetries: 0` so the owner sees it immediately — same posture as
   * the AIM chat call. (Re-sending the secrets would be harmless; the
   * wait isn't.)
   */
  update: (
    apiUrl: string,
    data: AdminGatewayCredentialsInput,
  ): Promise<ApiResponse<{ credentials: AdminGatewayCredentialStatus }>> =>
    request<{ credentials: AdminGatewayCredentialStatus }>(
      apiUrl,
      '/api/v2/admin/payments/gateway-credentials',
      { method: 'PUT', body: JSON.stringify(data) },
      { maxRetries: 0 },
    ),
  /**
   * Validate the SEALED credentials against the live gateway WITHOUT
   * moving money (Authorize.net authenticateTestRequest — a zero-dollar
   * credential check). `verified` ⇒ the keys authenticate; `simulation`
   * ⇒ no live gateway is configured (nothing to test). Never charges.
   * maxRetries: 0 — a credential-auth failure is deterministic, not a
   * cold-start; surface it immediately.
   */
  testConnection: (
    apiUrl: string,
  ): Promise<ApiResponse<{ connection: { verified: boolean; simulation: boolean } }>> =>
    request<{ connection: { verified: boolean; simulation: boolean } }>(
      apiUrl,
      '/api/v2/admin/payments/gateway-credentials/test',
      { method: 'POST', body: JSON.stringify({}) },
      { maxRetries: 0 },
    ),
};

// ─── Admin Quick Book (Track D, 2026-05-11) ──────────────────────────────
// Receptionist-side walk-in booking creation. Hits the new
// /api/admin/bookings endpoints. Boarding bookings carry an endDate;
// daycare and grooming bookings carry a date + optional startTime.

export type AdminCustomerDog = {
  id: string;
  name: string;
  breed: string | null;
  sizeCategory: string | null;
  /** Ted #28 Phase 2 (#1050) — the dog's flags for the picker; absent on an older server. */
  flags?: DogFlag[];
  /**
   * LG (#1111) — the server's verdict on WHICH dogs get "Is your dog currently
   * in heat?" (not known male, not known spayed, and the tenant has the
   * question on). Absent on an older server ⇒ nobody is asked.
   */
  needs_heat_question?: boolean;
  sex?: string | null;
  altered_status?: string | null;
};

/** LG (#1111) — "Is your dog currently in heat?", per dog id; only `not_in_heat` is ever stored. */
export type HeatAnswer = 'not_in_heat' | 'in_heat';
export type HeatDeclarations = Record<string, HeatAnswer>;

/** Ted #28 Phase 2 (#1050) — the Quick Book picker's payload. */
export type AdminCustomerDogsResponse = {
  dogs: AdminCustomerDog[];
  /** The OWNER's flags (unsigned agreement, balance, credit); absent on an older server. */
  account?: { flags: DogFlag[] };
  /** LG (#1111) — the tenant's heat policy and the question's copy; absent on an older server. */
  policies?: {
    heat_declaration_required: boolean;
    heat_question: { label: string; help: string; notice: string };
  };
  /**
   * Ted #34 E6.1 (#942) — the shop's groom-day half-day: whether the grooming
   * step offers "Add half-day daycare" and at what price per dog. null = not
   * offered; absent on an older server (also not offered).
   */
  groom_day_daycare?: GroomDayDaycareOffer | null;
};

/** Ted #34 E6.1 (#942) — the groom-day half-day offer, as the server sends it. */
export type GroomDayDaycareOffer = {
  service_type_id: string;
  display_name: string;
  rate_cents: number;
};

export type AdminBookingSingleDayInput = {
  customerId: string;
  serviceTypeId: string;
  dogIds: string[];
  date: string; // YYYY-MM-DD — Ted #13a: may be in the past for staff
  startTime?: string; // HH:MM
  notes?: string;
  /** Ted #12 — send the customer the confirmation (+ 24h reminder). */
  notifyCustomer?: boolean;
  /** Sawyer 09 pet-inactivity (D1) — the #718 desk override; 3–300 chars, audit-logged. */
  assessmentOverrideReason?: string;
  /** LG (#1111) — the owner's heat answers, per dog id, as the desk took them. */
  heatDeclarations?: HeatDeclarations;
  /** Ted #34 E6.1 (#942) — on a groom: also the half-day at the groom-day rate, one visit. */
  addHalfDayDaycare?: boolean;
  // Sawyer 07 quickbook-bed-addons (C1) — services ticked on the card; never a price (the server refuses one).
  additionalServices?: Array<{ addOnId?: string; serviceTypeId?: string; everyDay?: boolean }>;
};

export type AdminBookingMultiDayInput = {
  customerId: string;
  serviceTypeId: string;
  dogIds: string[];
  startDate: string; // YYYY-MM-DD — Ted #13a: may be in the past for staff
  endDate: string; // YYYY-MM-DD
  startTime?: string; // HH:MM
  notes?: string;
  /** Ted #12 — send the customer the confirmation (+ 24h reminder). */
  notifyCustomer?: boolean;
  /** Sawyer 09 pet-inactivity (D1) — see the single-day input. */
  assessmentOverrideReason?: string;
  /** LG (#1111) — see the single-day input. */
  heatDeclarations?: HeatDeclarations;
  // Sawyer 07 quickbook-bed-addons (C1) — see the single-day input; `everyDay` = one leg per day of the stay.
  additionalServices?: Array<{ addOnId?: string; serviceTypeId?: string; everyDay?: boolean }>;
};

export type AdminBookingInput =
  | AdminBookingSingleDayInput
  | AdminBookingMultiDayInput;

export type AdminBookingCreated = {
  id: string;
  customer_id: string;
  service_type_id: string;
  date: string;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  status: string;
  total_cents: number;
  notes: string | null;
  /** LG (board Contracts §3) — set when the row landed held for staff review. */
  review_hold_reason?: string | null;
  review_held?: boolean;
};

/** LG — the 201's STRONG FLAG: non-null means the reservation is pending a manager's review. */
export type AdminBookingCreateResponse = {
  booking: AdminBookingCreated;
  review_hold?: { reason: string } | null;
  /**
   * Ted #34 E6.1 — present only when the desk asked for the half-day: the leg
   * it added, or null with the reason it could not (a race lost after the
   * pre-flight — the groom itself is booked).
   */
  half_day_daycare?: AdminBookingCreated | null;
  half_day_daycare_error?: string | null;
};

export const adminBookingsApi = {
  create: (
    apiUrl: string,
    input: AdminBookingInput,
  ): Promise<ApiResponse<AdminBookingCreateResponse>> =>
    api.post<AdminBookingCreateResponse>(
      apiUrl,
      '/api/admin/bookings/',
      input,
    ),
  getCustomerDogs: (
    apiUrl: string,
    customerId: string,
  ): Promise<ApiResponse<AdminCustomerDogsResponse>> =>
    api.get<AdminCustomerDogsResponse>(
      apiUrl,
      `/api/admin/bookings/customer/${customerId}/dogs`,
    ),
};

// ─── Ted #30 (#1109, lane LR) — recurring reservation series ──────────────
// Pairs with apps/server/src/routes/admin-booking-series.ts. Gingr's "Add
// Recurring Dates" controls on the wire: weekdays (JS numbering, 0 = Sunday)
// or every day · every 1 / 2 / 4 weeks · an Until date OR a number of dates.

export type SeriesRuleInput = {
  startDate: string; // YYYY-MM-DD
  everyDay?: boolean;
  weekdays?: number[];
  intervalWeeks?: 1 | 2 | 4;
  untilDate?: string;
  occurrenceCount?: number;
};

export type SeriesVaccineIssue = {
  dog_id: string;
  dog_name: string;
  vaccine: string;
  status: 'expired' | 'missing';
  expires_at: string | null;
};

export type SeriesPreviewResponse = {
  dates: string[];
  occurrences: Array<{ date: string; vaccine_issues: SeriesVaccineIssue[] }>;
  rule_text: string;
  cap: { max_occurrences: number; max_span_days: number };
  dogs: Array<{ id: string; name: string }>;
  service_type: { id: string; name: string; display_name: string };
  heat_question_dog_ids: string[];
};

export type SeriesCreateInput = {
  customerId: string;
  serviceTypeId: string;
  dogIds: string[];
  rule: SeriesRuleInput;
  startTime?: string;
  dropOffTime?: string;
  pickUpTime?: string;
  notes?: string;
  transport?: TransportMode;
  notifyCustomer?: boolean;
  heatDeclarations?: HeatDeclarations;
  assessmentOverrideReason?: string;
};

export type SeriesOccurrenceOutcome = 'created' | 'held' | 'refused';

export type SeriesOccurrenceResult = {
  date: string;
  outcome: SeriesOccurrenceOutcome;
  booking_id: string | null;
  status: string | null;
  hold_reason: string | null;
  refusal: { kind: string; message: string } | null;
  vaccine_issues: SeriesVaccineIssue[];
};

export type SeriesWire = {
  id: string;
  customer_id: string;
  service_type_id: string;
  dog_ids: string[];
  rule: {
    every_day: boolean;
    weekdays: number[];
    interval_weeks: number;
    start_date: string;
    until_date: string | null;
    occurrence_count: number | null;
  };
  start_time: string | null;
  drop_off_time: string | null;
  pick_up_time: string | null;
  notes: string | null;
  transport: TransportMode;
  status: string;
  created_at: string;
};

export type SeriesCreateResponse = {
  series: SeriesWire;
  rule_text: string;
  occurrences: SeriesOccurrenceResult[];
  counts: { created: number; held: number; refused: number; confirmed: number };
  notifications: Array<{ type: string; outcome: 'inserted' | 'already_queued' | 'skipped' }>;
};

export type SeriesViewOccurrence = {
  booking_id: string;
  date: string;
  status: string;
  total_cents: number;
  drop_off_time: string | null;
  pick_up_time: string | null;
  review_hold_reason: string | null;
  review_held: boolean;
  detached_at: string | null;
  vaccine_issues: SeriesVaccineIssue[];
};

export type SeriesViewResponse = {
  series: SeriesWire;
  rule_text: string;
  service_type: { id: string; name: string; display_name: string } | null;
  dogs: Array<{ id: string; name: string }>;
  occurrences: SeriesViewOccurrence[];
  notifications: Array<{ type: string; count: number }>;
};

export type SeriesEditScope = 'this_only' | 'this_and_future';

export type SeriesTailResult = {
  booking_id: string;
  date: string;
  outcome: 'changed' | 'unchanged' | 'refused';
  refusal: { kind: string; message: string } | null;
};

/** The #986 edit through the series route — the plain edit result plus what the tail did. */
export type SeriesEditResult = ReservationEditResult & {
  scope: SeriesEditScope;
  detached: boolean;
  tail: SeriesTailResult[];
};

export const seriesApi = {
  preview: (apiUrl: string, input: Omit<SeriesCreateInput, 'notifyCustomer' | 'assessmentOverrideReason'>): Promise<ApiResponse<SeriesPreviewResponse>> =>
    api.post<SeriesPreviewResponse>(apiUrl, '/api/admin/bookings/series/preview', input),
  create: (apiUrl: string, input: SeriesCreateInput): Promise<ApiResponse<SeriesCreateResponse>> =>
    api.post<SeriesCreateResponse>(apiUrl, '/api/admin/bookings/series', input),
  get: (apiUrl: string, seriesId: string): Promise<ApiResponse<SeriesViewResponse>> =>
    api.get<SeriesViewResponse>(apiUrl, `/api/admin/bookings/series/${seriesId}`),
};

/**
 * "Change this reservation only" / "Change this and future reservations?" —
 * the same patch the plain edit sends, through the series route. Throws like
 * `editReservation` so the edit dialog's own catch (ApiError → sentence)
 * handles a refusal identically.
 */
export async function editSeriesOccurrence(
  apiUrl: string,
  token: string,
  seriesId: string,
  bookingId: string,
  scope: SeriesEditScope,
  patch: ReservationEditPatch,
): Promise<SeriesEditResult> {
  const { reason, notifyCustomer, assessmentOverrideReason, ...fields } = patch;
  const res = await fetch(`${apiUrl}/api/admin/bookings/series/${seriesId}/occurrences/${bookingId}/edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      scope,
      patch: fields,
      ...(reason ? { reason } : {}),
      ...(notifyCustomer ? { notifyCustomer } : {}),
      ...(assessmentOverrideReason ? { assessmentOverrideReason } : {}),
    }),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Could not update this reservation (${res.status})`);
  }
  return (await res.json()) as SeriesEditResult;
}

// ─── Platform-admin Stripe Connect (issue #23) ────────────────────────────
// Pairs with apps/server/src/routes/platform-tenant-stripe-connect.ts
// (issues #19 + #20). These endpoints take the tenantId in the URL path
// (the platform-operator console at bare admin.animalloversapp.com does not
// resolve a tenant via host middleware, so the tenantId is explicit).
//
// Public-safe surface only: account_id is opaque, status flags are booleans.
// No Stripe secrets ever cross the wire to the client.

export type PlatformTenantStripeStatusKind =
  | 'not_connected'
  | 'onboarding'
  | 'active'
  | 'restricted'
  | 'verification_required'
  | 'disconnected';

export interface PlatformTenantStripeStatusResponse {
  ok: true;
  tenantId: string;
  slug: string;
  /** Whether a Stripe Connect account is attached to the tenant. */
  connected: boolean;
  /** Opaque Stripe account id (acct_*). Null when not connected. */
  stripeAccountId: string | null;
  /** Email Stripe is using for the account (read from Stripe). */
  accountEmail: string | null;
  /** High-level lifecycle state for UI. */
  status: PlatformTenantStripeStatusKind;
  /** True once Stripe says the tenant can accept charges. */
  chargesEnabled: boolean;
  /** True once Stripe says the tenant can receive payouts. */
  payoutsEnabled: boolean;
  /** True once the tenant has submitted required onboarding details. */
  detailsSubmitted: boolean;
  /** True when Stripe still needs information from the tenant. */
  hasOutstandingRequirements: boolean;
  /** ISO-8601. Last time we persisted Stripe state for this tenant. */
  updatedAt: string | null;
}

export interface PlatformTenantStripeOnboardingLinkRequest {
  /** Absolute https URL Stripe redirects to on completion. */
  returnUrl: string;
  /** Absolute https URL Stripe redirects to if the link expires or user backs out. */
  refreshUrl?: string;
}

export interface PlatformTenantStripeOnboardingLinkResponse {
  ok: true;
  /** Stripe-hosted onboarding URL — caller opens in a new tab. */
  url: string;
  /** ISO-8601. Account Links expire within minutes. */
  expiresAt: string;
  stripeAccountId: string;
  /** True if this call created a new Connect account; false if it reused one. */
  accountCreated: boolean;
}

export const platformTenantStripeApi = {
  /**
   * Read the current Stripe Connect state for a tenant. Returns
   * `connected: false` when no account is attached yet. Polled every 5s by
   * Step3StripeConnect after the user opens the onboarding URL.
   */
  getStatus: async (
    apiUrl: string,
    token: string,
    tenantId: string,
    signal?: AbortSignal,
  ): Promise<PlatformTenantStripeStatusResponse> => {
    const res = await fetch(
      `${apiUrl}/api/platform/tenants/${encodeURIComponent(tenantId)}/stripe-connect/status`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    if (!res.ok) {
      throw await apiErrorFromResponse(res, `Stripe status fetch failed (${res.status})`);
    }
    return (await res.json()) as PlatformTenantStripeStatusResponse;
  },

  /**
   * Mint a fresh Stripe Account Link for the tenant. Creates the Stripe
   * Connect account on first call (server-side); subsequent calls reuse it
   * and mint a fresh time-limited link. Caller opens `url` in a new tab.
   */
  createOnboardingLink: async (
    apiUrl: string,
    token: string,
    tenantId: string,
    body: PlatformTenantStripeOnboardingLinkRequest,
    signal?: AbortSignal,
  ): Promise<PlatformTenantStripeOnboardingLinkResponse> => {
    const res = await fetch(
      `${apiUrl}/api/platform/tenants/${encodeURIComponent(tenantId)}/stripe-connect/onboarding-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (!res.ok) {
      throw await apiErrorFromResponse(res, `Stripe onboarding link failed (${res.status})`);
    }
    return (await res.json()) as PlatformTenantStripeOnboardingLinkResponse;
  },
};

// ─── Grooming tier confirmation + settle-later checkout (issues #24 + #25) ──
//
// Appended 2026-05-12. DO NOT REORDER this section relative to the rest of the
// file — `api.ts` is a hot file shared across the admin-app workstream and
// re-ordering breaks merge tracking.
//
// Becky-T1.1 (issue #24):
//   `checkOutGroomingBooking` extends the existing `/api/bookings/admin/:id/check-out`
//   endpoint with a structured `tierConfirmation` payload (final tier, original tier,
//   adjusted total). On the server today this is folded into the existing `notes`
//   field as a JSON blob (key `tier_confirmation`); when the dedicated
//   `/check-out/tier` endpoint lands in workstream A, swap the URL — the client
//   contract stays the same. Logged separately to the local audit trail so the
//   "who confirmed it" AC has somewhere to land before the server endpoint exists.
//
// Becky-T1.2 (issue #25; server-of-record #140; money-path fix #251):
//   `checkOutBookingSettleLater` posts to the existing `/check-out` endpoint
//   with a `settle_later: true` discriminator (encoded as a structured `notes`
//   envelope today; canonical field arrives with workstream A). It does the
//   CHECK-OUT LEG ONLY — the open-invoice money record is created by the
//   Bookings money-path orchestrator (`Bookings.tsx`) for BOTH tracks, so
//   settle-now and settle-later share one invoice/queue/settle pipeline
//   (`adminPaymentsApi.createOpenInvoice` / `settleOpenInvoice` /
//   `batchSettleOpenInvoices`). The localStorage stop-gap was removed in #140;
//   the old `settleLaterBooking` (checkout + best-effort invoice with a silent
//   console.warn on failure) was replaced in #251 — invoice failures are now
//   surfaced loudly by the orchestrator instead of swallowed.

import type {
  GroomingTierKey,
  GroomingTierDefinition,
} from './groomingTiers.js';

/**
 * Structured payload carried alongside the grooming check-out request.
 * Persisted on the booking record (today: nested inside `notes` as
 * `{ tier_confirmation: TierConfirmationPayload }`; tomorrow: a dedicated
 * column once Track-A adds it).
 */
export interface TierConfirmationPayload {
  /** Tier the booking was created at. */
  readonly original_tier: GroomingTierKey;
  /** Tier the operator picked at checkout. */
  readonly final_tier: GroomingTierKey;
  /** Display labels (for retro-rendering in the audit view). */
  readonly original_tier_label: string;
  readonly final_tier_label: string;
  /** Adjusted total in cents. Server MUST recalculate to prevent tampering. */
  readonly final_total_cents: number;
  /** Original total, in cents, for evidence in the audit trail. */
  readonly original_total_cents: number;
  /** Staff member who confirmed the tier (subject of the audit). */
  readonly confirmed_by_staff_id: string;
  readonly confirmed_by_staff_username: string | null;
  /** ISO-8601 timestamp. */
  readonly confirmed_at: string;
  /** Free-text notes Becky entered alongside the tier change. Optional. */
  readonly operator_notes: string | null;
}

/** What the server returns on a successful tier-confirmation check-out. */
export interface CheckOutGroomingResult {
  readonly booking: AdminBooking;
  readonly tierConfirmation: TierConfirmationPayload;
}

/**
 * Submit a grooming check-out with confirmed tier. Hits the existing
 * `/api/bookings/admin/:id/check-out` endpoint; the tier confirmation
 * rides inside the `notes` envelope today.
 *
 * Server-side, the existing endpoint will store the JSON-encoded notes;
 * when the dedicated tier-update endpoint ships (coordination issue
 * filed alongside this PR), point the fetch at the new URL.
 */
export async function checkOutGroomingBooking(
  apiUrl: string,
  token: string,
  bookingId: string,
  payload: TierConfirmationPayload,
  operatorNotes?: string | null,
  /**
   * Ted #6 / #27 (#909 · #988) — the ACTUAL check-out instant. A grooming
   * visit completed from the Outstanding list belongs to the day it
   * happened, not to the day the desk found it; without this the charge
   * lands on today. Omitted / null ⇒ now (unchanged behaviour for a visit
   * checked out on its own day).
   */
  checkedOutAt?: string | null,
): Promise<CheckOutGroomingResult> {
  // Encode the payload as a structured notes envelope so the server (which
  // already accepts `notes: string`) preserves it verbatim until the
  // dedicated field lands. The leading sentinel + JSON suffix make it
  // unambiguous to parse downstream.
  const envelope = JSON.stringify({ tier_confirmation: payload });
  const combinedNotes = operatorNotes
    ? `${operatorNotes}\n---\n${envelope}`
    : envelope;

  const updated = await postBookingAction(
    apiUrl,
    token,
    bookingId,
    'check-out',
    {
      notes: combinedNotes,
      // Forward-compatible field — server today ignores unknown keys.
      tier_confirmation: payload,
      // #251 review fix — apply the tier-confirmed final total to the
      // booking ROW (the desk charge endpoints recalculate from it).
      // Without this the card charge used the stale pre-tier total.
      final_total_cents: payload.final_total_cents,
      ...(checkedOutAt ? { checked_out_at: checkedOutAt } : {}),
    },
  );

  return {
    booking: updated,
    tierConfirmation: payload,
  };
}

/**
 * Build a tier-confirmation payload from the booking + selected tier + staff
 * session. Pure helper so the page component stays declarative.
 */
export function buildTierConfirmationPayload(args: {
  bookingTotalCents: number;
  originalTier: GroomingTierDefinition;
  selectedTier: GroomingTierDefinition;
  finalTotalCents: number;
  staffId: string;
  staffUsername: string | null;
  operatorNotes: string | null;
  now?: Date;
}): TierConfirmationPayload {
  const now = args.now ?? new Date();
  return {
    original_tier: args.originalTier.key,
    final_tier: args.selectedTier.key,
    original_tier_label: args.originalTier.label,
    final_tier_label: args.selectedTier.label,
    final_total_cents: args.finalTotalCents,
    original_total_cents: args.bookingTotalCents,
    confirmed_by_staff_id: args.staffId,
    confirmed_by_staff_username: args.staffUsername,
    confirmed_at: now.toISOString(),
    operator_notes: args.operatorNotes,
  };
}

// ─── Settle-later check-out leg (issue #25; #140; #251 money path) ─────────
//
// "Check Out — Settle Later" check-out leg ONLY. Marks the booking departed
// (via the existing `/check-out`) with the `settle_later` envelope. The
// money record (open invoice) for BOTH checkout tracks is created by the
// Bookings money-path orchestrator so no track can skip it silently (#251).
// The queue + tier-audit reads/writes live on `adminPaymentsApi` (above).

/**
 * "Check out — Settle Later" departure leg. Hits the existing check-out
 * endpoint to mark the booking departed with the `settle_later: true`
 * discriminator (and the tier-confirmation envelope when applicable).
 *
 * Deliberately does NOT create the open-invoice money record — that is the
 * shared responsibility of the Bookings money-path orchestrator for both
 * settle-now and settle-later, where a failure is surfaced as a blocking,
 * staff-visible error (issue #251; previously a silent console.warn here).
 */
export async function checkOutBookingSettleLater(args: {
  apiUrl: string;
  token: string;
  booking: Pick<
    AdminBooking,
    'id' | 'customer_id' | 'service_type_id' | 'total_cents'
  >;
  /** Operator-entered note (optional). */
  operatorNotes?: string | null;
  staffId: string;
  staffUsername: string | null;
  /** When tier-confirmation also happened, fold the payload into notes. */
  tierConfirmation?: TierConfirmationPayload | null;
  /** Ted #6 — corrected actual check-out instant (ISO 8601), if any. */
  checkedOutAt?: string | null;
}): Promise<AdminBooking> {
  // Build the notes envelope — same `tier_confirmation` slot when applicable,
  // plus a `settle_later: true` discriminator.
  const envelope = JSON.stringify({
    settle_later: true,
    ...(args.tierConfirmation
      ? { tier_confirmation: args.tierConfirmation }
      : {}),
  });
  const combinedNotes = args.operatorNotes
    ? `${args.operatorNotes}\n---\n${envelope}`
    : envelope;

  return postBookingAction(
    args.apiUrl,
    args.token,
    args.booking.id,
    'check-out',
    {
      notes: combinedNotes,
      settle_later: true,
      // Ted #6 — corrected actual check-out instant rides the same POST.
      ...(args.checkedOutAt ? { checked_out_at: args.checkedOutAt } : {}),
      ...(args.tierConfirmation
        ? {
            tier_confirmation: args.tierConfirmation,
            // #251 review fix — settle-later shares the bug class: a
            // later card charge from the queue recalculates from the
            // booking row, so the confirmed total must land on it too.
            final_total_cents: args.tierConfirmation.final_total_cents,
          }
        : {}),
    },
  );
}

// ─── adminAimApi (Lane D2 / D5 — AIM staff chat) ───────────────────────────

export interface AimChatResponse {
  conversationId: string;
  message: string;
  toolsUsed?: string[];
  modelUsed?: string;
  /** stop_reason === 'max_tokens' — reply is incomplete; UI shows a note. */
  truncated?: boolean;
}

export interface AimConversationSummary {
  id: string;
  title: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface AimHistoryMessage {
  id: string;
  role: string; // 'user' | 'assistant'
  content: string;
  createdAt: string;
}

/**
 * AIM staff chat endpoints (server: /api/v2/admin/ai, Lane D2/D4).
 * Gated server-side by ai-manager:use:all via the deny-by-default admin
 * guard; the nav/route in this app gates on the same matrix mirror.
 * 503 `ai_unavailable` = ANTHROPIC_API_KEY unset; 429 = per-staff rate
 * limit — both surface as result.error strings via `request<T>`.
 */
export const adminAimApi = {
  chat: (
    apiUrl: string,
    message: string,
    conversationId?: string,
  ): Promise<ApiResponse<AimChatResponse>> =>
    // Review nit — the chat endpoint's 503 (ai_unavailable) and 429
    // (rate_limited) are deterministic service states, not cold-start
    // signatures: retrying 3x just delays the banner ~7s. maxRetries: 0.
    // preferBodyMessage (#429): AIM error bodies pair a machine code with
    // a friendly `message` — surface the sentence, not the code. Bodies
    // without a message (429 rate_limited, the global handler's
    // internal_server_error) still yield the code, which the panel maps.
    request<AimChatResponse>(
      apiUrl,
      '/api/v2/admin/ai/chat',
      {
        method: 'POST',
        body: JSON.stringify({
          message,
          ...(conversationId ? { conversationId } : {}),
        }),
      },
      { maxRetries: 0, preferBodyMessage: true },
    ),
  listConversations: (
    apiUrl: string,
  ): Promise<ApiResponse<{ conversations: AimConversationSummary[] }>> =>
    api.get<{ conversations: AimConversationSummary[] }>(
      apiUrl,
      '/api/v2/admin/ai/conversations',
    ),
  getMessages: (
    apiUrl: string,
    conversationId: string,
  ): Promise<ApiResponse<{ conversationId: string; messages: AimHistoryMessage[] }>> =>
    api.get<{ conversationId: string; messages: AimHistoryMessage[] }>(
      apiUrl,
      `/api/v2/admin/ai/conversations/${conversationId}/messages`,
    ),
  archiveConversation: (
    apiUrl: string,
    conversationId: string,
  ): Promise<ApiResponse<{ conversation: { id: string; status: string } }>> =>
    api.delete<{ conversation: { id: string; status: string } }>(
      apiUrl,
      `/api/v2/admin/ai/conversations/${conversationId}`,
    ),
};

// ─── walkerApi ──────────────────────────────────────────────────────────
// Walker persona (#133) — today's walk assignments, start/complete actions.
// Server injects tenantId via req.tenant (Stage 1.5 middleware); no
// client-side tenantId needed. All endpoints require bookings.walking:*:self.

export interface WalkAssignment {
  id: string;
  date: string;
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled';
  startTime: string | null;
  notes: string | null;
  startedAt: string | null;
  completedAt: string | null;
  walkerNotes: string | null;
  dog: { id: string; name: string; breed: string | null };
  customer: { id: string; firstName: string; lastName: string };
}

export interface WalkAssignmentsResponse {
  assignments: WalkAssignment[];
  date: string;
}

export const walkerApi = {
  /** Today's walk assignments for the authenticated walker. */
  getAssignments: (
    apiUrl: string,
    date: string,
  ): Promise<ApiResponse<WalkAssignmentsResponse>> =>
    api.get<WalkAssignmentsResponse>(
      apiUrl,
      `/api/v2/admin/walks/assignments?date=${encodeURIComponent(date)}`,
    ),
  /** Mark a walk as started — sets startedAt timestamp. */
  startWalk: (
    apiUrl: string,
    assignmentId: string,
    notes?: string,
  ): Promise<ApiResponse<{ assignment: WalkAssignment }>> =>
    api.post<{ assignment: WalkAssignment }>(
      apiUrl,
      `/api/v2/admin/walks/${assignmentId}/start`,
      { notes },
    ),
  /** Mark a walk as completed — sets completedAt + walkerNotes. */
  completeWalk: (
    apiUrl: string,
    assignmentId: string,
    notes: string,
  ): Promise<ApiResponse<{ assignment: WalkAssignment }>> =>
    api.post<{ assignment: WalkAssignment }>(
      apiUrl,
      `/api/v2/admin/walks/${assignmentId}/complete`,
      { notes },
    ),
};

// ─── careLogApi (#721) ──────────────────────────────────────────────────
// The digital meal / medication administration log. APPEND-ONLY by
// contract: there is no update and no delete here because the server has no
// such route — a correction is a new entry, and both rows survive. That is
// what makes the log a PACFA-grade administration record (#553) rather than
// a status field, so do not add a PATCH helper "for convenience".
//
// The WRITE is `pets.notes:write:all` (the #649 append-only-observation
// class), so floor roles — walker, groomer, kennel_tech — can log what they
// saw. The READ is `pets:read:all`.

export type CareEventKind = 'meal' | 'medication';
export type MealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export type MealOutcome = 'ate_all' | 'ate_partial' | 'refused';
export type MedicationSlot = 'am' | 'midday' | 'pm' | 'bedtime' | 'as_needed';
/**
 * Medication outcomes as STORED. Sawyer 08 meds-feeding (A2) added the med pass's
 * 'prepared' and 'partially_administered' (migration 20261025100802).
 */
export type MedicationOutcome = 'given' | 'refused' | 'missed' | 'prepared' | 'partially_administered';
/** What the per-dog care log may POST — the #721 set (refuter C-10). */
export type LogMedicationOutcome = 'given' | 'refused' | 'missed';

export interface StaffCareEvent {
  id: string;
  kind: string;
  slot: string;
  /** May be the legacy 'logged' on rows imported from the old feeding hack. */
  outcome: string;
  notes: string | null;
  parentVisible: boolean;
  occurredAt: string;
  staffUserId: string | null;
  staffName: string | null;
  bookingId: string | null;
  medicationId: string | null;
}

export interface CareStreak {
  slot: MealSlot;
  days: number;
  lastRefusedOn: string;
}

/**
 * Sawyer #1295 (10-29) — the meals a dog ate none of on the page's facility
 * day, ready-made by the server (core `computeAteNoneOn`). Raised by the
 * FIRST "Ate none" meal; one entry per meal. Always read through
 * `parseAteNone` (components/care-log/AteNoneBadge): an older server omits
 * it and the surface renders nothing.
 */
export interface AteNoneWarning {
  meals: MealSlot[];
  /** "Ate none at breakfast" / "Ate none: breakfast, lunch". */
  label: string;
  /** Longest consecutive-day run among those meals (badge severity). */
  days: number;
}

export interface CareLogResponse {
  careEvents: StaffCareEvent[];
  streaks: CareStreak[];
  worstStreak: CareStreak | null;
  /** Ready-made copy — "Ate none at dinner 2 days running". Null when none. */
  streakLabel: string | null;
  /** Sawyer #1295 — today's "Ate none" meals; absent on an older server. */
  ateNone?: AteNoneWarning | null;
  /**
   * Whether this tenant has turned on pet-parent visibility at all. The UI
   * uses it to tell a staffer whether ticking "share with owner" will
   * actually do anything, instead of implying it will.
   */
  tenantParentVisibilityEnabled: boolean;
}

export type CreateCareEventBody =
  | {
      kind: 'meal';
      slot: MealSlot;
      outcome: MealOutcome;
      notes?: string;
      parentVisible?: boolean;
      occurredAt?: string;
    }
  | {
      kind: 'medication';
      slot: MedicationSlot;
      outcome: LogMedicationOutcome;
      notes?: string;
      medicationId?: string;
      /**
       * Accepted but INERT in v1 — medication outcomes never reach a pet
       * parent regardless, enforced in the server's customer serializer.
       */
      parentVisible?: boolean;
      occurredAt?: string;
    };

export const careLogApi = {
  /** History + the derived streak for one dog, in ONE call. */
  get: (
    apiUrl: string,
    dogId: string,
    days = 14,
  ): Promise<ApiResponse<CareLogResponse>> =>
    api.get<CareLogResponse>(
      apiUrl,
      `/api/admin/dogs/${encodeURIComponent(dogId)}/care-events?days=${days}`,
    ),
  /** Append one outcome. There is no edit — a correction is another append. */
  append: (
    apiUrl: string,
    dogId: string,
    body: CreateCareEventBody,
  ): Promise<ApiResponse<{ careEvent: StaffCareEvent }>> =>
    api.post<{ careEvent: StaffCareEvent }>(
      apiUrl,
      `/api/admin/dogs/${encodeURIComponent(dogId)}/care-events`,
      body,
    ),
  /** The per-tenant parent-visibility toggle. PUT is owner + manager. */
  getConfig: (
    apiUrl: string,
  ): Promise<ApiResponse<{ parentVisible: boolean; timezone: string }>> =>
    api.get<{ parentVisible: boolean; timezone: string }>(
      apiUrl,
      '/api/admin/care-log-config',
    ),
  setConfig: (
    apiUrl: string,
    parentVisible: boolean,
  ): Promise<ApiResponse<{ parentVisible: boolean }>> =>
    api.put<{ parentVisible: boolean }>(apiUrl, '/api/admin/care-log-config', {
      parentVisible,
    }),
};

// ─── kennelApi ──────────────────────────────────────────────────────────
// Kennel/yard tech persona (#134) — on-site roster, feeding/med log, run assignment.
// All endpoints require bookings.daycare-boarding:read:self and vaccinations:read:self.

export interface OnSiteAnimal {
  bookingId: string;
  dogId: string;
  dogName: string;
  breed: string | null;
  sizeCategory: string | null;
  runAssignment: string | null;
  checkInTime: string | null;
  feedingSchedule: string | null;
  medicationNotes: string | null;
  behaviorNotes: string | null;
  vaccineStatus: 'current' | 'expiring' | 'expired' | 'unverified';
  customerId: string;
  customerFirstName: string;
  customerLastName: string;
  /** #721 — "Ate none at dinner 2 days running", or null when there is none. */
  careStreakLabel: string | null;
  careStreakDays: number;
  /** Sawyer #1295 — the meal that streak is; absent on an older server. */
  careStreakSlot?: MealSlot | null;
  /** Sawyer #1295 — the roster day's "Ate none" meals; absent on an older server. */
  ateNone?: AteNoneWarning | null;
  /** Ted #28 (#1050) — the dog's + stay's flags; absent on an older server. */
  flags?: DogFlag[];
}

export interface OnSiteRosterResponse {
  animals: OnSiteAnimal[];
  date: string;
  total: number;
}

export interface FeedingLogEntry {
  id: string;
  dogId: string;
  staffId: string;
  loggedAt: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  notes: string | null;
  /** #721 — 'logged' means the meal was served with no outcome recorded. */
  outcome: MealOutcome | 'logged';
}

export const kennelApi = {
  /** On-site roster for today — dogs currently checked in. */
  getRoster: (apiUrl: string, date: string): Promise<ApiResponse<OnSiteRosterResponse>> =>
    api.get<OnSiteRosterResponse>(
      apiUrl,
      `/api/v2/admin/kennel/roster?date=${encodeURIComponent(date)}`,
    ),
  /** Log a feeding event for a dog. */
  /**
   * #721 — `outcome` is OPTIONAL on the wire. The server records 'logged'
   * ("served, outcome not recorded") when it is absent rather than guessing
   * 'ate_all', so an older build of this app can never manufacture a health
   * signal. The kennel UI always sends one.
   */
  logFeeding: (
    apiUrl: string,
    dogId: string,
    mealType: FeedingLogEntry['mealType'],
    outcome?: MealOutcome,
    notes?: string,
  ): Promise<ApiResponse<{ entry: FeedingLogEntry }>> =>
    api.post<{ entry: FeedingLogEntry }>(apiUrl, '/api/v2/admin/kennel/feeding', {
      dogId,
      mealType,
      outcome,
      notes,
    }),
  /** Assign a run/kennel slot to a dog for the current stay. */
  assignRun: (
    apiUrl: string,
    bookingId: string,
    runAssignment: string,
  ): Promise<ApiResponse<{ bookingId: string; runAssignment: string }>> =>
    api.post<{ bookingId: string; runAssignment: string }>(
      apiUrl,
      '/api/v2/admin/kennel/run-assign',
      { bookingId, runAssignment },
    ),
};

// ─── accountantApi ──────────────────────────────────────────────────────
// Accountant persona (#135) — read-only revenue/tips/transactions + CSV export.
// All endpoints require revenue:read:all and payments:read:all.

export interface AccountantRevenueRow {
  date: string;
  serviceName: string;
  totalCents: number;
  paymentMethod: string;
  status: 'settled' | 'refunded' | 'void';
  transactionRef: string | null;
}

export interface AccountantRevenueResponse {
  rows: AccountantRevenueRow[];
  totalCents: number;
  periodStart: string;
  periodEnd: string;
}

export interface AccountantPayoutRow {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  status: 'pending' | 'paid' | 'failed';
  paidAt: string | null;
}

export interface AccountantPayoutsResponse {
  payouts: AccountantPayoutRow[];
}

export interface AccountantTipRow {
  date: string;
  staffName: string;
  amountCents: number;
  bookingId: string;
}

export interface AccountantTipsResponse {
  tips: AccountantTipRow[];
  totalCents: number;
}

export const accountantApi = {
  /** Revenue transactions for a date range (ISO dates). */
  getRevenue: (
    apiUrl: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ApiResponse<AccountantRevenueResponse>> => {
    const p = new URLSearchParams({ periodStart, periodEnd });
    return api.get<AccountantRevenueResponse>(
      apiUrl,
      `/api/v2/admin/accountant/revenue?${p}`,
    );
  },
  /** Payout history. */
  getPayouts: (apiUrl: string): Promise<ApiResponse<AccountantPayoutsResponse>> =>
    api.get<AccountantPayoutsResponse>(apiUrl, '/api/v2/admin/accountant/payouts'),
  /** Tips breakdown. */
  getTips: (
    apiUrl: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ApiResponse<AccountantTipsResponse>> => {
    const p = new URLSearchParams({ periodStart, periodEnd });
    return api.get<AccountantTipsResponse>(apiUrl, `/api/v2/admin/accountant/tips?${p}`);
  },
  /** CSV export — returns a Blob URL the caller can trigger a download from. */
  exportCsv: (
    apiUrl: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ApiResponse<{ url: string }>> => {
    const p = new URLSearchParams({ periodStart, periodEnd });
    return api.get<{ url: string }>(apiUrl, `/api/v2/admin/accountant/export?${p}`);
  },
};

// ─── groomerQueueApi ─────────────────────────────────────────────────────
// Groomer persona (#290) — queue of today's grooming appointments with
// sub-service details, notes, and before/after photo upload.
// All endpoints require bookings.grooming:*:self.

export interface GroomerQueueItem {
  id: string;
  bookingId: string;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  scheduledTime: string | null;
  dogId: string;
  dogName: string;
  breed: string | null;
  sizeCategory: string | null;
  subService: string | null;
  subServiceDisplay: string | null;
  addOns: string[];
  groomingNotes: string | null;
  beforePhotoUrl: string | null;
  afterPhotoUrl: string | null;
  customerId: string;
  customerFirstName: string;
  customerLastName: string;
  totalCents: number;
}

export interface GroomerQueueResponse {
  queue: GroomerQueueItem[];
  date: string;
  total: number;
}

export const groomerQueueApi = {
  /** Groomer's queue for a given date. */
  getQueue: (apiUrl: string, date: string): Promise<ApiResponse<GroomerQueueResponse>> =>
    api.get<GroomerQueueResponse>(
      apiUrl,
      `/api/v2/admin/grooming/queue?date=${encodeURIComponent(date)}`,
    ),
  /** Start a grooming appointment — moves status to in_progress. */
  startGrooming: (
    apiUrl: string,
    queueItemId: string,
  ): Promise<ApiResponse<{ item: GroomerQueueItem }>> =>
    api.post<{ item: GroomerQueueItem }>(
      apiUrl,
      `/api/v2/admin/grooming/queue/${queueItemId}/start`,
      {},
    ),
  /** Complete a grooming appointment — sets notes + status=completed. */
  completeGrooming: (
    apiUrl: string,
    queueItemId: string,
    notes: string,
  ): Promise<ApiResponse<{ item: GroomerQueueItem }>> =>
    api.post<{ item: GroomerQueueItem }>(
      apiUrl,
      `/api/v2/admin/grooming/queue/${queueItemId}/complete`,
      { notes },
    ),
  /** Update groomer notes on a queue item. */
  updateNotes: (
    apiUrl: string,
    queueItemId: string,
    notes: string,
  ): Promise<ApiResponse<{ item: GroomerQueueItem }>> =>
    api.patch<{ item: GroomerQueueItem }>(
      apiUrl,
      `/api/v2/admin/grooming/queue/${queueItemId}/notes`,
      { notes },
    ),
};

// ─── resourceApi ─────────────────────────────────────────────────────────
// Wave 4 (W1) calendar assign write-path (#7). Mirrors the server's
// `POST /api/v2/admin/calendar/assign` route (scheduling-resources module).
// Assigning a groomer to a grooming booking writes a BookingResource row and
// denormalises the booking's groomer so the assignee's queue (GroomerHome)
// populates.
//
// The assign Zod schema requires `category:'grooming'` to carry a `date` +
// `startTime`/`endTime` window (NOT a `startDate`/`endDate` span — that form is
// boarding-only), so the grooming caller passes a same-day time window. The
// booking's own `start_time` (HH:MM) seats the window; absent that, a default
// is used.

/** One assignment row as the server returns it (`assignmentView`). */
export interface CalendarAssignment {
  id: string;
  bookingId: string;
  resourceUnitId: string | null;
  playGroupId: string | null;
  staffUserId: string | null;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  exclusive: boolean;
}

/** Default grooming appointment length (minutes) when the booking has none. */
const DEFAULT_GROOMING_SLOT_MINUTES = 60;
/** Default grooming start when the booking carries no `start_time`. */
const DEFAULT_GROOMING_START = '09:00';

/** Add `minutes` to an `HH:MM` clock time, clamped to 23:59 (same-day). */
function addMinutesHHMM(time: string, minutes: number): string {
  const [h, m] = time.split(':').map((n) => Number.parseInt(n, 10));
  const total = Math.min(23 * 60 + 59, (h ?? 0) * 60 + (m ?? 0) + minutes);
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export const resourceApi = {
  /**
   * Assign a groomer (StaffUser) to a grooming booking. POSTs the grooming
   * assignment shape to the calendar assign endpoint; on success the server
   * has both written the BookingResource row and pointed the booking at the
   * groomer, so the groomer's queue picks it up.
   *
   * `startTime` defaults to the booking's `start_time` (resolved by the
   * caller) or 09:00; `endTime` defaults to startTime + 60m.
   */
  assignGroomer: (
    apiUrl: string,
    token: string,
    params: {
      bookingId: string;
      staffUserId: string;
      date: string;
      startTime?: string | null;
      endTime?: string | null;
    },
  ): Promise<ApiResponse<{ assignment: CalendarAssignment }>> => {
    const startTime =
      params.startTime && params.startTime.length > 0
        ? params.startTime
        : DEFAULT_GROOMING_START;
    const endTime =
      params.endTime && params.endTime.length > 0
        ? params.endTime
        : addMinutesHHMM(startTime, DEFAULT_GROOMING_SLOT_MINUTES);

    const body = {
      bookingId: params.bookingId,
      category: 'grooming' as const,
      staffUserId: params.staffUserId,
      // Omit resourceUnitId entirely — the assign schema makes it
      // optional-but-NOT-nullable; for a groomer assignment staffUserId is the
      // single target. Sending `resourceUnitId: null` 400s ("expected string,
      // received null") — verified live against POST /api/v2/admin/calendar/assign.
      date: params.date,
      startTime,
      endTime,
    };

    return (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/v2/admin/calendar/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          // #711 review fix — this endpoint's REAL business errors are
          // machine codes (resource_conflict / off_shift / no_capacity)
          // and rendered raw in the groomer-assignment UI. parseErrorBody
          // sanitizes them (dictionary copy), fires the 401 auto-logout,
          // and preserves the token on `code` for typed branching.
          let body: unknown;
          try {
            body = await res.json();
          } catch {
            body = undefined; // Body wasn't JSON; fallback carries the status.
          }
          const parsed = parseErrorBody(res.status, body, {
            fallback: `Failed to assign groomer (${res.status})`,
          });
          return {
            error: parsed.message,
            ...(parsed.code ? { code: parsed.code } : {}),
          };
        }
        const data = (await res.json()) as { assignment: CalendarAssignment };
        return { data };
      } catch (err) {
        return {
          error:
            err instanceof Error
              ? err.message
              : 'Unable to reach the server. Please try again.',
          isRetryable: isRetryableError(err),
        };
      }
    })();
  },
};

// ─── #427 W1 — Time clock (spec: docs/STAFF-HR-MODULE-DESIGN-2026-07-13.md §4.1)
// Wire: bodies camelCase (zod-parsed server-side), responses snake_case —
// same convention as the staff-schedules endpoints. Day windows are sent
// as explicit UTC instants (`from`/`to`): the CLIENT owns local-day math.

export interface TimeclockBreak {
  id: string;
  time_entry_id: string;
  kind: string; // 'rest' | 'meal'
  paid: boolean;
  start_at: string;
  end_at: string | null;
  source: string;
}

export interface TimeclockEntry {
  id: string;
  staff_user_id: string;
  staff_name: string;
  clock_in_at: string;
  clock_out_at: string | null;
  clock_in_source: string; // 'self' | 'kiosk' | 'admin'
  clock_out_source: string | null;
  voided_at: string | null;
  void_reason: string | null;
  breaks: TimeclockBreak[];
  // Sawyer 14 early-clockin-gate (E1). Optional on purpose: a server
  // without this lane omits them, and every reader treats absent as
  // "not early" (deploy window, AC21).
  shift_id?: string | null;
  early?: TimeclockEarly | null;
  /** When pay starts; differs from clock_in_at for a pending/trimmed early start. */
  paid_from?: string;
}

/** Sawyer 14 (E1): an early clock-in and the manager's decision on it. */
export interface TimeclockEarly {
  status: 'pending' | 'approved' | 'trimmed';
  scheduled_start_at: string;
  minutes_early: number;
  decided_by_name: string | null;
  decided_at: string | null;
}

export interface TimeclockMeToday {
  open: TimeclockEntry | null;
  entries: TimeclockEntry[];
}

export interface TimeclockSettingsView {
  ip_restriction_enabled: boolean;
  allowed_cidrs: string[];
  on_duty_meal_paid: boolean;
  /** Sawyer 14 (E1): absent on an older server; null = gate off. */
  early_clock_in_allowance_minutes?: number | null;
}

export interface TimeclockKiosk {
  id: string;
  name: string;
  last_seen_at: string | null;
  last_seen_ip: string | null;
  revoked_at: string | null;
  created_at: string;
}

export const adminTimeclockApi = {
  clockIn: (
    apiUrl: string,
    deviceId?: string,
  ): Promise<ApiResponse<{ entry: TimeclockEntry; early_notice?: string | null }>> =>
    api.post<{ entry: TimeclockEntry; early_notice?: string | null }>(
      apiUrl,
      '/api/v2/admin/timeclock/clock-in',
      deviceId ? { deviceId } : {},
    ),
  clockOut: (
    apiUrl: string,
    deviceId?: string,
  ): Promise<ApiResponse<{ entry: TimeclockEntry }>> =>
    api.post<{ entry: TimeclockEntry }>(
      apiUrl,
      '/api/v2/admin/timeclock/clock-out',
      deviceId ? { deviceId } : {},
    ),
  startBreak: (
    apiUrl: string,
    kind: 'rest' | 'meal',
  ): Promise<ApiResponse<{ break: TimeclockBreak }>> =>
    api.post<{ break: TimeclockBreak }>(
      apiUrl,
      '/api/v2/admin/timeclock/breaks/start',
      { kind },
    ),
  endBreak: (apiUrl: string): Promise<ApiResponse<{ break: TimeclockBreak }>> =>
    api.post<{ break: TimeclockBreak }>(
      apiUrl,
      '/api/v2/admin/timeclock/breaks/end',
      {},
    ),
  meToday: (
    apiUrl: string,
    fromIso: string,
    toIso: string,
  ): Promise<ApiResponse<TimeclockMeToday>> =>
    api.get<TimeclockMeToday>(
      apiUrl,
      `/api/v2/admin/timeclock/me/today?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
    ),
  setMyPin: (apiUrl: string, pin: string): Promise<ApiResponse<{ ok: boolean }>> =>
    api.put<{ ok: boolean }>(apiUrl, '/api/v2/admin/timeclock/me/kiosk-pin', {
      pin,
    }),
  setStaffPin: (
    apiUrl: string,
    staffUserId: string,
    pin: string,
  ): Promise<ApiResponse<{ ok: boolean }>> =>
    api.put<{ ok: boolean }>(
      apiUrl,
      `/api/v2/admin/timeclock/staff/${encodeURIComponent(staffUserId)}/kiosk-pin`,
      { pin },
    ),
  entries: (
    apiUrl: string,
    fromIso: string,
    toIso: string,
    staffUserId?: string,
  ): Promise<ApiResponse<{ entries: TimeclockEntry[] }>> =>
    api.get<{ entries: TimeclockEntry[] }>(
      apiUrl,
      `/api/v2/admin/timeclock/entries?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}${
        staffUserId ? `&staffUserId=${encodeURIComponent(staffUserId)}` : ''
      }`,
    ),
  editEntry: (
    apiUrl: string,
    entryId: string,
    patch: { clockInAt?: string; clockOutAt?: string | null; reason: string },
  ): Promise<ApiResponse<{ entry: TimeclockEntry }>> =>
    api.patch<{ entry: TimeclockEntry }>(
      apiUrl,
      `/api/v2/admin/timeclock/entries/${encodeURIComponent(entryId)}`,
      patch,
    ),
  voidEntry: (
    apiUrl: string,
    entryId: string,
    reason: string,
  ): Promise<ApiResponse<{ entry: TimeclockEntry }>> =>
    api.post<{ entry: TimeclockEntry }>(
      apiUrl,
      `/api/v2/admin/timeclock/entries/${encodeURIComponent(entryId)}/void`,
      { reason },
    ),
  addManualEntry: (
    apiUrl: string,
    data: {
      staffUserId: string;
      clockInAt: string;
      clockOutAt: string;
      reason: string;
      /** Sawyer 14 (E3): only sent when the server advertises the lane. */
      mealBreak?: { startAt: string; endAt: string; paid: boolean };
    },
  ): Promise<ApiResponse<{ entry: TimeclockEntry }>> =>
    api.post<{ entry: TimeclockEntry }>(
      apiUrl,
      '/api/v2/admin/timeclock/entries',
      data,
    ),
  // Sawyer 14 early-clockin-gate (E1): the waiting list + the decision.
  pendingEarly: (
    apiUrl: string,
  ): Promise<ApiResponse<{ entries: TimeclockEntry[] }>> =>
    api.get<{ entries: TimeclockEntry[] }>(
      apiUrl,
      '/api/v2/admin/timeclock/entries/pending-early',
    ),
  decideEarly: (
    apiUrl: string,
    entryId: string,
    body: { decision: 'approve' | 'trim'; note?: string },
  ): Promise<ApiResponse<{ entry: TimeclockEntry }>> =>
    api.post<{ entry: TimeclockEntry }>(
      apiUrl,
      `/api/v2/admin/timeclock/entries/${encodeURIComponent(entryId)}/early-decision`,
      body,
    ),
  getSettings: (
    apiUrl: string,
  ): Promise<ApiResponse<{ settings: TimeclockSettingsView }>> =>
    api.get<{ settings: TimeclockSettingsView }>(
      apiUrl,
      '/api/v2/admin/timeclock/settings',
    ),
  updateSettings: (
    apiUrl: string,
    patch: {
      ipRestrictionEnabled?: boolean;
      allowedCidrs?: string[];
      onDutyMealPaid?: boolean;
      earlyClockInAllowanceMinutes?: number | null;
    },
  ): Promise<ApiResponse<{ settings: TimeclockSettingsView }>> =>
    api.put<{ settings: TimeclockSettingsView }>(
      apiUrl,
      '/api/v2/admin/timeclock/settings',
      patch,
    ),
  listKiosks: (
    apiUrl: string,
  ): Promise<ApiResponse<{ kiosks: TimeclockKiosk[] }>> =>
    api.get<{ kiosks: TimeclockKiosk[] }>(
      apiUrl,
      '/api/v2/admin/timeclock/kiosks',
    ),
  registerKiosk: (
    apiUrl: string,
    name: string,
  ): Promise<
    ApiResponse<{ kiosk: { id: string; name: string }; device_token: string }>
  > =>
    api.post<{ kiosk: { id: string; name: string }; device_token: string }>(
      apiUrl,
      '/api/v2/admin/timeclock/kiosks',
      { name },
    ),
  revokeKiosk: (
    apiUrl: string,
    kioskId: string,
  ): Promise<ApiResponse<{ ok: boolean }>> =>
    api.post<{ ok: boolean }>(
      apiUrl,
      `/api/v2/admin/timeclock/kiosks/${encodeURIComponent(kioskId)}/revoke`,
      {},
    ),
};

// ─── #431 — first-login welcome card (self-scoped) ────────────────────
// Server-side dismissal, deliberately NOT localStorage: staff share a
// kiosk tablet and switch browsers, so a device-local flag would re-show a
// dismissed card on the next device and hide it on one where the person
// has never seen it. Both calls address the CALLER only — the server takes
// the staff id from the JWT, never from a parameter, so there is no way to
// read or dismiss another staffer's card.

export type StaffWelcomeState = {
  /** ISO timestamp of the first dismissal, or null when never dismissed. */
  dismissed_at: string | null;
};

export const adminStaffWelcomeApi = {
  get: (apiUrl: string): Promise<ApiResponse<{ welcome: StaffWelcomeState }>> =>
    api.get<{ welcome: StaffWelcomeState }>(
      apiUrl,
      '/api/v2/admin/staff/me/welcome',
    ),
  dismiss: (
    apiUrl: string,
  ): Promise<ApiResponse<{ welcome: StaffWelcomeState }>> =>
    api.post<{ welcome: StaffWelcomeState }>(
      apiUrl,
      '/api/v2/admin/staff/me/welcome/dismiss',
      {},
    ),
};

// ─── #427 W3 — Timesheets & Payroll export ────────────────────────────
// Wire shapes mirror the server serializers EXACTLY (do not reshape):
//   - `timesheetRowView` in packages/core/modules/staff-payroll/timesheets/
//     service.ts (the per-staff computed row)
//   - buildPayrollRouter / buildTimesheetsRouter response bodies in
//     .../timesheets/routes.ts
// Money is always integer CENTS; time is always integer MINUTES; period
// dates are YYYY-MM-DD and period_end is INCLUSIVE. `null` money columns are
// an HONEST "no rate on file" — never a fake $0 (Design Philosophy P5).

/** Which of the three CO OT triggers produced the winning (greatest) amount. */
export type OvertimeBasis = 'weekly' | 'daily' | 'consecutive';

export type TimesheetFlag =
  // an un-clocked-out entry overlaps the period — BLOCKS approval
  | 'open_entry'
  // a >5h shift with no qualifying duty-free meal
  | 'missing_meal_gt5h'
  // no PayRate on file — hours compute, money columns stay EMPTY (never $0)
  | 'no_rate_set';

/** One flagged rest instance for a single workday — "at risk" until a
 *  manager attests provided/missed (spec D3). */
export type RestFlagInstance = {
  /** YYYY-MM-DD workday. */
  date: string;
  /** Rest periods the workday's hours entitle. */
  owed: number;
  /** Rest breaks actually punched that day. */
  punched: number;
  /** owed − punched, floored at 0 — the count that may become owed wages. */
  missing: number;
};

/** Per-instance attestation supplied at approval time (spec D3). */
export type RestAttestationInput = {
  /** YYYY-MM-DD workday the flagged instance belongs to. */
  date: string;
  /** true = rest was actually provided (no wages); false = missed → wages. */
  provided: boolean;
};

/** A fully-computed timesheet row (mirror of `timesheetRowView`). */
export type TimesheetRowView = {
  staff_user_id: string;
  employee_name: string;
  email: string;
  period_start: string;
  period_end: string;
  regular_minutes: number;
  overtime_minutes: number;
  ot_basis: OvertimeBasis | null;
  paid_break_minutes: number;
  unpaid_break_minutes: number;
  declared_tips_cents: number;
  rest_flags: RestFlagInstance[];
  rest_periods_flagged: number;
  rest_periods_attested_missed: number;
  missed_rest_wages_owed_cents: number;
  flags: TimesheetFlag[];
  hourly_rate_cents: number | null;
  gross_regular_cents: number | null;
  gross_overtime_cents: number | null;
  gross_total_cents: number | null;
  rate_changed_in_period: boolean;
  /** Sawyer 15 (E2) — a saved TimesheetApproval exists for this staff +
   *  period (the server always sent it; the type dropped it, so approvals
   *  looked lost after a reload). Optional: absent reads as not approved. */
  approved?: boolean;
};

export type TimesheetsListResponse = {
  period_start: string;
  period_end: string;
  rows: TimesheetRowView[];
};

export type PayRateView = {
  hourly_rate_cents: number;
  /** YYYY-MM-DD the rate takes effect (inclusive). */
  effective_from: string;
};

export type PayrollExportSummary = {
  id: string;
  /** Sawyer 15 (E2) — 'csv' (per period) | 'csv_workweek' (week by week).
   *  Absent from an older server: read as per period. */
  format?: string;
  period_start: string;
  period_end: string;
  file_name: string;
  checksum_sha256: string;
  totals: unknown;
  created_at: string;
};

/** Result of a CSV export: the streamed body plus its provenance headers so
 *  the caller can trigger a browser download AND surface the frozen export
 *  id + checksum (the accountant artifact, #135). */
export type PayrollCsvExport = {
  blob: Blob;
  fileName: string;
  exportId: string | null;
  checksumSha256: string | null;
  /** Sawyer 15 (E2) — which file came back ('csv' = per period,
   *  'csv_workweek' = week by week). */
  format: 'csv' | 'csv_workweek';
};

const periodQs = (periodStart: string, periodEnd: string): string =>
  `period_start=${encodeURIComponent(periodStart)}&period_end=${encodeURIComponent(periodEnd)}`;

export const adminTimesheetsApi = {
  /** GET /api/v2/admin/timesheets — all staff rows for the period. */
  list: (
    apiUrl: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ApiResponse<TimesheetsListResponse>> =>
    api.get<TimesheetsListResponse>(
      apiUrl,
      `/api/v2/admin/timesheets?${periodQs(periodStart, periodEnd)}`,
    ),
  /** GET /api/v2/admin/timesheets/:id/daily — one staff member's row. */
  daily: (
    apiUrl: string,
    staffUserId: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ApiResponse<{ row: TimesheetRowView }>> =>
    api.get<{ row: TimesheetRowView }>(
      apiUrl,
      `/api/v2/admin/timesheets/${encodeURIComponent(staffUserId)}/daily?${periodQs(periodStart, periodEnd)}`,
    ),
  /** POST /api/v2/admin/timesheets/approve — attest + approve. */
  approve: (
    apiUrl: string,
    body: {
      staffUserId: string;
      periodStart: string;
      periodEnd: string;
      attestations: RestAttestationInput[];
    },
  ): Promise<ApiResponse<{ approved: boolean; row: TimesheetRowView }>> =>
    api.post<{ approved: boolean; row: TimesheetRowView }>(
      apiUrl,
      '/api/v2/admin/timesheets/approve',
      {
        staff_user_id: body.staffUserId,
        period_start: body.periodStart,
        period_end: body.periodEnd,
        attestations: body.attestations,
      },
    ),
  /** POST /api/v2/admin/timesheets/me/tips — staff self-declares a day's
   *  tips at clock-out (tips:write:self). */
  declareMyTips: (
    apiUrl: string,
    body: { date: string; amountCents: number; note?: string },
  ): Promise<ApiResponse<{ declared: boolean }>> =>
    api.post<{ declared: boolean }>(
      apiUrl,
      '/api/v2/admin/timesheets/me/tips',
      {
        date: body.date,
        amount_cents: body.amountCents,
        ...(body.note ? { note: body.note } : {}),
      },
    ),
};

export const adminPayrollApi = {
  /** GET /api/v2/admin/payroll/exports — frozen export history. */
  listExports: (
    apiUrl: string,
  ): Promise<ApiResponse<{ exports: PayrollExportSummary[] }>> =>
    api.get<{ exports: PayrollExportSummary[] }>(
      apiUrl,
      '/api/v2/admin/payroll/exports',
    ),
  /** GET /api/v2/admin/payroll/rates/:id — a staff member's rate history. */
  getRates: (
    apiUrl: string,
    staffUserId: string,
  ): Promise<ApiResponse<{ staff_user_id: string; rates: PayRateView[] }>> =>
    api.get<{ staff_user_id: string; rates: PayRateView[] }>(
      apiUrl,
      `/api/v2/admin/payroll/rates/${encodeURIComponent(staffUserId)}`,
    ),
  /** PUT /api/v2/admin/payroll/rates/:id — owner sets a new effective rate
   *  (payroll.rates:write:all). Cents must be a positive integer. */
  setRate: (
    apiUrl: string,
    staffUserId: string,
    body: { hourlyRateCents: number; effectiveFrom: string },
  ): Promise<ApiResponse<{ ok: boolean }>> =>
    api.put<{ ok: boolean }>(
      apiUrl,
      `/api/v2/admin/payroll/rates/${encodeURIComponent(staffUserId)}`,
      {
        hourly_rate_cents: body.hourlyRateCents,
        effective_from: body.effectiveFrom,
      },
    ),
};

/**
 * POST /api/v2/admin/payroll/export — streams a text/csv body (NOT JSON), so
 * this bypasses `request<T>` and reads the response as a Blob plus its
 * provenance headers (X-Payroll-Export-Id / X-Payroll-Checksum-Sha256). The
 * caller triggers the browser download from the returned blob. Throws
 * `ApiError` on a non-2xx (the JSON error body is surfaced when present).
 */
export async function exportPayrollCsv(
  apiUrl: string,
  periodStart: string,
  periodEnd: string,
  // Sawyer 15 workweek-hours-ot (E2) — which file, and the explicit
  // review-first override ("Export anyway"). A 409 export_needs_review throws
  // an ApiError with code 'export_needs_review'.
  opts: { format?: 'period' | 'workweek'; confirmUnreviewed?: boolean } = {},
): Promise<PayrollCsvExport> {
  const wantWorkweek = opts.format === 'workweek';
  const { token } = readSession();
  const res = await fetch(`${apiUrl}/api/v2/admin/payroll/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      period_start: periodStart,
      period_end: periodEnd,
      format: wantWorkweek ? 'workweek' : 'period',
      confirm_unreviewed: opts.confirmUnreviewed === true,
    }),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Payroll export failed (${res.status})`, {
      preferBodyMessage: true,
    });
  }
  // Sawyer 15 (E2) — read the CSV as text once (UTF-8, so the Blob carries
  // the same bytes the server checksummed) so its header line can be checked.
  const csvText = await res.text();
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  // Sawyer 15 (E2) — deploy-window guard: an older server ignores `format`
  // and returns the per-period file. Never hand that over as week-by-week.
  // The server stamps X-Payroll-Export-Format, but the API's CORS config does
  // not expose custom headers to a cross-origin admin, so the CSV's own header
  // line is the check the browser can always read.
  const headerFormat = res.headers.get('X-Payroll-Export-Format');
  let format: 'csv' | 'csv_workweek' = headerFormat === 'csv_workweek' ? 'csv_workweek' : 'csv';
  if (headerFormat === null) {
    const firstLine = csvText.split(/\r?\n/, 1)[0] ?? '';
    format = firstLine.split(',').includes('workweek_start') ? 'csv_workweek' : 'csv';
  }
  if (wantWorkweek && format !== 'csv_workweek') {
    throw new ApiError(
      409,
      "The week-by-week file isn't available on the server yet. Try again after the update.",
      'workweek_export_unavailable',
    );
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+?)"?(?:;|$)/.exec(disposition);
  const fileName =
    match?.[1] ??
    (format === 'csv_workweek'
      ? `payroll_workweeks_${periodStart}_${periodEnd}.csv`
      : `payroll-${periodStart}_${periodEnd}.csv`);
  return {
    blob,
    fileName,
    exportId: res.headers.get('X-Payroll-Export-Id'),
    checksumSha256: res.headers.get('X-Payroll-Checksum-Sha256'),
    format,
  };
}

// ─── adminPricingRulesApi (#814 — owner-managed automatic pricing rules) ────
// CRUD against /api/bookings/admin/pricing-rules (booking module admin
// surface, deny-by-default guard). Reads need business-config:read:all;
// writes business-config.pricing:write:all (owner-only — the same split as
// the loyalty value card). Platform-wide rules arrive with
// `editable: false` and are display-only on tenant surfaces.

export type AdminPricingRuleType =
  | 'percentage_discount'
  | 'fixed_discount'
  | 'surcharge';

export interface AdminPricingRule {
  id: string;
  serviceTypeId: string;
  name: string;
  type: AdminPricingRuleType | string;
  valueCents: number | null;
  percentage: number | null;
  minDogs: number | null;
  minNights: number | null;
  dayOfWeek: number | null;
  isActive: boolean;
  priority: number;
  /** false = platform-wide rule (tenant surfaces render it read-only). */
  editable: boolean;
}

/** Create body — server stamps the tenant; type↔value pairing enforced there too. */
export interface AdminPricingRuleDraft {
  serviceTypeId: string;
  name: string;
  type: AdminPricingRuleType;
  valueCents?: number;
  percentage?: number;
  minDogs?: number;
  minNights?: number;
  dayOfWeek?: number;
  priority?: number;
  isActive?: boolean;
}

export type AdminPricingRulePatch = Partial<Omit<AdminPricingRuleDraft, 'serviceTypeId'>>;

export const adminPricingRulesApi = {
  list: (
    apiUrl: string,
    serviceTypeId?: string,
  ): Promise<ApiResponse<{ rules: AdminPricingRule[] }>> =>
    api.get<{ rules: AdminPricingRule[] }>(
      apiUrl,
      `/api/bookings/admin/pricing-rules${
        serviceTypeId ? `?serviceTypeId=${encodeURIComponent(serviceTypeId)}` : ''
      }`,
    ),
  create: (
    apiUrl: string,
    draft: AdminPricingRuleDraft,
  ): Promise<ApiResponse<{ rule: AdminPricingRule }>> =>
    api.post<{ rule: AdminPricingRule }>(
      apiUrl,
      '/api/bookings/admin/pricing-rules',
      draft,
    ),
  update: (
    apiUrl: string,
    id: string,
    patch: AdminPricingRulePatch,
  ): Promise<ApiResponse<{ rule: AdminPricingRule }>> =>
    api.patch<{ rule: AdminPricingRule }>(
      apiUrl,
      `/api/bookings/admin/pricing-rules/${id}`,
      patch,
    ),
  remove: (
    apiUrl: string,
    id: string,
  ): Promise<ApiResponse<{ deleted: boolean }>> =>
    api.delete<{ deleted: boolean }>(
      apiUrl,
      `/api/bookings/admin/pricing-rules/${id}`,
    ),
};

// ─── adminTenantSettingsApi (#933 — Owner Settings → Operations) ─────────
// GET/PUT /api/v2/admin/tenant/operations. Read is business-config:read:all
// (owner/manager/receptionist/accountant); write is business-config:write:all
// (owner, manager; the charging keys stay owner-only, see the server's
// tenant-settings/access.ts). The PUT is a PATCH: send only the keys that changed; the server
// merges into the tenant's row and answers with the full view plus the keys
// it actually changed. `staffingDogsPerHandler: null` clears the ratio.

export type TenantVaccinationGate = 'off' | 'warn' | 'block';
export type TenantSettingSource = 'configured' | 'default';

/** #1104 — the vaccines a facility can set a term policy for. */
export type TenantVaccineTermKey = 'rabies' | 'bordetella' | 'dhpp';
/**
 * #1104 — allowed term lengths in years: [] not calculated, [1] / [3]
 * fixed, [1, 3] the reviewer chooses.
 */
export type TenantVaccineTermPolicy = number[];
export type TenantVaccineTerms = Record<TenantVaccineTermKey, TenantVaccineTermPolicy>;

export interface TenantOperationsValues {
  defaultFacilityCapacity: number;
  vaccinationBookingGate: TenantVaccinationGate;
  careLogParentVisible: boolean;
  staffingDogsPerHandler: number | null;
  /**
   * #926 — Ted A4: bill meal tracking per night, per dog, on boarding stays
   * at checkout (a `meal_tracking` line on the invoice). Optional on the
   * wire: an older server omits them and the card reads "off / $5.00".
   */
  mealTrackingChargeEnabled?: boolean;
  mealTrackingRateCents?: number;
  /** #939 — Ted's lodging threshold (beds); null = not set; absent on an older server. */
  lodgingCapacityThreshold?: number | null;
  /**
   * #914 — Ted #11: the boarding checkout cutoff. `HH:MM`, 24-hour, in the
   * FACILITY's timezone. Optional on the wire for the same reason as #926 —
   * frontends auto-deploy on merge and the server does not, so a newer card
   * can be talking to a server that does not carry these keys yet.
   */
  boardingLatePickupChargeEnabled?: boolean;
  boardingCheckoutLocalTime?: string;
  boardingLateGraceLocalTime?: string;
  /**
   * #1104 — Ted, 2026-09-25: "Bordetella is one year, while DAPP and rabies
   * may be one or three years." Absent on an older server; the card then
   * hides the control rather than showing a policy it cannot save.
   */
  vaccineTerms?: TenantVaccineTerms;
  /**
   * Sawyer 09 pet-inactivity (D1) — months without a checked-out visit after
   * which a pet is inactive; null = off. Absent on an older server; the
   * Pet inactivity card then renders nothing (it cannot save there).
   */
  petInactivityMonths?: number | null;
  /**
   * LG (#1113) — the service names whose dogs share a play / common area for
   * the day; the intact-pair guard fires only inside this list (or a shared
   * recorded playgroup). Default daycare + daycare-half. Absent on an older
   * server; the card hides the control.
   */
  comminglingServiceNames?: string[];
  /** LG (#1111) — ask "currently in heat?" at reservation and refuse a Yes. OFF by default. */
  heatDeclarationRequired?: boolean;
  /**
   * Ted #34 E6.1 (#942) — what a half-day of daycare costs, per dog, booked
   * with a groom. null = not offered. Absent on an older server; the card
   * then hides the control.
   */
  groomDayHalfDaycareRateCents?: number | null;
  /** Ted #34 E6.2 — whole percent off a dog's first groom; null = off. Absent on an older server. */
  firstGroomDiscountPercent?: number | null;
  /**
   * #948 — whole percent off each visit when two or more of a family's dogs
   * are here together; null = off. Absent on an older server; the card then
   * hides the control.
   */
  multiDogDiscountPercent?: number | null;
  /** #948 — the service names the multi-dog discount applies to. Default daycare, half-day, boarding. */
  multiDogServices?: string[];
  /** #948 — every dog gets it, or every dog but the first. */
  multiDogAppliesTo?: TenantMultiDogAppliesTo;
  /**
   * Sawyer 10-30 — the service names whose check-in does not ask to print a
   * run card. Default daycare, half-day; [] asks on every service. Absent on
   * an older server; the card then hides the control.
   */
  runCardPromptOffServices?: string[];
  // Sawyer 06 beds-lockers-runcard (B2, B4) — absent on an older server; the
  // Lodging card then hides both controls rather than show a policy it cannot save.
  /** The size labels a bed may carry, in the owner's order. Default Small, Big; [] = beds are not sized. */
  bedSizeClasses?: string[];
  /** A stay with no locker of its own shows its bed's default locker. ON by default. */
  lockerFollowsBed?: boolean;
  /**
   * SPEC 2026-10-26 D4 — only records staff have checked count for online
   * booking. OFF by default. Absent on an older server; the card then hides
   * the control (the same posture as `heatDeclarationRequired`).
   */
  vaccinationVerifiedRecordsOnly?: boolean;
  /**
   * SPEC 2026-10-26 Story 5 — vet name and phone before a dog's first online
   * booking. Never asked at sign-up. OFF by default; absent on an older server.
   */
  vetInfoRequiredForBooking?: boolean;
}
export type TenantMultiDogAppliesTo = 'every_dog' | 'additional_dogs';
export type TenantOperationsKey = keyof TenantOperationsValues;

export interface TenantOperationsSettings {
  operations: TenantOperationsValues;
  /** Provenance for the business_meta keys only (the column has none). */
  source: {
    defaultFacilityCapacity: TenantSettingSource;
    vaccinationBookingGate: TenantSettingSource;
    staffingDogsPerHandler: TenantSettingSource;
    mealTrackingChargeEnabled?: TenantSettingSource;
    mealTrackingRateCents?: TenantSettingSource;
    /** #939 — absent on an older server. */
    lodgingCapacityThreshold?: TenantSettingSource;
    /** #914 — absent on an older server. */
    boardingLatePickupChargeEnabled?: TenantSettingSource;
    boardingCheckoutLocalTime?: TenantSettingSource;
    boardingLateGraceLocalTime?: TenantSettingSource;
    /** #1104 — configured when ANY vaccine is. */
    vaccineTerms?: TenantSettingSource;
    /** Sawyer 09 pet-inactivity (D1) — PRESENT only on a server that can save the rule. */
    petInactivityMonths?: TenantSettingSource;
    /** LG — absent on an older server. */
    comminglingServiceNames?: TenantSettingSource;
    heatDeclarationRequired?: TenantSettingSource;
    /** Ted #34 — absent on an older server. */
    groomDayHalfDaycareRateCents?: TenantSettingSource;
    firstGroomDiscountPercent?: TenantSettingSource;
    /** #948 — absent on an older server. */
    multiDogDiscountPercent?: TenantSettingSource;
    multiDogServices?: TenantSettingSource;
    multiDogAppliesTo?: TenantSettingSource;
    /** Sawyer 10-30 — absent on an older server. */
    runCardPromptOffServices?: TenantSettingSource;
    // Sawyer 06 beds-lockers-runcard (B2, B4) — absent on an older server.
    bedSizeClasses?: TenantSettingSource;
    lockerFollowsBed?: TenantSettingSource;
    // SPEC 2026-10-26 (D4, Story 5) — absent on an older server.
    vaccinationVerifiedRecordsOnly?: TenantSettingSource;
    vetInfoRequiredForBooking?: TenantSettingSource;
  };
  /** #1104 — per-vaccine provenance; absent on an older server. */
  vaccineTermsSource?: Record<TenantVaccineTermKey, TenantSettingSource>;
  limits: {
    maxFacilityCapacity: number;
    maxStaffingDogsPerHandler: number;
    /** #926 — cents per night; absent on an older server. */
    maxMealTrackingRateCents?: number;
    /** Sawyer 09 pet-inactivity (D1) — calendar months; absent on an older server. */
    minPetInactivityMonths?: number;
    maxPetInactivityMonths?: number;
    /** Ted #34 — cents per dog / whole percent; absent on an older server. */
    maxGroomDayHalfDaycareRateCents?: number;
    maxFirstGroomDiscountPercent?: number;
    /** #948 — whole percent; absent on an older server. */
    maxMultiDogDiscountPercent?: number;
  };
  lastChange: { at: string; byName: string; changedKeys: TenantOperationsKey[] } | null;
  /**
   * R2 + R23 — the tenant's ONLINE PAYMENTS MODE, read-only here (the PUT
   * does not accept it; changing it is a platform step). `effective` is what
   * customers get: 'off' = pay at pickup. Absent on an older server — the
   * card then shows nothing rather than a guess.
   */
  onlinePayments?: TenantOnlinePayments;
  /**
   * #933 D2 — what THIS caller may save, stated by the server. Absent on an
   * older server, which is the deploy-window signal: lib/settings-access.ts.
   */
  access?: { settings: boolean; charges: boolean; aiSwitches: boolean };
}

export type TenantOnlinePaymentsMode = 'off' | 'live' | 'simulation';
export interface TenantOnlinePayments {
  effective: TenantOnlinePaymentsMode;
  configured: TenantOnlinePaymentsMode;
  source: TenantSettingSource;
  demoTenant: boolean;
}

export interface TenantOperationsPatch {
  defaultFacilityCapacity?: number;
  vaccinationBookingGate?: TenantVaccinationGate;
  careLogParentVisible?: boolean;
  staffingDogsPerHandler?: number | null;
  mealTrackingChargeEnabled?: boolean;
  mealTrackingRateCents?: number;
  /** #939 — Ted's lodging threshold (beds); null clears it. */
  lodgingCapacityThreshold?: number | null;
  /** #914 — Ted #11: the boarding checkout cutoff, `HH:MM`. Neither time is clearable. */
  boardingLatePickupChargeEnabled?: boolean;
  boardingCheckoutLocalTime?: string;
  boardingLateGraceLocalTime?: string;
  /** #1104 — merged per vaccine on the server; send only the vaccines that changed. */
  vaccineTerms?: Partial<TenantVaccineTerms>;
  /** Sawyer 09 pet-inactivity (D1) — whole months 1–36; null turns the rule off. */
  petInactivityMonths?: number | null;
  /** LG (#1113) — the WHOLE list replaces the stored one; [] = nothing commingles. */
  comminglingServiceNames?: string[];
  /** LG (#1111) — the heat question switch. */
  heatDeclarationRequired?: boolean;
  /** Ted #34 E6.1 — cents per dog; null stops offering the half-day with a groom. */
  groomDayHalfDaycareRateCents?: number | null;
  /** Ted #34 E6.2 — whole percent; null turns the first-groom discount off. */
  firstGroomDiscountPercent?: number | null;
  /** #948 — whole percent; null turns the multi-dog discount off. */
  multiDogDiscountPercent?: number | null;
  /** #948 — the WHOLE list replaces the stored one; null restores the default; [] = on no service. */
  multiDogServices?: string[] | null;
  /** #948 — every dog, or every dog but the first. */
  multiDogAppliesTo?: TenantMultiDogAppliesTo;
  /** Sawyer 10-30 — the WHOLE list replaces the stored one; null restores the default; [] = ask on every service. */
  runCardPromptOffServices?: string[] | null;
  // Sawyer 06 beds-lockers-runcard (B2, B4)
  /** The WHOLE list replaces the stored one; [] = beds are not sized; null restores Small, Big. */
  bedSizeClasses?: string[] | null;
  lockerFollowsBed?: boolean;
  /** SPEC 2026-10-26 D4. */
  vaccinationVerifiedRecordsOnly?: boolean;
  /** SPEC 2026-10-26 Story 5. */
  vetInfoRequiredForBooking?: boolean;
}

export type TenantOperationsUpdateResponse = TenantOperationsSettings & {
  changedKeys: TenantOperationsKey[];
  audited: boolean;
};

// Woofers Phase 0 — GET/PUT /api/v2/admin/tenant/assistant. Same read/write
// split as operations: business-config:read:all / business-config:write:all.
export interface TenantAssistantValues {
  /** What customers and staff call the assistant ("Woofers"); null = unnamed. */
  name: string | null;
  staffChatEnabled: boolean;
  customerChatEnabled: boolean;
  textingEnabled: boolean;
}
export type TenantAssistantKey = keyof TenantAssistantValues;
export interface TenantAssistantSettings {
  assistant: TenantAssistantValues;
  source: Record<TenantAssistantKey, 'configured' | 'default'>;
  limits: { maxNameLength: number };
  lastChange: { at: string; byName: string; changedKeys: TenantAssistantKey[] } | null;
}
/** PATCH: only the keys being changed. `name: null` clears it. */
export type TenantAssistantPatch = Partial<TenantAssistantValues>;
export type TenantAssistantUpdateResponse = TenantAssistantSettings & {
  changedKeys: TenantAssistantKey[];
  audited: boolean;
};

export const adminTenantSettingsApi = {
  getOperations: (apiUrl: string): Promise<ApiResponse<TenantOperationsSettings>> =>
    api.get<TenantOperationsSettings>(apiUrl, '/api/v2/admin/tenant/operations'),
  updateOperations: (
    apiUrl: string,
    patch: TenantOperationsPatch,
  ): Promise<ApiResponse<TenantOperationsUpdateResponse>> =>
    api.put<TenantOperationsUpdateResponse>(apiUrl, '/api/v2/admin/tenant/operations', patch),
  getAssistant: (apiUrl: string): Promise<ApiResponse<TenantAssistantSettings>> =>
    api.get<TenantAssistantSettings>(apiUrl, '/api/v2/admin/tenant/assistant'),
  updateAssistant: (
    apiUrl: string,
    patch: TenantAssistantPatch,
  ): Promise<ApiResponse<TenantAssistantUpdateResponse>> =>
    api.put<TenantAssistantUpdateResponse>(apiUrl, '/api/v2/admin/tenant/assistant', patch),
};

/**
 * #933 — does this facility's SERVER carry Owner Settings yet? Answered by
 * `GET /api/v2/admin/tenant/operations` returning 200. Same contract as
 * `fetchVaxIntakeConfig`: a raw fetch (never `request<T>`, whose 401
 * handler would sign a working user out), never rejects, every failure —
 * network, 401, a pre-deploy server's 403 `route_not_mapped`, a 404 for an
 * unprovisioned tenant, 5xx — resolves to false. Fail-closed: at worst a
 * freshly deployed server briefly hides the nav item until the next mount.
 * The same split that motivated #551 — frontends auto-deploy on merge, the
 * server does not — is what this probe exists for.
 */
export async function probeOwnerSettingsAvailable(
  apiUrl: string,
  token: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${apiUrl}/api/v2/admin/tenant/operations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.status === 200;
  } catch {
    return false;
  }
}

/** True when an older server rejected the call because the route does not exist there yet. */
export function isRouteNotMapped(res: Pick<ApiResponse<unknown>, 'reason'>): boolean {
  return res.reason === 'route_not_mapped';
}

// ─── adminTenantCapacityApi (#933 PR 2b — Settings → Capacity) ─────────────
// The three owner controls PR 2a put on the same `/api/v2/admin/tenant`
// router: per-service capacity rules, blocked days (capacity overrides) and
// the assessment schedule. Reads are business-config:read:all; writes are
// business-config:write:all (owner, manager). The rows are the ones the booking
// engine already reads — nothing here invents a shape (spec §2 principle 1):
// a DAY rule is `startTime: null`, a service-wide rule is `dayOfWeek: null`,
// a closure is `maxCapacity: null`.

export interface TenantCapacityService {
  id: string;
  /** The stable key the engine pools on (`daycare`, `boarding`…). Never shown raw. */
  name: string;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
  basePriceCents: number;
  durationMinutes: number | null;
  minLeadTimeMinutes: number | null;
  maxAdvanceDays: number | null;
  /** Ted #17 (#921) — palette key (`lime`, `sky`, …) or null (neutral). */
  color: string | null;
  /**
   * Kate K7/K9 — what the booking page shows under the name; null = the
   * customer app's generic line. Absent on an older server.
   */
  description?: string | null;
  /**
   * Beta K7 — the weekdays a client may book it, 0 = Sunday … 6 = Saturday;
   * null = every day. Absent on an older server, and the control then
   * renders nothing.
   */
  runDays?: number[] | null;
}

export interface TenantCapacityRule {
  id: string;
  serviceTypeId: string;
  resourceTypeId: string | null;
  /** 0 = Sunday … 6 = Saturday; null = every day (the service-wide default). */
  dayOfWeek: number | null;
  /** null = a whole-day rule; 'HH:MM' = one offered slot (assessments, grooming). */
  startTime: string | null;
  endTime: string | null;
  maxCapacity: number;
}

export interface TenantCapacityView {
  services: TenantCapacityService[];
  rules: TenantCapacityRule[];
}

export interface TenantCapacityRuleDraft {
  serviceTypeId: string;
  dayOfWeek: number | null;
  startTime?: string | null;
  maxCapacity: number;
}

export type TenantCapacityOverrideReason = 'closure' | 'holiday' | 'special_event' | 'expanded';

export interface TenantCapacityOverride {
  id: string;
  /** YYYY-MM-DD — a calendar date, never a timestamp. */
  date: string;
  reason: TenantCapacityOverrideReason | string;
  description: string | null;
  /** null = facility-wide (every service that day). */
  serviceTypeId: string | null;
  /** null = closed; a number = that day's limit instead of the rule's. */
  maxCapacity: number | null;
}

export interface TenantCapacityOverridesView {
  services: TenantCapacityService[];
  overrides: TenantCapacityOverride[];
  range: { from: string; to: string };
}

export interface TenantCapacityOverrideDraft {
  date: string;
  reason: TenantCapacityOverrideReason;
  serviceTypeId: string | null;
  maxCapacity: number | null;
  description?: string;
}

export interface TenantAssessmentSchedule {
  /** True when the tenant has any assessment DAY rule — the gate's own test. */
  configured: boolean;
  serviceTypeId: string | null;
  isActive: boolean;
  days: number[];
  times: string[];
  /** null when unconfigured, or when caps differ by weekday (a hand edit). */
  dailyCap: number | null;
  slotCap: number | null;
  priceCents: number | null;
  durationMinutes: number | null;
  minLeadTimeMinutes: number | null;
  maxAdvanceDays: number | null;
  ruleCount: number;
  notes: string[];
}

/**
 * The PUT body. `times` is the expanded list — the first-slot + stagger form
 * is a client convenience (see settings/capacity-helpers.ts). `slotCap`
 * omitted = same as `dailyCap`. `minLeadTimeMinutes` / `maxAdvanceDays`:
 * omitted = leave the stored value alone, null = clear it.
 */
export interface TenantAssessmentScheduleInput {
  days: number[];
  times: string[];
  dailyCap: number;
  slotCap?: number;
  /** Omitted = keep the stored price. A manager never sends one. */
  priceCents?: number;
  durationMinutes: number;
  minLeadTimeMinutes?: number | null;
  maxAdvanceDays?: number | null;
}

export interface TenantAssessmentScheduleWritten {
  created: number;
  updated: number;
  /** Rules for weekdays/times no longer wanted — pruned so "Tue/Thu only" means Tue/Thu only. */
  deleted: number;
}

export interface TenantAssessmentScheduleSaveResponse {
  schedule: TenantAssessmentSchedule;
  written: TenantAssessmentScheduleWritten;
  warnings: string[];
  audited: boolean;
  serviceTypeId: string;
}

const TENANT_CAPACITY_BASE = '/api/v2/admin/tenant';

export const adminTenantCapacityApi = {
  listRules: (apiUrl: string): Promise<ApiResponse<TenantCapacityView>> =>
    api.get<TenantCapacityView>(apiUrl, `${TENANT_CAPACITY_BASE}/capacity-rules`),
  createRule: (
    apiUrl: string,
    draft: TenantCapacityRuleDraft,
  ): Promise<ApiResponse<{ rule: TenantCapacityRule; audited: boolean }>> =>
    api.post<{ rule: TenantCapacityRule; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-rules`,
      draft,
    ),
  updateRule: (
    apiUrl: string,
    id: string,
    patch: { maxCapacity: number },
  ): Promise<ApiResponse<{ rule: TenantCapacityRule; audited: boolean }>> =>
    api.patch<{ rule: TenantCapacityRule; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-rules/${encodeURIComponent(id)}`,
      patch,
    ),
  deleteRule: (
    apiUrl: string,
    id: string,
  ): Promise<ApiResponse<{ deleted: true; audited: boolean }>> =>
    api.delete<{ deleted: true; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-rules/${encodeURIComponent(id)}`,
    ),

  listBlockedDays: (
    apiUrl: string,
    range?: { from?: string; to?: string },
  ): Promise<ApiResponse<TenantCapacityOverridesView>> => {
    const q = new URLSearchParams();
    if (range?.from) q.set('from', range.from);
    if (range?.to) q.set('to', range.to);
    const qs = q.toString();
    return api.get<TenantCapacityOverridesView>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-overrides${qs ? `?${qs}` : ''}`,
    );
  },
  /** Upsert on (date, service): a second save for the same day updates in place. */
  saveBlockedDay: (
    apiUrl: string,
    draft: TenantCapacityOverrideDraft,
  ): Promise<ApiResponse<{ override: TenantCapacityOverride; created: boolean; audited: boolean }>> =>
    api.post<{ override: TenantCapacityOverride; created: boolean; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-overrides`,
      draft,
    ),
  deleteBlockedDay: (
    apiUrl: string,
    id: string,
  ): Promise<ApiResponse<{ deleted: true; audited: boolean }>> =>
    api.delete<{ deleted: true; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/capacity-overrides/${encodeURIComponent(id)}`,
    ),

  getAssessmentSchedule: (
    apiUrl: string,
  ): Promise<ApiResponse<{ schedule: TenantAssessmentSchedule }>> =>
    api.get<{ schedule: TenantAssessmentSchedule }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/assessment-schedule`,
    ),
  saveAssessmentSchedule: (
    apiUrl: string,
    input: TenantAssessmentScheduleInput,
  ): Promise<ApiResponse<TenantAssessmentScheduleSaveResponse>> =>
    api.put<TenantAssessmentScheduleSaveResponse>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/assessment-schedule`,
      input,
    ),
  /** The script's `--unconfigure`: removes every assessment rule; the service row stays. */
  clearAssessmentSchedule: (
    apiUrl: string,
  ): Promise<ApiResponse<{ deleted: number; audited: boolean }>> =>
    api.delete<{ deleted: number; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/assessment-schedule`,
    ),
};

// ─── adminTenantServicesApi (#933 PR 3 — Settings → Services & prices) ─────
// GET/POST /api/v2/admin/tenant/service-types, PATCH /service-types/:id
// (everything but the price and the engine key), PATCH /service-types/:id/price.
// Reads are business-config:read:all; the general write is
// business-config:write:all (owner, manager); a price change — and creating a service, which
// sets one — is business-config.pricing:write:all (owner). `name` is never
// writable: the booking engine keys on it.

export interface TenantServiceView extends TenantCapacityService {
  /** The service-wide rule's cap, if any (what the dashboard meter reads). */
  everyDayCap: number | null;
  ruleCount: number;
  /** The service whose limit this one counts against (half-day → daycare), by engine key. */
  pooledInto: string | null;
  /** What the engine does with this service, in the owner's words. */
  behavior: string;
}

export interface TenantCreatableService {
  name: string;
  label: string;
  defaultDisplayName: string;
  defaultDurationMinutes: number | null;
  /** Counts against another service's limit — no capacity asked on create. */
  pooled: boolean;
  /** Ted #28 Phase 1b — rides a visit as a line item only (transport); no capacity asked on create. Absent on an older server. */
  legOnly?: boolean;
  behavior: string;
  alreadyAdded: boolean;
}

export interface TenantServicesView {
  services: TenantServiceView[];
  creatable: TenantCreatableService[];
}

export interface TenantServicePatch {
  displayName?: string;
  /** Kate K7/K9 — null clears it. */
  description?: string | null;
  /** null = a whole-day service. */
  durationMinutes?: number | null;
  isActive?: boolean;
  sortOrder?: number;
  minLeadTimeMinutes?: number | null;
  maxAdvanceDays?: number | null;
  /** Ted #17 (#921) — a palette key; null clears the color. */
  color?: string | null;
  /** Beta K7 — the days clients can book it; null = every day. Never empty. */
  runDays?: number[] | null;
}

export interface TenantServiceDraft {
  name: string;
  displayName: string;
  /** Kate K7/K9 — optional; omitted when blank. */
  description?: string;
  basePriceCents: number;
  durationMinutes?: number | null;
  /** Required unless the name is pooled; becomes the service-wide rule in the same write. */
  capacity?: number;
}

export interface TenantServiceWriteResponse {
  service: TenantServiceView;
  changedKeys: string[];
  audited: boolean;
}

export const adminTenantServicesApi = {
  list: (apiUrl: string): Promise<ApiResponse<TenantServicesView>> =>
    api.get<TenantServicesView>(apiUrl, `${TENANT_CAPACITY_BASE}/service-types`),
  update: (
    apiUrl: string,
    id: string,
    patch: TenantServicePatch,
  ): Promise<ApiResponse<TenantServiceWriteResponse>> =>
    api.patch<TenantServiceWriteResponse>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/service-types/${encodeURIComponent(id)}`,
      patch,
    ),
  updatePrice: (
    apiUrl: string,
    id: string,
    basePriceCents: number,
  ): Promise<ApiResponse<TenantServiceWriteResponse>> =>
    api.patch<TenantServiceWriteResponse>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/service-types/${encodeURIComponent(id)}/price`,
      { basePriceCents },
    ),
  create: (
    apiUrl: string,
    draft: TenantServiceDraft,
  ): Promise<ApiResponse<{ service: TenantServiceView; rule: TenantCapacityRule | null; audited: boolean }>> =>
    api.post<{ service: TenantServiceView; rule: TenantCapacityRule | null; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/service-types`,
      draft,
    ),
};

// ─── adminGroomingMenuApi (Ted #1208 — Settings → Services & prices → Grooming menu) ─
// GET /api/v2/admin/tenant/grooming-menu (business-config:read:all), and the
// writes — POST /services, PATCH /services/:id, PUT /services/:id/prices,
// POST /add-ons, PATCH /add-ons/:id — all business-config.pricing:write:all
// (owner), because every one changes what a customer is charged or offered.
// No DELETE: a row is hidden (`isActive: false`), never removed.

export type GroomingSizeKey = 'small' | 'medium' | 'large' | 'xl';
/** Cents per size; null = no price for that size (the starting price is charged). */
export type GroomingSizePrices = Record<GroomingSizeKey, number | null>;

export interface GroomingMenuService {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  /** The starting price — charged when the dog has no size on file. */
  basePriceCents: number;
  isCoatRelated: boolean;
  isActive: boolean;
  sortOrder: number;
  prices: GroomingSizePrices;
}

export interface GroomingMenuAddOn {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  priceCents: number;
  isActive: boolean;
  /** "Clients can book this" — false = staff-only (the desk adds it to a visit). */
  customerVisible: boolean;
  /** #1123 — this add-on IS the daily meal charge; meal tracking never bills its days again. */
  isMealCharge: boolean;
  sortOrder: number;
}

export interface GroomingMenuView {
  services: GroomingMenuService[];
  addOns: GroomingMenuAddOn[];
  sizes: Array<{ key: GroomingSizeKey; label: string }>;
}

export interface GroomingMenuServiceDraft {
  displayName: string;
  description?: string | null;
  basePriceCents: number;
  isCoatRelated?: boolean;
  prices?: Partial<GroomingSizePrices>;
}

export interface GroomingMenuServicePatch {
  displayName?: string;
  description?: string | null;
  basePriceCents?: number;
  isCoatRelated?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface GroomingMenuAddOnDraft {
  displayName: string;
  description?: string | null;
  priceCents: number;
  customerVisible: boolean;
  isMealCharge: boolean;
}

export interface GroomingMenuAddOnPatch {
  displayName?: string;
  description?: string | null;
  priceCents?: number;
  customerVisible?: boolean;
  isMealCharge?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

const GROOMING_MENU_BASE = `${TENANT_CAPACITY_BASE}/grooming-menu`;

export const adminGroomingMenuApi = {
  get: (apiUrl: string): Promise<ApiResponse<GroomingMenuView>> =>
    api.get<GroomingMenuView>(apiUrl, GROOMING_MENU_BASE),
  createService: (
    apiUrl: string,
    draft: GroomingMenuServiceDraft,
  ): Promise<ApiResponse<{ service: GroomingMenuService; audited: boolean }>> =>
    api.post<{ service: GroomingMenuService; audited: boolean }>(apiUrl, `${GROOMING_MENU_BASE}/services`, draft),
  updateService: (
    apiUrl: string,
    id: string,
    patch: GroomingMenuServicePatch,
  ): Promise<ApiResponse<{ service: GroomingMenuService; changedKeys: string[]; audited: boolean }>> =>
    api.patch<{ service: GroomingMenuService; changedKeys: string[]; audited: boolean }>(
      apiUrl,
      `${GROOMING_MENU_BASE}/services/${encodeURIComponent(id)}`,
      patch,
    ),
  setServicePrices: (
    apiUrl: string,
    id: string,
    prices: Partial<GroomingSizePrices>,
  ): Promise<ApiResponse<{ service: GroomingMenuService; changedKeys: string[]; audited: boolean }>> =>
    api.put<{ service: GroomingMenuService; changedKeys: string[]; audited: boolean }>(
      apiUrl,
      `${GROOMING_MENU_BASE}/services/${encodeURIComponent(id)}/prices`,
      { prices },
    ),
  createAddOn: (
    apiUrl: string,
    draft: GroomingMenuAddOnDraft,
  ): Promise<ApiResponse<{ addOn: GroomingMenuAddOn; audited: boolean }>> =>
    api.post<{ addOn: GroomingMenuAddOn; audited: boolean }>(apiUrl, `${GROOMING_MENU_BASE}/add-ons`, draft),
  updateAddOn: (
    apiUrl: string,
    id: string,
    patch: GroomingMenuAddOnPatch,
  ): Promise<ApiResponse<{ addOn: GroomingMenuAddOn; changedKeys: string[]; audited: boolean }>> =>
    api.patch<{ addOn: GroomingMenuAddOn; changedKeys: string[]; audited: boolean }>(
      apiUrl,
      `${GROOMING_MENU_BASE}/add-ons/${encodeURIComponent(id)}`,
      patch,
    ),
};

// ─── adminTenantLodgingApi (#939 3a — Settings → Lodging) ────────────────
// GET/POST /api/v2/admin/tenant/lodging/units, PATCH /lodging/units/:id.
// Read business-config:read:all; write business-config:write:all (owner, manager)
// OR business-config.lodging:write:all (owner + manager, Sawyer B3).
// The lodging capacity THRESHOLD is an operations key (adminTenantSettingsApi).

export interface TenantLodgingUnit {
  id: string;
  name: string;
  /** 'run' | 'kennel' | 'suite' | 'cage' — never shown raw; `kindLabel` is. */
  kind: string;
  kindLabel: string;
  zone: string;
  /** Dogs per bed. */
  capacity: number;
  isActive: boolean;
  resourceTypeId: string;
  sortOrder: number;
  // Sawyer 06 beds-lockers-runcard (B2, B4) — absent on an older server (reads as not set).
  sizeClass?: string | null;
  defaultLocker?: string | null;
}

export interface TenantLodgingView {
  units: TenantLodgingUnit[];
  activeCount: number;
  activeDogCapacity: number;
  kinds: Array<{ key: string; label: string }>;
}

export interface TenantLodgingUnitDraft {
  name: string;
  kind: string;
  zone: string;
  capacity: number;
  // Sawyer 06 beds-lockers-runcard (B2)
  sizeClass?: string | null;
}

export interface TenantLodgingUnitPatch {
  name?: string;
  kind?: string;
  zone?: string;
  capacity?: number;
  isActive?: boolean;
  // Sawyer 06 beds-lockers-runcard (B2, B4) — null clears.
  sizeClass?: string | null;
  defaultLocker?: string | null;
}

export const adminTenantLodgingApi = {
  list: (apiUrl: string): Promise<ApiResponse<TenantLodgingView>> =>
    api.get<TenantLodgingView>(apiUrl, `${TENANT_CAPACITY_BASE}/lodging/units`),
  create: (
    apiUrl: string,
    draft: TenantLodgingUnitDraft,
  ): Promise<ApiResponse<{ unit: TenantLodgingUnit; audited: boolean }>> =>
    api.post<{ unit: TenantLodgingUnit; audited: boolean }>(apiUrl, `${TENANT_CAPACITY_BASE}/lodging/units`, draft),
  update: (
    apiUrl: string,
    id: string,
    patch: TenantLodgingUnitPatch,
  ): Promise<ApiResponse<{ unit: TenantLodgingUnit; changedKeys: string[]; audited: boolean }>> =>
    api.patch<{ unit: TenantLodgingUnit; changedKeys: string[]; audited: boolean }>(
      apiUrl,
      `${TENANT_CAPACITY_BASE}/lodging/units/${encodeURIComponent(id)}`,
      patch,
    ),
};

// ─── adminMessagingSettingsApi (#1212 — texting B1, Settings → Messaging) ──
// The tenant's OWN Twilio connection. Owner-only on the server
// (payments-ops.credentials:write:all, the gateway-credentials gate). The Auth
// Token is WRITE-ONLY: GET answers SID last 4 + the sender, never the token.
// Writes run with maxRetries 0 — a 503 here is a deterministic ops state
// (MESSAGING_CREDS_ENCRYPTION_KEY missing), and a retried test text is a
// second text. Shapes mirror packages/core/modules/messaging-sms/service.ts.

export type MessagingSenderView =
  | { kind: 'number'; e164: string }
  | { kind: 'messaging_service'; sidLast4: string };

export type MessagingSettings =
  | { connected: false; accountSidLast4: null; sender: null; verifiedAt: null }
  | { connected: true; accountSidLast4: string | null; sender: MessagingSenderView | null; verifiedAt: string | null };

export interface MessagingConnectInput {
  accountSid: string;
  authToken: string;
  sender: { kind: 'number'; e164: string } | { kind: 'messaging_service'; sid: string };
}

export interface MessagingTestTextResult {
  message: { messageId: string | null; twilioSid: string; status: string };
}

const MESSAGING_SETTINGS_PATH = `${TENANT_CAPACITY_BASE}/messaging`;

/** Woofers Phase 1 — the owner's phone for texts that need a person. */
export interface TextingEscalation {
  /** E.164, or null when the owner is never texted. */
  escalationNotifyTo: string | null;
}

/** The automatic texts in Settings → Texting. Shapes mirror messaging-sms/auto-replies.ts. */
export type TextingMessageKey = 'assistantOff' | 'assistantUnavailable' | 'clientLimit' | 'unknownSender';

export interface AutoReplyMessage {
  custom: string | null;
  /** Today's wording, exactly as sent. Empty means no text goes out. */
  standard: string[];
  startingPoint: string;
}

export interface AutoRepliesSettings {
  messages: Record<TextingMessageKey, AutoReplyMessage>;
  framing: { businessName: string; signUpUrl: string };
  assistant: { name: string | null; textingEnabled: boolean };
  readiness: { twilioConnected: boolean; programOn: boolean; alertNumberSet: boolean };
  limits: { maxLength: number };
  lastChange: { at: string; byName: string; changedKeys: TextingMessageKey[] } | null;
}

export interface AutoRepliesUpdateResult extends AutoRepliesSettings {
  changedKeys: TextingMessageKey[];
  audited: boolean;
}

export const adminMessagingSettingsApi = {
  getAutoReplies: (apiUrl: string): Promise<ApiResponse<AutoRepliesSettings>> =>
    api.get<AutoRepliesSettings>(apiUrl, `${MESSAGING_SETTINGS_PATH}/auto-replies`),
  updateAutoReplies: (
    apiUrl: string,
    messages: Partial<Record<TextingMessageKey, string | null>>,
  ): Promise<ApiResponse<AutoRepliesUpdateResult>> =>
    request<AutoRepliesUpdateResult>(
      apiUrl,
      `${MESSAGING_SETTINGS_PATH}/auto-replies`,
      { method: 'PUT', body: JSON.stringify({ messages }) },
      { maxRetries: 0 },
    ),
  getEscalation: (apiUrl: string): Promise<ApiResponse<TextingEscalation>> =>
    api.get<TextingEscalation>(apiUrl, `${MESSAGING_SETTINGS_PATH}/escalation`),
  setEscalation: (apiUrl: string, escalationNotifyTo: string): Promise<ApiResponse<TextingEscalation>> =>
    request<TextingEscalation>(
      apiUrl,
      `${MESSAGING_SETTINGS_PATH}/escalation`,
      { method: 'PUT', body: JSON.stringify({ escalationNotifyTo }) },
      { maxRetries: 0 },
    ),
  get: (apiUrl: string): Promise<ApiResponse<MessagingSettings>> =>
    api.get<MessagingSettings>(apiUrl, MESSAGING_SETTINGS_PATH),
  connect: (apiUrl: string, input: MessagingConnectInput): Promise<ApiResponse<MessagingSettings>> =>
    request<MessagingSettings>(
      apiUrl,
      MESSAGING_SETTINGS_PATH,
      { method: 'PUT', body: JSON.stringify(input) },
      { maxRetries: 0 },
    ),
  disconnect: (apiUrl: string): Promise<ApiResponse<MessagingSettings>> =>
    request<MessagingSettings>(apiUrl, MESSAGING_SETTINGS_PATH, { method: 'DELETE' }, { maxRetries: 0 }),
  sendTestText: (apiUrl: string, to: string): Promise<ApiResponse<MessagingTestTextResult>> =>
    request<MessagingTestTextResult>(
      apiUrl,
      `${MESSAGING_SETTINGS_PATH}/test`,
      { method: 'POST', body: JSON.stringify({ to }) },
      { maxRetries: 0 },
    ),
};

// ─── adminLodgingCalendarApi (#939 3b — /calendar/lodging) ───────────────
// GET /api/v2/admin/calendar/lodging?start&end (inclusive) — one read: beds,
// stays, unassigned boarders, per-night counts with the owner's threshold
// tone. Gated like the calendar feed (the desk trio). Shapes mirror
// packages/core/modules/scheduling-resources/lodging-calendar.ts.

export interface LodgingUnit {
  id: string;
  name: string;
  zone: string;
  kind: string;
  capacity: number;
  isActive: boolean;
  sortOrder: number;
  zoneSortOrder: number;
  // Sawyer 06 beds-lockers-runcard (B2) — the bed's size label; absent on an older server.
  sizeClass?: string | null;
}

export interface LodgingStay {
  assignmentId: string;
  bookingId: string;
  resourceUnitId: string;
  /** YYYY-MM-DD, first night. */
  startDate: string;
  /** YYYY-MM-DD, checkout day (exclusive — the night before is the last night). */
  endDate: string;
  status: string;
  customerId: string | null;
  customerName: string;
  dogNames: string[];
  dogCount: number;
  serviceDisplayName: string;
  /** #921 palette key or null. */
  serviceColor: string | null;
  /** Ted #28 (#1050) — the stay's + dogs' flags on the bed block; absent on an older server. */
  flags?: DogFlag[];
  nightsInRange: string[];
  nights: number;
  checkoutDate: string;
  continuesFromBefore: boolean;
  continuesAfter: boolean;
  shared: boolean;
}

export interface LodgingUnassignedBoarder {
  bookingId: string;
  /** YYYY-MM-DD, inclusive both ends. */
  startDate: string;
  endDate: string;
  status: string;
  customerId: string | null;
  customerName: string;
  dogNames: string[];
  dogCount: number;
  serviceDisplayName: string;
  serviceColor: string | null;
  /** Ted #28 (#1050) — the stay's + dogs' flags; absent on an older server. */
  flags?: DogFlag[];
}

export interface LodgingNight {
  date: string;
  bedsOccupied: number;
  dogsOvernight: number;
  goingHome: number;
  arriving: number;
  tone: 'green' | 'amber' | 'red';
}

export interface LodgingCalendarView {
  start: string;
  end: string;
  threshold: number | null;
  thresholdSource: 'configured' | 'default';
  units: LodgingUnit[];
  stays: LodgingStay[];
  unassigned: LodgingUnassignedBoarder[];
  nights: LodgingNight[];
}

export const adminLodgingCalendarApi = {
  load: async (apiUrl: string, start: string, end: string): Promise<LodgingCalendarView> => {
    const res = await api.get<LodgingCalendarView>(
      apiUrl,
      `/api/v2/admin/calendar/lodging?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    );
    if (res.error || !res.data) throw new Error(res.error ?? 'Could not load the lodging calendar.');
    return res.data;
  },
};

// ─── adminLodgingAssignApi (#939 3c — assign / move / unassign a bed) ──────
// POST  /api/v2/admin/calendar/assign            → 201 { assignment }
// PATCH /api/v2/admin/calendar/assign            → 200 { assignment } (replaceAssignmentId in the body)
// DELETE /api/v2/admin/calendar/assign/:id       → 200 { ok: true }
// A refusal is a plain sentence that NAMES the dog already there:
//   409 resource_conflict  — "Luna is already on that bed for those nights."
//   409 unrelated_family   — "Hazel is already on that bed and belongs to
//                             another family — dogs from different families
//                             never share a bed."  (Ted's rule, enforced in
//                             the service layer; the UI never offers it.)
// `preferBodyMessage` keeps that sentence as `error` so the page can show it
// verbatim; `code` carries the machine token for anyone who branches on it.

export interface LodgingAssignBody {
  bookingId: string;
  unitId: string;
  /** First night, YYYY-MM-DD. */
  startDate: string;
  /** Checkout day, YYYY-MM-DD (the bed row's exclusive end). */
  endDate: string;
}

export interface LodgingAssignment {
  id: string;
  bookingId: string;
  resourceUnitId: string | null;
  startDate: string;
  endDate: string;
}

const LODGING_ASSIGN_PATH = '/api/v2/admin/calendar/assign';

export const adminLodgingAssignApi = {
  assign: (apiUrl: string, body: LodgingAssignBody): Promise<ApiResponse<{ assignment: LodgingAssignment }>> =>
    request<{ assignment: LodgingAssignment }>(
      apiUrl,
      LODGING_ASSIGN_PATH,
      {
        method: 'POST',
        body: JSON.stringify({ bookingId: body.bookingId, category: 'boarding', resourceUnitId: body.unitId, startDate: body.startDate, endDate: body.endDate }),
      },
      { preferBodyMessage: true },
    ),
  move: (
    apiUrl: string,
    body: LodgingAssignBody & { replaceAssignmentId: string },
  ): Promise<ApiResponse<{ assignment: LodgingAssignment }>> =>
    request<{ assignment: LodgingAssignment }>(
      apiUrl,
      LODGING_ASSIGN_PATH,
      {
        method: 'PATCH',
        body: JSON.stringify({
          replaceAssignmentId: body.replaceAssignmentId,
          bookingId: body.bookingId,
          category: 'boarding',
          resourceUnitId: body.unitId,
          startDate: body.startDate,
          endDate: body.endDate,
        }),
      },
      { preferBodyMessage: true },
    ),
  unassign: (apiUrl: string, assignmentId: string): Promise<ApiResponse<{ ok: true }>> =>
    request<{ ok: true }>(apiUrl, `${LODGING_ASSIGN_PATH}/${encodeURIComponent(assignmentId)}`, { method: 'DELETE' }, { preferBodyMessage: true }),
};

// ─── adminAgreementsApi (#1114 lane LA — Settings → Agreements) ──────────
// /api/agreements/admin/* (buildAgreementsRouter's admin sub-router). Reads
// need contracts.customer:read:all (owner/manager/receptionist); writes need
// contracts.customer:write:all (owner/manager). Shapes mirror
// packages/core/modules/agreements/routes.ts views.

export interface AgreementTemplate {
  id: string;
  /** Stable key (`hthd-agreement`); `reack-v*` rows are ledger markers, not documents. */
  name: string;
  displayName: string;
  content: string;
  version: number;
  /** Signatures below this version are outdated; moves only on a re-sign-required save. */
  resignRequiredFrom: number;
  isActive: boolean;
  /** Service-type names, or ['*'] for every service. [] = required for nothing. */
  requiredFor: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AgreementVersionRow {
  id: string;
  agreementId: string;
  version: number;
  displayName: string;
  content: string;
  requiredFor: string[];
  requireResign: boolean;
  changedByStaffId: string | null;
  changedByName: string | null;
  note: string | null;
  createdAt: string;
}

export interface AgreementSignatureRow {
  id: string;
  agreementId: string;
  customerId: string;
  typedName: string;
  signedVersion: number;
  signedAt: string;
  agreement?: { name?: string; displayName: string; version: number; resignRequiredFrom?: number };
  customer?: { id: string; firstName: string; lastName: string; email: string };
}

export interface AgreementTemplateDraft {
  name: string;
  displayName: string;
  content: string;
  requiredFor: string[];
}

export interface AgreementTemplatePatch {
  displayName?: string;
  content?: string;
  requiredFor?: string[];
  isActive?: boolean;
  /** The re-sign switch: true invalidates every earlier signature on this document. */
  requireResign?: boolean;
  note?: string;
}

export interface AgreementTemplateWriteResponse {
  agreement: AgreementTemplate;
  publishedNewVersion?: boolean;
  resignRequired?: boolean;
}

const AGREEMENTS_ADMIN_BASE = '/api/agreements/admin/agreements';

export const adminAgreementsApi = {
  list: (apiUrl: string): Promise<ApiResponse<{ agreements: AgreementTemplate[] }>> =>
    api.get<{ agreements: AgreementTemplate[] }>(apiUrl, AGREEMENTS_ADMIN_BASE),
  create: (apiUrl: string, draft: AgreementTemplateDraft): Promise<ApiResponse<AgreementTemplateWriteResponse>> =>
    api.post<AgreementTemplateWriteResponse>(apiUrl, AGREEMENTS_ADMIN_BASE, draft),
  update: (apiUrl: string, id: string, patch: AgreementTemplatePatch): Promise<ApiResponse<AgreementTemplateWriteResponse>> =>
    api.patch<AgreementTemplateWriteResponse>(apiUrl, `${AGREEMENTS_ADMIN_BASE}/${encodeURIComponent(id)}`, patch),
  deactivate: (apiUrl: string, id: string): Promise<ApiResponse<{ success: boolean; agreement: AgreementTemplate }>> =>
    api.delete<{ success: boolean; agreement: AgreementTemplate }>(apiUrl, `${AGREEMENTS_ADMIN_BASE}/${encodeURIComponent(id)}`),
  versions: (apiUrl: string, id: string): Promise<ApiResponse<{ versions: AgreementVersionRow[] }>> =>
    api.get<{ versions: AgreementVersionRow[] }>(apiUrl, `${AGREEMENTS_ADMIN_BASE}/${encodeURIComponent(id)}/versions`),
  signatures: (apiUrl: string, id: string): Promise<ApiResponse<{ signatures: AgreementSignatureRow[] }>> =>
    api.get<{ signatures: AgreementSignatureRow[] }>(apiUrl, `${AGREEMENTS_ADMIN_BASE}/signatures?agreementId=${encodeURIComponent(id)}`),
};
